// Mock Data for MedFast CRM Pro
// Centralized data layer for all application data

// Role-based permissions configuration
export const rolePermissions = {
  admin: {
    name: 'System Administrator',
    description: 'Full system access and management',
    permissions: [
      'view_all',
      'manage_users', 
      'manage_facilities',
      'view_analytics', 
      'manage_billing', 
      'approve_orders', 
      'system_settings',
      'audit_logs',
      'backup_restore',
      'security_settings'
    ],
    tabs: ['dashboard', 'residents', 'enquiries', 'clinical', 'medication', 'careplans', 'wellbeing', 'telehealth', 'orders', 'quality', 'roster', 'billing', 'reports', 'network', 'alerts', 'pharmacy', 'kitchen', 'maintenance', 'emergency', 'visitors', 'hr', 'workforce', 'settings']
  },
  
  manager: {
    name: 'Facility Manager',
    description: 'Facility-level management and oversight',
    permissions: [
      'view_facility',
      'manage_staff',
      'view_reports',
      'manage_enquiries',
      'approve_rosters',
      'manage_budgets',
      'quality_oversight',
      'incident_management',
      'family_communications'
    ],
    tabs: ['dashboard', 'residents', 'enquiries', 'clinical', 'medication', 'careplans', 'wellbeing', 'telehealth', 'orders', 'quality', 'roster', 'billing', 'reports', 'kitchen', 'maintenance', 'emergency', 'visitors', 'hr', 'workforce', 'settings']
  },
  
  doctor: {
    name: 'Medical Doctor',
    description: 'Medical care and clinical decision making',
    permissions: [
      'view_all_patients',
      'edit_prescriptions',
      'view_medical_history',
      'add_notes',
      'eprescribe',
      'order_tests',
      'refer_specialists',
      'approve_care_plans',
      'emergency_override',
      'family_consultations',
      'clinical_audits'
    ],
    tabs: ['dashboard', 'residents', 'clinical', 'medication', 'careplans', 'telehealth', 'orders', 'reports']
  },
  
  nurse: {
    name: 'Registered Nurse',
    description: 'Direct patient care and medication administration',
    permissions: [
      'view_assigned_patients',
      'administer_medications',
      'record_vitals',
      'update_care_plans',
      'report_incidents',
      'family_updates',
      'emergency_response',
      'wound_care',
      'medication_checks'
    ],
    tabs: ['dashboard', 'residents', 'clinical', 'medication', 'careplans', 'wellbeing', 'telehealth', 'orders', 'roster', 'alerts', 'kitchen', 'emergency', 'visitors']
  },
  
  physiotherapist: {
    name: 'Physiotherapist',
    description: 'Physical therapy and mobility assessment',
    permissions: [
      'view_assigned_patients',
      'assess_mobility',
      'create_exercise_plans',
      'record_therapy_sessions',
      'fall_prevention',
      'equipment_assessment',
      'family_education',
      'progress_reports'
    ],
    tabs: ['dashboard', 'residents', 'clinical', 'careplans', 'wellbeing', 'telehealth', 'roster']
  },
  
  occupational_therapist: {
    name: 'Occupational Therapist',
    description: 'Daily living skills and equipment assessment',
    permissions: [
      'view_assigned_patients',
      'assess_adl_capacity',
      'recommend_equipment',
      'create_activity_plans',
      'home_visits',
      'family_training',
      'safety_assessments'
    ],
    tabs: ['dashboard', 'residents', 'clinical', 'careplans', 'wellbeing', 'telehealth']
  },
  
  pharmacist: {
    name: 'Pharmacist',
    description: 'Medication management and pharmacy services',
    permissions: [
      'view_medication_orders',
      'dispense_medications',
      'medication_reviews',
      'drug_interaction_checks',
      'medication_education',
      'inventory_management',
      'clinical_pharmacy'
    ],
    tabs: ['dashboard', 'medication', 'pharmacy', 'orders', 'reports', 'alerts']
  },
  
  care_worker: {
    name: 'Care Worker',
    description: 'Direct personal care and support',
    permissions: [
      'view_assigned_patients',
      'personal_care',
      'meal_assistance',
      'activity_participation',
      'basic_observations',
      'family_support',
      'incident_reporting'
    ],
    tabs: ['dashboard', 'residents', 'wellbeing', 'telehealth', 'roster', 'kitchen', 'visitors']
  },
  
  activities_coordinator: {
    name: 'Activities Coordinator',
    description: 'Recreational and social activities',
    permissions: [
      'view_all_residents',
      'plan_activities',
      'record_participation',
      'family_events',
      'volunteer_management',
      'activity_reports'
    ],
    tabs: ['dashboard', 'residents', 'wellbeing', 'telehealth', 'roster', 'visitors']
  },
  
  maintenance: {
    name: 'Maintenance Staff',
    description: 'Facility maintenance and safety',
    permissions: [
      'view_facility_areas',
      'maintenance_requests',
      'safety_checks',
      'equipment_maintenance',
      'emergency_repairs'
    ],
    tabs: ['dashboard', 'reports', 'alerts', 'maintenance', 'emergency']
  },
  
  family: {
    name: 'Family Member',
    description: 'Family access to resident information',
    permissions: [
      'view_family_member',
      'receive_updates',
      'schedule_visits',
      'view_activities',
      'communication_portal'
    ],
    tabs: ['dashboard', 'residents', 'wellbeing', 'billing']
  }
};

export const users = [
  { 
    id: 1, 
    username: 'admin', 
    password: 'admin123', 
    role: 'admin', 
    name: 'Admin User', 
    title: 'System Administrator', 
    facilities: ['all'], 
    permissions: rolePermissions.admin.permissions
  },
  { 
    id: 2, 
    username: 'manager', 
    password: 'manager123', 
    role: 'manager', 
    name: 'Sarah Mitchell', 
    title: 'Facility Manager', 
    facility: 'sunshine', 
    permissions: rolePermissions.manager.permissions
  },
  // Enhanced Doctors - 20 doctors with comprehensive profiles
  { 
    id: 3, 
    username: 'dr.smith', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Robert Smith', 
    title: 'Senior Geriatrician', 
    specialization: 'Geriatric Medicine',
    qualifications: ['MBBS', 'FRACP', 'PhD Gerontology'],
    experience: 25,
    facilities: ['sunshine', 'bayview'], 
    phone: '0412 345 678',
    email: 'dr.smith@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['Dementia Care', 'Palliative Care', 'Falls Prevention'],
    certifications: ['Advanced Life Support', 'Palliative Care Certification'],
    languages: ['English', 'Spanish'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.8,
      medicationAccuracy: 98.5,
      responseTime: '2.3 hours'
    }
  },
  { 
    id: 4, 
    username: 'dr.wilson', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Emily Wilson', 
    title: 'General Practitioner', 
    specialization: 'Aged Care',
    qualifications: ['MBBS', 'FRACGP', 'Dip Geriatric Medicine'],
    experience: 18,
    facilities: ['greenhill', 'rosewood'], 
    phone: '0413 456 789',
    email: 'dr.wilson@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Chronic Disease Management', 'Mental Health', 'Wound Care'],
    certifications: ['Mental Health First Aid', 'Wound Care Specialist'],
    languages: ['English', 'Mandarin'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.7,
      medicationAccuracy: 97.2,
      responseTime: '3.1 hours'
    }
  },
  { 
    id: 5, 
    username: 'dr.chen', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Michael Chen', 
    title: 'Cardiologist', 
    specialization: 'Cardiology',
    qualifications: ['MBBS', 'FRACP', 'Cardiology Fellowship'],
    experience: 22,
    facilities: ['sunshine', 'bayview', 'greenhill'], 
    phone: '0414 567 890',
    email: 'dr.chen@medfastcrm.com.au',
    availability: {
      monday: '10:00-18:00',
      tuesday: '10:00-18:00',
      wednesday: '10:00-18:00',
      thursday: '10:00-18:00',
      friday: '10:00-18:00'
    },
    specializations: ['Cardiac Care', 'Hypertension Management', 'Heart Failure'],
    certifications: ['Advanced Cardiac Life Support', 'Echocardiography'],
    languages: ['English', 'Mandarin', 'Cantonese'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.9,
      medicationAccuracy: 99.1,
      responseTime: '1.8 hours'
    }
  },
  { 
    id: 6, 
    username: 'dr.patel', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Priya Patel', 
    title: 'Psychiatrist', 
    specialization: 'Geriatric Psychiatry',
    qualifications: ['MBBS', 'FRANZCP', 'Dip Geriatric Psychiatry'],
    experience: 15,
    facilities: ['sunshine', 'rosewood'], 
    phone: '0415 678 901',
    email: 'dr.patel@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['Depression', 'Anxiety', 'Dementia', 'Behavioral Management'],
    certifications: ['Mental Health First Aid', 'Cognitive Behavioral Therapy'],
    languages: ['English', 'Hindi', 'Gujarati'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.6,
      medicationAccuracy: 96.8,
      responseTime: '2.7 hours'
    }
  },
  { 
    id: 7, 
    username: 'dr.brown', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. James Brown', 
    title: 'Orthopedic Surgeon', 
    specialization: 'Orthopedics',
    qualifications: ['MBBS', 'FRACS', 'Orthopedic Fellowship'],
    experience: 20,
    facilities: ['bayview', 'greenhill'], 
    phone: '0416 789 012',
    email: 'dr.brown@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Joint Replacement', 'Fracture Management', 'Osteoporosis'],
    certifications: ['Trauma Surgery', 'Joint Replacement Specialist'],
    languages: ['English'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.7,
      medicationAccuracy: 98.3,
      responseTime: '3.5 hours'
    }
  },
  { 
    id: 8, 
    username: 'dr.garcia', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Maria Garcia', 
    title: 'Endocrinologist', 
    specialization: 'Endocrinology',
    qualifications: ['MBBS', 'FRACP', 'Endocrinology Fellowship'],
    experience: 16,
    facilities: ['sunshine', 'rosewood'], 
    phone: '0417 890 123',
    email: 'dr.garcia@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['Diabetes Management', 'Thyroid Disorders', 'Metabolic Disorders'],
    certifications: ['Diabetes Educator', 'Thyroid Specialist'],
    languages: ['English', 'Spanish'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.8,
      medicationAccuracy: 97.9,
      responseTime: '2.1 hours'
    }
  },
  { 
    id: 9, 
    username: 'dr.kim', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. David Kim', 
    title: 'Neurologist', 
    specialization: 'Neurology',
    qualifications: ['MBBS', 'FRACP', 'Neurology Fellowship'],
    experience: 19,
    facilities: ['bayview', 'greenhill'], 
    phone: '0418 901 234',
    email: 'dr.kim@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Stroke Management', 'Parkinson\'s Disease', 'Multiple Sclerosis'],
    certifications: ['Stroke Specialist', 'Movement Disorders'],
    languages: ['English', 'Korean'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.9,
      medicationAccuracy: 98.7,
      responseTime: '2.0 hours'
    }
  },
  { 
    id: 10, 
    username: 'dr.anderson', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Lisa Anderson', 
    title: 'Respiratory Physician', 
    specialization: 'Respiratory Medicine',
    qualifications: ['MBBS', 'FRACP', 'Respiratory Fellowship'],
    experience: 17,
    facilities: ['sunshine', 'rosewood'], 
    phone: '0419 012 345',
    email: 'dr.anderson@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['COPD', 'Sleep Apnea', 'Pulmonary Rehabilitation'],
    certifications: ['Sleep Medicine', 'Pulmonary Function Testing'],
    languages: ['English'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.7,
      medicationAccuracy: 97.5,
      responseTime: '2.4 hours'
    }
  },
  { 
    id: 11, 
    username: 'dr.thompson', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Andrew Thompson', 
    title: 'Urologist', 
    specialization: 'Urology',
    qualifications: ['MBBS', 'FRACS', 'Urology Fellowship'],
    experience: 21,
    facilities: ['bayview', 'greenhill'], 
    phone: '0420 123 456',
    email: 'dr.thompson@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Prostate Health', 'Urinary Incontinence', 'Kidney Stones'],
    certifications: ['Prostate Cancer Specialist', 'Urodynamics'],
    languages: ['English'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.6,
      medicationAccuracy: 98.1,
      responseTime: '3.2 hours'
    }
  },
  { 
    id: 12, 
    username: 'dr.lee', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Jennifer Lee', 
    title: 'Dermatologist', 
    specialization: 'Dermatology',
    qualifications: ['MBBS', 'FRACP', 'Dermatology Fellowship'],
    experience: 14,
    facilities: ['sunshine', 'rosewood'], 
    phone: '0421 234 567',
    email: 'dr.lee@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['Skin Cancer', 'Wound Care', 'Elderly Skin Conditions'],
    certifications: ['Skin Cancer Surgery', 'Wound Care Specialist'],
    languages: ['English', 'Mandarin'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.8,
      medicationAccuracy: 97.8,
      responseTime: '2.6 hours'
    }
  },
  { 
    id: 13, 
    username: 'dr.white', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Robert White', 
    title: 'Gastroenterologist', 
    specialization: 'Gastroenterology',
    qualifications: ['MBBS', 'FRACP', 'Gastroenterology Fellowship'],
    experience: 18,
    facilities: ['bayview', 'greenhill'], 
    phone: '0422 345 678',
    email: 'dr.white@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Digestive Disorders', 'Colon Cancer Screening', 'Inflammatory Bowel Disease'],
    certifications: ['Endoscopy Specialist', 'Colonoscopy'],
    languages: ['English'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.7,
      medicationAccuracy: 98.2,
      responseTime: '2.8 hours'
    }
  },
  { 
    id: 14, 
    username: 'dr.martinez', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Carlos Martinez', 
    title: 'Rheumatologist', 
    specialization: 'Rheumatology',
    qualifications: ['MBBS', 'FRACP', 'Rheumatology Fellowship'],
    experience: 16,
    facilities: ['sunshine', 'rosewood'], 
    phone: '0423 456 789',
    email: 'dr.martinez@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['Arthritis', 'Autoimmune Diseases', 'Joint Pain Management'],
    certifications: ['Rheumatoid Arthritis Specialist', 'Joint Injection'],
    languages: ['English', 'Spanish'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.6,
      medicationAccuracy: 97.4,
      responseTime: '3.0 hours'
    }
  },
  { 
    id: 15, 
    username: 'dr.clark', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Sarah Clark', 
    title: 'Palliative Care Physician', 
    specialization: 'Palliative Care',
    qualifications: ['MBBS', 'FRACP', 'Palliative Care Fellowship'],
    experience: 12,
    facilities: ['sunshine', 'bayview'], 
    phone: '0424 567 890',
    email: 'dr.clark@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['Pain Management', 'End-of-Life Care', 'Symptom Control'],
    certifications: ['Palliative Care Certification', 'Pain Management Specialist'],
    languages: ['English'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.9,
      medicationAccuracy: 99.0,
      responseTime: '1.5 hours'
    }
  },
  { 
    id: 16, 
    username: 'dr.taylor', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Michael Taylor', 
    title: 'Geriatrician', 
    specialization: 'Geriatric Medicine',
    qualifications: ['MBBS', 'FRACP', 'Geriatric Medicine Fellowship'],
    experience: 23,
    facilities: ['greenhill', 'rosewood'], 
    phone: '0425 678 901',
    email: 'dr.taylor@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Comprehensive Geriatric Assessment', 'Falls Prevention', 'Polypharmacy Management'],
    certifications: ['Geriatric Assessment', 'Falls Prevention Specialist'],
    languages: ['English'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.8,
      medicationAccuracy: 98.6,
      responseTime: '2.2 hours'
    }
  },
  { 
    id: 17, 
    username: 'dr.harris', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Emma Harris', 
    title: 'Rehabilitation Physician', 
    specialization: 'Rehabilitation Medicine',
    qualifications: ['MBBS', 'FRACP', 'Rehabilitation Fellowship'],
    experience: 15,
    facilities: ['bayview', 'greenhill'], 
    phone: '0426 789 012',
    email: 'dr.harris@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['Stroke Rehabilitation', 'Orthopedic Rehabilitation', 'Functional Assessment'],
    certifications: ['Rehabilitation Specialist', 'Functional Assessment'],
    languages: ['English'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.7,
      medicationAccuracy: 97.9,
      responseTime: '2.5 hours'
    }
  },
  { 
    id: 18, 
    username: 'dr.rodriguez', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Sofia Rodriguez', 
    title: 'Infectious Disease Specialist', 
    specialization: 'Infectious Diseases',
    qualifications: ['MBBS', 'FRACP', 'Infectious Disease Fellowship'],
    experience: 14,
    facilities: ['sunshine', 'rosewood'], 
    phone: '0427 890 123',
    email: 'dr.rodriguez@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Antibiotic Management', 'Infection Control', 'Tropical Diseases'],
    certifications: ['Antimicrobial Stewardship', 'Infection Control Specialist'],
    languages: ['English', 'Spanish'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.6,
      medicationAccuracy: 98.3,
      responseTime: '2.9 hours'
    }
  },
  { 
    id: 19, 
    username: 'dr.murphy', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Patrick Murphy', 
    title: 'Emergency Medicine Physician', 
    specialization: 'Emergency Medicine',
    qualifications: ['MBBS', 'FACEM', 'Emergency Medicine Fellowship'],
    experience: 20,
    facilities: ['sunshine', 'bayview'], 
    phone: '0428 901 234',
    email: 'dr.murphy@medfastcrm.com.au',
    availability: {
      monday: '12:00-20:00',
      tuesday: '12:00-20:00',
      wednesday: '12:00-20:00',
      thursday: '12:00-20:00',
      friday: '12:00-20:00'
    },
    specializations: ['Emergency Care', 'Trauma Management', 'Critical Care'],
    certifications: ['Advanced Life Support', 'Trauma Specialist'],
    languages: ['English'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.8,
      medicationAccuracy: 98.9,
      responseTime: '0.5 hours'
    }
  },
  { 
    id: 20, 
    username: 'dr.nguyen', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Linh Nguyen', 
    title: 'General Practitioner', 
    specialization: 'Aged Care',
    qualifications: ['MBBS', 'FRACGP', 'Dip Geriatric Medicine'],
    experience: 11,
    facilities: ['greenhill', 'rosewood'], 
    phone: '0429 012 345',
    email: 'dr.nguyen@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Chronic Disease Management', 'Preventive Care', 'Mental Health'],
    certifications: ['Mental Health First Aid', 'Chronic Disease Management'],
    languages: ['English', 'Vietnamese'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.5,
      medicationAccuracy: 96.7,
      responseTime: '3.3 hours'
    }
  },
  { 
    id: 21, 
    username: 'dr.zhang', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Wei Zhang', 
    title: 'General Practitioner', 
    specialization: 'Aged Care',
    qualifications: ['MBBS', 'FRACGP', 'Dip Geriatric Medicine'],
    experience: 13,
    facilities: ['sunshine', 'bayview'], 
    phone: '0430 123 456',
    email: 'dr.zhang@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['Diabetes Management', 'Hypertension', 'Preventive Care'],
    certifications: ['Diabetes Educator', 'Hypertension Specialist'],
    languages: ['English', 'Mandarin', 'Cantonese'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.7,
      medicationAccuracy: 97.8,
      responseTime: '2.7 hours'
    }
  },
  { 
    id: 22, 
    username: 'dr.kumar', 
    password: 'doctor123', 
    role: 'doctor', 
    name: 'Dr. Rajesh Kumar', 
    title: 'General Practitioner', 
    specialization: 'Aged Care',
    qualifications: ['MBBS', 'FRACGP', 'Dip Geriatric Medicine'],
    experience: 16,
    facilities: ['greenhill', 'rosewood'], 
    phone: '0431 234 567',
    email: 'dr.kumar@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Cardiovascular Health', 'Respiratory Medicine', 'Mental Health'],
    certifications: ['Cardiovascular Health', 'Mental Health First Aid'],
    languages: ['English', 'Hindi', 'Punjabi'],
    permissions: rolePermissions.doctor.permissions,
    performance: {
      patientSatisfaction: 4.6,
      medicationAccuracy: 97.5,
      responseTime: '2.8 hours'
    }
  },
  // Nurses
  { 
    id: 23, 
    username: 'nurse.mitchell', 
    password: 'nurse123', 
    role: 'nurse', 
    name: 'Sarah Mitchell', 
    title: 'Registered Nurse', 
    specialization: 'Aged Care',
    qualifications: ['RN', 'Dip Nursing', 'Geriatric Care Certificate'],
    experience: 8,
    facilities: ['sunshine'], 
    phone: '0432 345 678',
    email: 'sarah.mitchell@medfastcrm.com.au',
    availability: {
      monday: '7:00-15:00',
      tuesday: '7:00-15:00',
      wednesday: '7:00-15:00',
      thursday: '7:00-15:00',
      friday: '7:00-15:00'
    },
    specializations: ['Medication Administration', 'Wound Care', 'Vital Signs'],
    certifications: ['Medication Administration', 'Wound Care Specialist'],
    languages: ['English'],
    permissions: rolePermissions.nurse.permissions,
    performance: {
      patientSatisfaction: 4.8,
      medicationAccuracy: 99.2,
      responseTime: '1.2 hours'
    }
  },
  { 
    id: 24, 
    username: 'nurse.garcia', 
    password: 'nurse123', 
    role: 'nurse', 
    name: 'Maria Garcia', 
    title: 'Registered Nurse', 
    specialization: 'Aged Care',
    qualifications: ['RN', 'Dip Nursing', 'Geriatric Care Certificate'],
    experience: 6,
    facilities: ['bayview'], 
    phone: '0433 456 789',
    email: 'maria.garcia@medfastcrm.com.au',
    availability: {
      monday: '15:00-23:00',
      tuesday: '15:00-23:00',
      wednesday: '15:00-23:00',
      thursday: '15:00-23:00',
      friday: '15:00-23:00'
    },
    specializations: ['Medication Administration', 'Personal Care', 'Family Support'],
    certifications: ['Medication Administration', 'Family Support'],
    languages: ['English', 'Spanish'],
    permissions: rolePermissions.nurse.permissions,
    performance: {
      patientSatisfaction: 4.7,
      medicationAccuracy: 98.8,
      responseTime: '1.5 hours'
    }
  },
  // Physiotherapists
  { 
    id: 25, 
    username: 'physio.davis', 
    password: 'physio123', 
    role: 'physiotherapist', 
    name: 'Mark Davis', 
    title: 'Physiotherapist', 
    specialization: 'Geriatric Physiotherapy',
    qualifications: ['BSc Physiotherapy', 'MSc Geriatric Physiotherapy'],
    experience: 5,
    facilities: ['sunshine', 'bayview'], 
    phone: '0434 567 890',
    email: 'mark.davis@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Mobility Assessment', 'Fall Prevention', 'Exercise Programs'],
    certifications: ['Fall Prevention Specialist', 'Exercise Physiology'],
    languages: ['English'],
    permissions: rolePermissions.physiotherapist.permissions,
    performance: {
      patientSatisfaction: 4.9,
      treatmentEffectiveness: 95.5,
      responseTime: '2.0 hours'
    }
  },
  // Pharmacists
  { 
    id: 26, 
    username: 'pharmacist.wilson', 
    password: 'pharmacist123', 
    role: 'pharmacist', 
    name: 'Emma Wilson', 
    title: 'Clinical Pharmacist', 
    specialization: 'Aged Care Pharmacy',
    qualifications: ['BPharm', 'MSc Clinical Pharmacy', 'Geriatric Pharmacy Certificate'],
    experience: 7,
    facilities: ['all'], 
    phone: '0435 678 901',
    email: 'emma.wilson@medfastcrm.com.au',
    availability: {
      monday: '9:00-17:00',
      tuesday: '9:00-17:00',
      wednesday: '9:00-17:00',
      thursday: '9:00-17:00',
      friday: '9:00-17:00'
    },
    specializations: ['Medication Reviews', 'Drug Interactions', 'Polypharmacy Management'],
    certifications: ['Medication Review Specialist', 'Drug Interaction Specialist'],
    languages: ['English'],
    permissions: rolePermissions.pharmacist.permissions,
    performance: {
      medicationAccuracy: 99.8,
      reviewCompleteness: 98.5,
      responseTime: '1.0 hours'
    }
  },
  // Care Workers
  { 
    id: 27, 
    username: 'care.brown', 
    password: 'care123', 
    role: 'care_worker', 
    name: 'Tom Brown', 
    title: 'Care Worker', 
    specialization: 'Personal Care',
    qualifications: ['Cert III Individual Support', 'First Aid Certificate'],
    experience: 3,
    facilities: ['sunshine'], 
    phone: '0436 789 012',
    email: 'tom.brown@medfastcrm.com.au',
    availability: {
      monday: '6:00-14:00',
      tuesday: '6:00-14:00',
      wednesday: '6:00-14:00',
      thursday: '6:00-14:00',
      friday: '6:00-14:00'
    },
    specializations: ['Personal Care', 'Meal Assistance', 'Activity Support'],
    certifications: ['Personal Care', 'Meal Assistance'],
    languages: ['English'],
    permissions: rolePermissions.care_worker.permissions,
    performance: {
      patientSatisfaction: 4.6,
      careQuality: 94.2,
      responseTime: '0.8 hours'
    }
  },
  // Activities Coordinators
  { 
    id: 28, 
    username: 'activities.johnson', 
    password: 'activities123', 
    role: 'activities_coordinator', 
    name: 'Lisa Johnson', 
    title: 'Activities Coordinator', 
    specialization: 'Recreational Activities',
    qualifications: ['Dip Leisure & Health', 'Cert IV Training & Assessment'],
    experience: 4,
    facilities: ['sunshine', 'bayview'], 
    phone: '0437 890 123',
    email: 'lisa.johnson@medfastcrm.com.au',
    availability: {
      monday: '8:00-16:00',
      tuesday: '8:00-16:00',
      wednesday: '8:00-16:00',
      thursday: '8:00-16:00',
      friday: '8:00-16:00'
    },
    specializations: ['Group Activities', 'Individual Programs', 'Family Events'],
    certifications: ['Activities Planning', 'Dementia Care Activities'],
    languages: ['English'],
    permissions: rolePermissions.activities_coordinator.permissions,
    performance: {
      participationRate: 85.3,
      activityVariety: 92.1,
      familySatisfaction: 4.7
    }
  },
  // Family Members
  { 
    id: 29, 
    username: 'family.thompson', 
    password: 'family123', 
    role: 'family', 
    name: 'Sarah Thompson', 
    title: 'Family Member', 
    relationship: 'Daughter',
    residentId: 1,
    phone: '0412 345 678',
    email: 'sarah.thompson@email.com',
    permissions: rolePermissions.family.permissions,
    accessLevel: 'Full',
    notificationPreferences: ['medication_changes', 'health_updates', 'activities']
  }
];

export const facilities = [
  { 
    id: 'sunshine', 
    name: 'Sunshine Gardens Aged Care', 
    address: '123 Care Street, Melbourne VIC 3000', 
    phone: '(03) 9123 4567', 
    email: 'sunshine@medfastcrm.com.au', 
    manager: 'Sarah Mitchell', 
    beds: 120, 
    occupancy: 94, 
    rating: 4.8, 
    totalResidents: 89, 
    totalWards: 4, 
    type: 'Large Facility' 
  },
  { 
    id: 'bayview', 
    name: 'Bayview Gardens Aged Care', 
    address: '456 Ocean Road, St Kilda VIC 3182', 
    phone: '(03) 9876 5432', 
    email: 'bayview@medfastcrm.com.au', 
    manager: 'Michael Chen', 
    beds: 85, 
    occupancy: 91, 
    rating: 4.7, 
    totalResidents: 56, 
    totalWards: 3, 
    type: 'Medium Facility' 
  },
  { 
    id: 'greenhill', 
    name: 'Green Hill Residence', 
    address: '789 Park Avenue, Brighton VIC 3186', 
    phone: '(03) 9555 1234', 
    email: 'greenhill@medfastcrm.com.au', 
    manager: 'David Wilson', 
    beds: 60, 
    occupancy: 88, 
    rating: 4.9, 
    totalResidents: 42, 
    totalWards: 2, 
    type: 'Boutique Facility' 
  },
  { 
    id: 'rosewood', 
    name: 'Rosewood Care Community', 
    address: '321 Garden Lane, Hawthorn VIC 3122', 
    phone: '(03) 9444 7890', 
    email: 'rosewood@medfastcrm.com.au', 
    manager: 'Emma Thompson', 
    beds: 100, 
    occupancy: 92, 
    rating: 4.6, 
    totalResidents: 78, 
    totalWards: 4, 
    type: 'Large Facility' 
  },
  { 
    id: 'ndis_sunshine_support', 
    name: 'NDIS Sunshine Support Services', 
    address: '123 Support Street, Medical City, MC 12345', 
    phone: '(03) 9123 4568', 
    email: 'sunshine.support@ndis.com.au', 
    manager: 'Jennifer Walsh', 
    beds: 45, 
    occupancy: 89, 
    rating: 4.7, 
    totalResidents: 40, 
    totalWards: 3, 
    type: 'NDIS Facility' 
  },
  { 
    id: 'ndis_bayview_community', 
    name: 'NDIS Bayview Community Services', 
    address: '456 Community Road, Medical City, MC 12345', 
    phone: '(03) 9876 5433', 
    email: 'bayview.community@ndis.com.au', 
    manager: 'Robert Kim', 
    beds: 38, 
    occupancy: 92, 
    rating: 4.8, 
    totalResidents: 35, 
    totalWards: 2, 
    type: 'NDIS Facility' 
  },
  { 
    id: 'ndis_house_1', 
    name: 'NDIS House 1 - Independence Living', 
    address: '15 Freedom Street, Melbourne VIC 3000', 
    phone: '(03) 9555 1235', 
    email: 'house1.independence@ndis.com.au', 
    manager: 'Lisa Chen', 
    beds: 12, 
    occupancy: 100, 
    rating: 4.9, 
    totalResidents: 12, 
    totalWards: 1, 
    type: 'NDIS House' 
  },
  { 
    id: 'ndis_house_2', 
    name: 'NDIS House 2 - Community Hub', 
    address: '27 Community Road, St Kilda VIC 3182', 
    phone: '(03) 9444 7891', 
    email: 'house2.community@ndis.com.au', 
    manager: 'Mark Thompson', 
    beds: 15, 
    occupancy: 93, 
    rating: 4.6, 
    totalResidents: 14, 
    totalWards: 1, 
    type: 'NDIS House' 
  },
  { 
    id: 'ndis_house_3', 
    name: 'NDIS House 3 - Wellness Center', 
    address: '42 Wellness Avenue, Hawthorn VIC 3122', 
    phone: '(03) 9444 7892', 
    email: 'house3.wellness@ndis.com.au', 
    manager: 'Sarah Davis', 
    beds: 18, 
    occupancy: 94, 
    rating: 4.8, 
    totalResidents: 17, 
    totalWards: 1, 
    type: 'NDIS House' 
  }
];

export const wardsByFacility = {
  sunshine: [
    { 
      id: 'ward_a', 
      name: 'Ward A - Memory Care', 
      capacity: 30, 
      occupied: 28, 
      careLevel: 'High', 
      specialization: 'Dementia & Alzheimer\'s', 
      staffRatio: '1:3', 
      manager: 'Jessica Lee', 
      activeAlerts: 2, 
      pendingMeds: 5, 
      connectedFamilies: 18, 
      automatedWorkflows: 12, 
      adherenceRate: 94.2 
    },
    { 
      id: 'ward_b', 
      name: 'Ward B - General Care', 
      capacity: 40, 
      occupied: 38, 
      careLevel: 'Medium', 
      specialization: 'General Aged Care', 
      staffRatio: '1:5', 
      manager: 'David Wilson', 
      activeAlerts: 1, 
      pendingMeds: 3, 
      connectedFamilies: 21, 
      automatedWorkflows: 10, 
      adherenceRate: 96.8 
    },
    { 
      id: 'ward_c', 
      name: 'Ward C - Assisted Living', 
      capacity: 25, 
      occupied: 23, 
      careLevel: 'Low', 
      specialization: 'Independent Living Support', 
      staffRatio: '1:6', 
      manager: 'Tom Anderson', 
      activeAlerts: 0, 
      pendingMeds: 2, 
      connectedFamilies: 15, 
      automatedWorkflows: 8, 
      adherenceRate: 98.1 
    },
    { 
      id: 'ward_d', 
      name: 'Ward D - Palliative Care', 
      capacity: 25, 
      occupied: 21, 
      careLevel: 'Critical', 
      specialization: 'End of Life Care', 
      staffRatio: '1:2', 
      manager: 'Emma White', 
      activeAlerts: 3, 
      pendingMeds: 6, 
      connectedFamilies: 14, 
      automatedWorkflows: 15, 
      adherenceRate: 91.5 
    }
  ],
  bayview: [
    { 
      id: 'ocean_wing', 
      name: 'Ocean Wing', 
      capacity: 30, 
      occupied: 27, 
      careLevel: 'Medium', 
      specialization: 'General Care', 
      staffRatio: '1:5', 
      manager: 'Rachel Green', 
      activeAlerts: 1, 
      pendingMeds: 2, 
      connectedFamilies: 16, 
      automatedWorkflows: 9, 
      adherenceRate: 95.5 
    },
    { 
      id: 'garden_wing', 
      name: 'Garden Wing', 
      capacity: 30, 
      occupied: 28, 
      careLevel: 'High', 
      specialization: 'Memory Care', 
      staffRatio: '1:3', 
      manager: 'Mark Davis', 
      activeAlerts: 2, 
      pendingMeds: 4, 
      connectedFamilies: 17, 
      automatedWorkflows: 11, 
      adherenceRate: 93.8 
    },
    { 
      id: 'sunset_wing', 
      name: 'Sunset Wing', 
      capacity: 25, 
      occupied: 23, 
      careLevel: 'Low', 
      specialization: 'Assisted Living', 
      staffRatio: '1:6', 
      manager: 'Lisa Wang', 
      activeAlerts: 0, 
      pendingMeds: 1, 
      connectedFamilies: 13, 
      automatedWorkflows: 7, 
      adherenceRate: 97.2 
    }
  ],
  ndis_sunshine_support: [
    { 
      id: 'support_wing_a', 
      name: 'Support Wing A - Daily Living', 
      capacity: 15, 
      occupied: 14, 
      careLevel: 'Medium', 
      specialization: 'Daily Living Support', 
      staffRatio: '1:4', 
      manager: 'Jennifer Walsh', 
      activeAlerts: 1, 
      pendingMeds: 2, 
      connectedFamilies: 12, 
      automatedWorkflows: 8, 
      adherenceRate: 96.5 
    },
    { 
      id: 'support_wing_b', 
      name: 'Support Wing B - Community Access', 
      capacity: 15, 
      occupied: 13, 
      careLevel: 'Low', 
      specialization: 'Community Access & Social', 
      staffRatio: '1:5', 
      manager: 'Michael Torres', 
      activeAlerts: 0, 
      pendingMeds: 1, 
      connectedFamilies: 11, 
      automatedWorkflows: 6, 
      adherenceRate: 98.2 
    },
    { 
      id: 'support_wing_c', 
      name: 'Support Wing C - Skills Development', 
      capacity: 15, 
      occupied: 13, 
      careLevel: 'Medium', 
      specialization: 'Skills Development & Training', 
      staffRatio: '1:4', 
      manager: 'Sarah Johnson', 
      activeAlerts: 1, 
      pendingMeds: 2, 
      connectedFamilies: 10, 
      automatedWorkflows: 7, 
      adherenceRate: 95.8 
    }
  ],
  ndis_bayview_community: [
    { 
      id: 'community_wing_a', 
      name: 'Community Wing A - Independent Living', 
      capacity: 19, 
      occupied: 18, 
      careLevel: 'Low', 
      specialization: 'Independent Living Skills', 
      staffRatio: '1:6', 
      manager: 'Robert Kim', 
      activeAlerts: 0, 
      pendingMeds: 1, 
      connectedFamilies: 15, 
      automatedWorkflows: 5, 
      adherenceRate: 97.8 
    },
    { 
      id: 'community_wing_b', 
      name: 'Community Wing B - Social Participation', 
      capacity: 19, 
      occupied: 17, 
      careLevel: 'Medium', 
      specialization: 'Social Participation & Recreation', 
      staffRatio: '1:4', 
      manager: 'Emma Wilson', 
      activeAlerts: 1, 
      pendingMeds: 2, 
      connectedFamilies: 14, 
      automatedWorkflows: 8, 
      adherenceRate: 96.1 
    }
  ],
  ndis_house_1: [
    { 
      id: 'independence_house', 
      name: 'Independence Living House', 
      capacity: 12, 
      occupied: 12, 
      careLevel: 'Low', 
      specialization: 'Independent Living with Support', 
      staffRatio: '1:6', 
      manager: 'Lisa Chen', 
      activeAlerts: 0, 
      pendingMeds: 0, 
      connectedFamilies: 10, 
      automatedWorkflows: 4, 
      adherenceRate: 99.1 
    }
  ],
  ndis_house_2: [
    { 
      id: 'community_hub_house', 
      name: 'Community Hub House', 
      capacity: 15, 
      occupied: 14, 
      careLevel: 'Medium', 
      specialization: 'Community Integration & Support', 
      staffRatio: '1:5', 
      manager: 'Mark Thompson', 
      activeAlerts: 1, 
      pendingMeds: 1, 
      connectedFamilies: 12, 
      automatedWorkflows: 6, 
      adherenceRate: 97.3 
    }
  ],
  ndis_house_3: [
    { 
      id: 'wellness_center_house', 
      name: 'Wellness Center House', 
      capacity: 18, 
      occupied: 17, 
      careLevel: 'Medium', 
      specialization: 'Health & Wellness Support', 
      staffRatio: '1:4', 
      manager: 'Sarah Davis', 
      activeAlerts: 1, 
      pendingMeds: 2, 
      connectedFamilies: 14, 
      automatedWorkflows: 7, 
      adherenceRate: 96.7 
    }
  ]
};

export const residents = [
  // Original 5 residents (enhanced)
  {
    id: 1,
    name: 'Margaret Rose Thompson',
    photo: '👵',
    room: 'A101',
    facility: 'sunshine',
    ward: 'ward_a',
    age: 83,
    dateOfBirth: '1941-03-15',
    admissionDate: '2022-06-10',
    medicare: 'ETHO3151941',
    careLevel: 'High',
    mobility: 'Walker for distances > 20m',
    cognitiveStatus: 'Mild Dementia',
    primaryDiagnosis: ['Alzheimer\'s Disease', 'Type 2 Diabetes', 'Osteoarthritis', 'Hypertension', 'Heart Disease', 'COPD'],
    secondaryConditions: ['Osteoporosis', 'Sleep Apnea', 'Depression', 'Anxiety', 'Urinary Incontinence', 'Vision Impairment'],
    allergies: ['Penicillin', 'Shellfish', 'Latex'],
    dietaryRestrictions: ['Diabetic Diet', 'Low Sodium', 'Pureed'],
    weight: 68,
    height: 162,
    bloodType: 'O+',
    emergencyContact: {
      name: 'Sarah Thompson',
      relationship: 'Daughter',
      phone: '0412 345 678',
      email: 'sarah.thompson@email.com',
      familyConnectEnabled: true,
      lastLogin: '2025-07-23 14:30',
      notificationPreferences: ['medication_changes', 'health_updates', 'activities']
    },
    primaryPhysician: 'Dr. Robert Smith',
    careTeam: ['Nurse Sarah Mitchell', 'Care Worker Tom Brown'],
    prescriptions: [
      { 
        id: 'RX001', 
        medication: 'Donepezil 10mg', 
        dosage: '10mg once daily at bedtime', 
        reason: 'Alzheimer\'s Disease',
        doctor: 'Dr. Robert Smith', 
        issued: '2025-06-01', 
        expires: '2025-08-01', 
        refillsLeft: 2, 
        totalRefills: 5, 
        status: 'Active', 
        instructions: 'Take at bedtime', 
        sideEffects: ['Nausea', 'Diarrhea'], 
        lastTaken: '2025-07-24 21:00', 
        adherenceRate: 98, 
        automatedDispense: true 
      },
      { 
        id: 'RX002', 
        medication: 'Metformin 500mg', 
        dosage: '500mg twice daily', 
        reason: 'Type 2 Diabetes',
        doctor: 'Dr. Robert Smith', 
        issued: '2025-05-15', 
        expires: '2025-07-25', 
        refillsLeft: 0, 
        totalRefills: 3, 
        status: 'Expiring Soon', 
        instructions: 'Take with meals', 
        sideEffects: ['Nausea', 'Stomach upset'], 
        lastTaken: '2025-07-24 08:00', 
        adherenceRate: 95, 
        automatedDispense: true 
      },
      { 
        id: 'RX003', 
        medication: 'Lisinopril 10mg', 
        dosage: '10mg once daily', 
        reason: 'Hypertension',
        doctor: 'Dr. Robert Smith', 
        issued: '2025-06-10', 
        expires: '2025-08-10', 
        refillsLeft: 3, 
        totalRefills: 5, 
        status: 'Active', 
        instructions: 'Take in the morning', 
        sideEffects: ['Dizziness', 'Dry cough'], 
        lastTaken: '2025-07-24 08:00', 
        adherenceRate: 97, 
        automatedDispense: true 
      },
      { 
        id: 'RX004', 
        medication: 'Atorvastatin 20mg', 
        dosage: '20mg once daily', 
        reason: 'High Cholesterol',
        doctor: 'Dr. Robert Smith', 
        issued: '2025-05-20', 
        expires: '2025-08-20', 
        refillsLeft: 2, 
        totalRefills: 4, 
        status: 'Active', 
        instructions: 'Take at bedtime', 
        sideEffects: ['Muscle pain'], 
        lastTaken: '2025-07-24 21:00', 
        adherenceRate: 96, 
        automatedDispense: true 
      },
      { 
        id: 'RX005', 
        medication: 'Paracetamol 500mg', 
        dosage: '500mg every 6 hours as needed', 
        reason: 'Osteoarthritis Pain',
        doctor: 'Dr. Robert Smith', 
        issued: '2025-06-01', 
        expires: '2025-09-01', 
        refillsLeft: 4, 
        totalRefills: 6, 
        status: 'Active', 
        instructions: 'Take for pain as needed', 
        sideEffects: [], 
        lastTaken: '2025-07-24 14:00', 
        adherenceRate: 90, 
        automatedDispense: false 
      },
      { 
        id: 'RX006', 
        medication: 'Salbutamol Inhaler', 
        dosage: '2 puffs as needed', 
        reason: 'COPD',
        doctor: 'Dr. Robert Smith', 
        issued: '2025-05-15', 
        expires: '2025-08-15', 
        refillsLeft: 1, 
        totalRefills: 3, 
        status: 'Active', 
        instructions: 'Use for shortness of breath', 
        sideEffects: ['Tremor', 'Rapid heartbeat'], 
        lastTaken: '2025-07-24 10:00', 
        adherenceRate: 94, 
        automatedDispense: false 
      },
      { 
        id: 'RX007', 
        medication: 'Furosemide 40mg', 
        dosage: '40mg once daily', 
        reason: 'Heart Disease',
        doctor: 'Dr. Robert Smith', 
        issued: '2025-06-05', 
        expires: '2025-08-05', 
        refillsLeft: 2, 
        totalRefills: 4, 
        status: 'Active', 
        instructions: 'Take in the morning', 
        sideEffects: ['Frequent urination', 'Dizziness'], 
        lastTaken: '2025-07-24 08:00', 
        adherenceRate: 99, 
        automatedDispense: true 
      }
    ],
    medicalHistory: [
      { 
        date: '2025-07-24', 
        event: 'Blood glucose check', 
        result: '6.8 mmol/L', 
        status: 'Normal', 
        doctor: 'Dr. Smith', 
        automated: true,
        type: 'AI-Monitored'
      },
      { 
        date: '2025-07-20', 
        event: 'Cognitive assessment', 
        result: 'MMSE Score: 18/30', 
        status: 'Stable', 
        doctor: 'Dr. Smith', 
        automated: false
      }
    ],
    doctorNotes: [
      { 
        id: 'DN001', 
        date: '2025-07-24', 
        doctor: 'Dr. Robert Smith', 
        type: 'Progress Note', 
        noteFormat: 'SOAP', 
        content: 'Patient showing stable cognitive function with current Donepezil dosage. Diabetes well controlled.', 
        subjective: 'Patient reports feeling well, engaged in activities', 
        objective: 'Alert, cooperative, MMSE 18/30', 
        assessment: 'Alzheimer\'s disease stable, diabetes controlled', 
        plan: 'Continue current medications, monitor cognitive function monthly', 
        priority: 'Normal', 
        sharedWithFamily: true,
        followUpRequired: '2025-08-24'
      }
    ],
    carePlan: {
      goals: [
        'Maintain cognitive function and slow progression of Alzheimer\'s',
        'Control diabetes and maintain stable blood glucose levels',
        'Prevent falls and maintain mobility',
        'Maintain social engagement and reduce isolation',
        'Manage pain from osteoarthritis effectively',
        'Control hypertension and prevent cardiovascular complications',
        'Improve respiratory function and manage COPD symptoms',
        'Support family involvement in care planning'
      ],
      interventions: [
        'Memory exercises and cognitive stimulation',
        'Regular blood glucose monitoring and diabetes management',
        'Comprehensive fall prevention program',
        'Social activities and engagement programs',
        'Pain management for osteoarthritis',
        'Blood pressure monitoring and medication management',
        'Respiratory therapy and COPD management',
        'Family education and support sessions'
      ],
      assessments: {
        cognitive: { score: 18, date: '2025-07-20', tool: 'MMSE' },
        falls: { risk: 'Medium', date: '2025-07-15', score: 4 }
      },
      reviewDate: '2025-08-15'
    },
    lastAssessment: '2025-07-24',
    nextAssessment: '2025-08-24',
    fallRisk: 'Medium',
    cognitiveScore: 18,
    behavioralNotes: 'Enjoys music therapy, sometimes forgets names',
    monthlyMedicationCost: 156.80,
    yearToDateCost: 1097.60,
    familyUpdates: [
      { date: '2025-07-23', type: 'activity', message: 'Participated in music therapy session', read: true },
      { date: '2025-07-22', type: 'health', message: 'Blood sugar levels stable', read: false }
    ],
    advanceCareDirective: true,
    resuscitationStatus: 'DNR',
    familyMembers: [
      {
        id: 'FM001',
        name: 'Sarah Thompson',
        relationship: 'Daughter',
        phone: '0412 345 678',
        email: 'sarah.thompson@email.com',
        isPrimaryContact: true,
        notificationPreferences: ['medication_changes', 'health_updates', 'activities'],
        lastVisit: '2025-07-20',
        nextVisit: '2025-07-27',
        accessLevel: 'Full'
      }
    ],
    // ===== SPEC-ALIGNED SECTIONS =====
    demographics: {
      title: 'Mrs',
      legalFirstName: 'Margaret',
      legalMiddleNames: 'Rose',
      legalSurname: 'Thompson',
      preferredName: 'Maggie',
      previousSurname: 'O\'Brien',
      dateOfBirth: '1941-03-15',
      age: 83,
      gender: 'Female',
      pronouns: 'She/Her',
      sexAssignedAtBirth: 'Female',
      maritalStatus: 'Widowed',
      countryOfBirth: 'Australia',
      nationality: 'Australian',
      indigenousStatus: 'Neither',
      primaryLanguage: 'English',
      otherLanguages: [],
      interpreterRequired: false,
      communicationMethods: ['Hearing aid', 'Glasses'],
      communicationNotes: 'Speaks clearly but needs hearing aid; prefers face-to-face conversation',
      religion: 'Catholic',
      culturalBackground: 'Irish-Australian',
      veteranStatus: 'Not a veteran',
      dvaFileNumber: null,
      profilePhoto: null
    },
    governmentIds: {
      medicareNumber: '2345 67890 1',
      medicareIrn: 1,
      medicareExpiry: '2027-03-31',
      ihi: '8003 6100 0036 5432',
      pensionCardNumber: 'PEN-345678',
      pensionType: 'Age Pension',
      centrelinkCrn: '305 678 901A',
      ndisNumber: null,
      myAgedCareClientId: 'MAC-2022-061034',
      tfnEncrypted: '[ENCRYPTED]',
      tfnMasked: '***-***-341',
      privateHealthInsurer: 'Medibank',
      privateHealthNumber: 'MBK-9876543'
    },
    contactAddress: {
      homeAddress: { street: '42 Elm Street', suburb: 'Brighton', state: 'VIC', postcode: '3186', country: 'Australia' },
      mailingAddress: null,
      phoneMobile: '+61412345678',
      phoneHome: '+61395551234',
      email: null
    },
    admission: {
      admissionDate: '2022-06-10',
      admissionType: 'Permanent',
      admissionSource: 'Hospital',
      referringOrganisation: 'The Alfred Hospital',
      facilityId: 'sunshine',
      wingUnit: 'ward_a',
      roomNumber: 'A101',
      bedNumber: '1',
      roomType: 'Single',
      residentStatus: 'Active',
      expectedLengthOfStay: 'Permanent',
      dischargeDate: null,
      dischargeReason: null,
      dateOfDeath: null
    },
    funding: {
      anaccClass: 8,
      anaccClassificationDate: '2025-01-15',
      anaccReassessmentDue: '2026-01-15',
      anaccAssessorName: 'Dr. Anne Richards',
      baseCareTariff: 76.84,
      anaccSubsidyDaily: 198.45,
      entryAdjustmentPayment: null,
      supplements: [{ type: 'Oxygen', dailyRate: 12.50, startDate: '2024-06-01', endDate: null }],
      meansTestingStatus: 'Partially supported',
      accommodationPaymentType: 'Combination',
      radAmountAgreed: 350000,
      radBalance: 280000,
      dapDailyRate: 45.20,
      accommodationAgreementDate: '2022-06-10',
      meansAssessmentEffectiveDate: '2022-07-01',
      basicDailyFee: 61.96,
      incomeTestedCareFee: 28.50,
      extraServicesAgreement: { enabled: false, details: null }
    },
    clinical: {
      primaryDiagnosis: { code: 'G30.1', displayText: "Alzheimer's Disease, late onset" },
      secondaryDiagnoses: [
        { code: 'E11.9', displayText: 'Type 2 Diabetes Mellitus', onsetDate: '2005-03-20' },
        { code: 'M15.0', displayText: 'Primary Generalised Osteoarthritis', onsetDate: '2010-08-15' },
        { code: 'I10', displayText: 'Essential Hypertension', onsetDate: '2008-11-01' },
        { code: 'I25.9', displayText: 'Chronic Ischaemic Heart Disease', onsetDate: '2018-02-10' },
        { code: 'J44.1', displayText: 'COPD with Acute Exacerbation', onsetDate: '2019-06-15' }
      ],
      allergies: [
        { allergen: 'Penicillin', type: 'drug', reaction: 'Anaphylaxis', severity: 'severe', dateIdentified: '1985-06-15' },
        { allergen: 'Shellfish', type: 'food', reaction: 'Hives and swelling', severity: 'moderate', dateIdentified: '1990-12-01' },
        { allergen: 'Latex', type: 'environmental', reaction: 'Contact dermatitis', severity: 'mild', dateIdentified: '2010-04-20' }
      ],
      clinicalAlerts: ['Falls risk', 'Allergy', 'DNACPR'],
      bloodType: 'O+',
      heightCm: 162,
      weightKg: 68,
      weightHistory: [
        { date: '2025-07-01', weightKg: 68, measuredBy: 'Nurse Mitchell' },
        { date: '2025-06-01', weightKg: 68.5, measuredBy: 'Nurse Mitchell' },
        { date: '2025-05-01', weightKg: 69.2, measuredBy: 'Nurse Mitchell' },
        { date: '2025-01-15', weightKg: 70.1, measuredBy: 'Nurse Garcia' }
      ],
      bmi: 25.9,
      cognitiveStatus: 'Mild impairment',
      dementiaDiagnosis: { diagnosed: true, type: "Alzheimer's", dateDiagnosed: '2020-03-15' },
      mobilityStatus: 'Assisted (1 person)',
      fallsRiskRating: { level: 'Medium', score: 4 },
      pressureInjuryStatus: { present: false, stage: null, locations: [], woundPlanLink: null },
      continenceStatus: { bladder: 'Occasionally incontinent', bowel: 'Continent', aids: ['Pad'] },
      painLevel: { currentScore: 3, toolUsed: 'Numeric Rating Scale', lastAssessed: '2025-07-24T10:00:00Z' },
      infectionStatus: { currentInfections: [], isolationRequired: false, mrsa: false, vre: false, cdiff: false },
      immunisations: [
        { vaccine: 'COVID-19 (Pfizer)', dateGiven: '2025-03-15', batchNumber: 'FT1234', administeredBy: 'Nurse Mitchell', nextDue: '2025-09-15' },
        { vaccine: 'Influenza', dateGiven: '2025-04-01', batchNumber: 'FL5678', administeredBy: 'Nurse Mitchell', nextDue: '2026-04-01' },
        { vaccine: 'Pneumococcal', dateGiven: '2023-05-10', batchNumber: 'PN9012', administeredBy: 'Nurse Garcia', nextDue: '2028-05-10' }
      ],
      advanceCareDirective: { exists: true, documentId: 'DOC-ACD-001', reviewDate: '2025-12-01', summary: 'Comfort measures preferred. No aggressive interventions.' },
      resuscitationStatus: 'Not for resuscitation (NFR)',
      goalsOfCare: 'Focus on comfort, quality of life, and maintaining social connections. Family wishes to be informed of any significant changes.',
      restrictivePractices: []
    },
    dietary: {
      dietType: 'Diabetic',
      iddsiFoodLevel: 'Level 5 (Minced & Moist)',
      iddsiFluidLevel: 'Level 0 (Thin)',
      foodAllergies: [{ allergen: 'Shellfish', severity: 'moderate', reaction: 'Hives and swelling' }],
      dietaryPreferences: ['Low Sodium'],
      portionSize: 'Regular',
      fluidRestriction: { restricted: false, dailyLimitMl: null },
      nutritionalRiskScore: { score: 4, tool: 'MNA', dateAssessed: '2025-07-01', riskLevel: 'Low' },
      enteralFeeding: { feedType: null, rateMlHr: null, regime: null, startDate: null },
      mealtimeAssistance: 'Supervision'
    },
    personalLife: {
      lifeHistory: 'Margaret grew up in rural Victoria on a dairy farm. She married Thomas Thompson in 1962 and they had three children. She was a primary school teacher for 30 years before retiring. She is deeply connected to her Irish-Australian heritage and enjoys sharing stories about her childhood.',
      occupationHistory: 'Primary school teacher (1963-1993), School principal (1993-2001)',
      hobbiesInterests: ['Music therapy', 'Gardening', 'Knitting', 'Reading', 'Bingo'],
      musicPreferences: 'Classical music, Irish folk songs, Frank Sinatra',
      dailyRoutinePreferences: { wakeTime: '07:00', showerPreference: 'morning', mealPreferences: 'Enjoys tea with breakfast', bedtime: '21:00' },
      socialPreferences: 'Enjoys small group activities; becomes anxious in large crowds',
      petsAnimals: 'Had cats throughout her life; loves visits from therapy animals',
      importantDates: [{ date: '1962-04-15', description: 'Wedding anniversary' }, { date: '1965-08-20', description: "Daughter Sarah's birthday" }],
      comfortItems: 'Family photos on bedside table, rosary beads, knitted blanket from daughter'
    },
    supportNetwork: {
      primaryContact: { name: 'Sarah Thompson', relationship: 'Daughter', phone: '0412 345 678', phoneAlt: '03 9555 4567', email: 'sarah.thompson@email.com', address: { street: '15 Oak Avenue', suburb: 'Brighton', state: 'VIC', postcode: '3186', country: 'Australia' } },
      emergencyContacts: [
        { name: 'Sarah Thompson', relationship: 'Daughter', phone: '0412 345 678', email: 'sarah.thompson@email.com' },
        { name: 'Michael Thompson', relationship: 'Son', phone: '0423 456 789', email: 'michael.thompson@email.com' }
      ],
      authorisedRepresentatives: [{ name: 'Sarah Thompson', relationship: 'Daughter', type: 'POA Medical', documentId: 'DOC-POA-001', expiryDate: null }],
      poaMedical: { exists: true, person: 'Sarah Thompson', documentId: 'DOC-POA-001' },
      poaFinancial: { exists: true, person: 'Michael Thompson', documentId: 'DOC-POA-002' },
      familyPortalUsers: [{ email: 'sarah.thompson@email.com', name: 'Sarah Thompson', relationship: 'Daughter', permissionLevel: 'full', status: 'active' }],
      supportNetwork: [{ name: 'Father Patrick O\'Brien', relationship: 'Parish priest', phone: '0398765432', email: null, role: 'Spiritual support' }],
      gp: { name: 'Dr. Robert Smith', practice: 'Brighton Medical Centre', phone: '03 9555 7890', fax: '03 9555 7891', email: 'rsmith@brightonmed.com.au', providerNumber: '2345678A', prescriberNumber: 'PBS-2345678' },
      specialists: [{ name: 'Dr. Susan Lee', specialty: 'Geriatrician', practice: 'Melbourne Geriatric Specialists', phone: '03 9555 1234', email: 'slee@mgs.com.au', providerNumber: '3456789B' }],
      dentist: { name: 'Dr. James Wong', practice: 'Brighton Dental', phone: '03 9555 3456', providerNumber: '4567890C' },
      preferredPharmacy: 'MedFast Pharmacy Sunshine',
      alliedHealth: [
        { name: 'Mark Davis', discipline: 'Physio', practice: 'Sunshine Physiotherapy', phone: '03 9555 5678', providerNumber: '5678901D' },
        { name: 'Lisa White', discipline: 'Speech', practice: 'Speech Path Melbourne', phone: '03 9555 6789', providerNumber: '6789012E' }
      ]
    },
    leaveMovements: {
      currentLeaveStatus: 'In Facility',
      leaveHistory: [
        { type: 'Social Leave', startDate: '2025-06-15', endDate: '2025-06-16', destination: "Daughter's home", notes: 'Family lunch', returnStatus: 'Returned' },
        { type: 'Hospital Leave', startDate: '2024-11-02', endDate: '2024-11-08', destination: 'The Alfred Hospital', notes: 'Chest infection', returnStatus: 'Returned' }
      ],
      hospitalTransfers: [{ dateOut: '2024-11-02', hospital: 'The Alfred Hospital', reason: 'Chest infection requiring IV antibiotics', transferSummaryId: 'DOC-TS-001', dateReturned: '2024-11-08', clinicalChangesOnReturn: 'Completed antibiotic course, respiratory function improved' }],
      bedHold: { active: false, startDate: null, expectedReturn: null, reason: null },
      evacuationPriority: 'Priority 3'
    },
    documents: {
      residentAgreement: 'DOC-RA-001',
      consentForms: ['DOC-CON-001', 'DOC-CON-002', 'DOC-CON-003'],
      poaDocuments: ['DOC-POA-001', 'DOC-POA-002'],
      advanceCareDirectiveDoc: 'DOC-ACD-001',
      myHealthRecord: { connected: true, consentGiven: true, lastSync: '2025-07-24T08:00:00Z' },
      externalReferrals: ['DOC-REF-001'],
      transferSummaries: ['DOC-TS-001'],
      photoGallery: []
    },
    crossSectorLinks: {
      ndisParticipantId: null,
      pharmacyPatientId: 'PH-001',
      crossSectorConsent: { ndisDataSharing: false, pharmacyDataSharing: true, consentDate: '2022-06-10', consentedBy: 'Sarah Thompson' }
    },
    qualityCompliance: {
      nqipData: { pressureInjuries: 0, falls: 1, fallsWithInjury: 0, physicalRestraint: false, unplannedWeightLoss: false, polypharmacy: true, antipsychotics: false },
      incidentReports: [],
      sirsNotifications: [],
      progressNotes: [],
      clinicalHandoverNotes: [],
      careMinutesLog: [],
      monthlyCareStatement: null,
      complaintsFeedback: [],
      starRatingContribution: null
    },
    audit: {
      createdAt: '2022-06-10T09:00:00Z',
      createdBy: 'admin',
      updatedAt: '2025-07-24T10:30:00Z',
      updatedBy: 'nurse_mitchell',
      version: 47,
      isArchived: false,
      dataSensitivityLevel: 'Sensitive',
      lastAccessedAt: '2025-07-24T10:30:00Z',
      lastAccessedBy: 'nurse_mitchell'
    }
  },
  {
    id: 2,
    name: 'William James Wilson',
    photo: '👴',
    room: 'B203',
    facility: 'sunshine',
    ward: 'ward_b',
    age: 89,
    dateOfBirth: '1935-11-22',
    admissionDate: '2021-09-15',
    medicare: 'WJWI2221935',
    careLevel: 'Critical',
    mobility: 'Wheelchair',
    cognitiveStatus: 'Severe Dementia',
    primaryDiagnosis: ['Advanced Alzheimer\'s Disease', 'Parkinson\'s Disease', 'Heart Failure'],
    secondaryConditions: ['Depression', 'Urinary Incontinence', 'Pressure Ulcers'],
    allergies: ['Codeine', 'Aspirin', 'Latex'],
    dietaryRestrictions: ['Low Sodium', 'Pureed', 'Thickened Liquids'],
    weight: 72,
    height: 175,
    bloodType: 'A-',
    emergencyContact: {
      name: 'Margaret Wilson',
      relationship: 'Wife',
      phone: '0413 456 789',
      email: 'margaret.wilson@email.com',
      familyConnectEnabled: true,
      lastLogin: '2025-07-24 09:15',
      notificationPreferences: ['emergencies', 'health_updates', 'medication_changes']
    },
    primaryPhysician: 'Dr. Emily Wilson',
    careTeam: ['Nurse Maria Garcia', 'Care Worker Lisa Johnson'],
    prescriptions: [
      { 
        id: 'RX003', 
        medication: 'Levodopa 100mg', 
        dosage: '100mg three times daily', 
        doctor: 'Dr. Emily Wilson', 
        issued: '2025-06-10', 
        expires: '2025-08-10', 
        refillsLeft: 1, 
        totalRefills: 3, 
        status: 'Active', 
        instructions: 'Take 30 minutes before meals', 
        sideEffects: ['Nausea', 'Dizziness'], 
        lastTaken: '2025-07-24 07:00', 
        adherenceRate: 92, 
        automatedDispense: true 
      },
      { 
        id: 'RX004', 
        medication: 'Furosemide 40mg', 
        dosage: '40mg once daily', 
        doctor: 'Dr. Emily Wilson', 
        issued: '2025-06-05', 
        expires: '2025-07-25', 
        refillsLeft: 0, 
        totalRefills: 2, 
        status: 'Expiring Soon', 
        instructions: 'Take in morning', 
        sideEffects: ['Frequent urination', 'Dizziness'], 
        lastTaken: '2025-07-24 08:00', 
        adherenceRate: 98, 
        automatedDispense: true 
      }
    ],
    medicalHistory: [
      { 
        date: '2025-07-24', 
        event: 'Blood pressure check', 
        result: '145/90 mmHg', 
        status: 'Elevated', 
        doctor: 'Dr. Wilson', 
        automated: true,
        type: 'AI-Monitored'
      },
      { 
        date: '2025-07-20', 
        event: 'Cognitive assessment', 
        result: 'MMSE Score: 8/30', 
        status: 'Severe Decline', 
        doctor: 'Dr. Wilson', 
        automated: false
      }
    ],
    doctorNotes: [
      { 
        id: 'DN002', 
        date: '2025-07-24', 
        doctor: 'Dr. Emily Wilson', 
        type: 'Progress Note', 
        noteFormat: 'SOAP', 
        content: 'Patient showing significant cognitive decline. Parkinson\'s symptoms stable with current medication.', 
        subjective: 'Patient non-verbal, appears comfortable', 
        objective: 'Alert to voice, MMSE 8/30, BP 145/90', 
        assessment: 'Advanced dementia, stable Parkinson\'s', 
        plan: 'Continue current medications, monitor for pressure ulcers', 
        priority: 'High', 
        sharedWithFamily: true,
        followUpRequired: '2025-07-31'
      }
    ],
    carePlan: {
      goals: ['Maintain comfort', 'Prevent pressure ulcers', 'Manage Parkinson\'s symptoms', 'Family support'],
      interventions: ['Pressure ulcer prevention', 'Medication management', 'Comfort measures', 'Family counseling'],
      assessments: {
        cognitive: { score: 8, date: '2025-07-20', tool: 'MMSE' },
        falls: { risk: 'High', date: '2025-07-15', score: 8 }
      },
      reviewDate: '2025-08-01'
    },
    lastAssessment: '2025-07-24',
    nextAssessment: '2025-07-31',
    fallRisk: 'High',
    cognitiveScore: 8,
    behavioralNotes: 'Non-verbal, responds to touch and music',
    monthlyMedicationCost: 234.50,
    yearToDateCost: 1641.50,
    familyUpdates: [
      { date: '2025-07-23', type: 'health', message: 'Blood pressure elevated but stable', read: true },
      { date: '2025-07-22', type: 'care', message: 'Pressure ulcer prevention measures in place', read: false }
    ],
    advanceCareDirective: true,
    resuscitationStatus: 'DNR',
    familyMembers: [
      {
        id: 'FM002',
        name: 'Margaret Wilson',
        relationship: 'Wife',
        phone: '0413 456 789',
        email: 'margaret.wilson@email.com',
        isPrimaryContact: true,
        notificationPreferences: ['emergencies', 'health_updates', 'medication_changes'],
        lastVisit: '2025-07-24',
        nextVisit: '2025-07-26',
        accessLevel: 'Full'
      }
    ],
    demographics: {
      title: 'Mr',
      legalFirstName: 'William',
      legalMiddleNames: 'James',
      legalSurname: 'Wilson',
      preferredName: 'Bill',
      previousSurname: null,
      dateOfBirth: '1935-11-22',
      age: 89,
      gender: 'Male',
      pronouns: 'He/Him',
      sexAssignedAtBirth: 'Male',
      maritalStatus: 'Married',
      countryOfBirth: 'Australia',
      nationality: 'Australian',
      indigenousStatus: 'Neither',
      primaryLanguage: 'English',
      otherLanguages: ['Italian'],
      interpreterRequired: false,
      communicationMethods: ['Hearing aid'],
      communicationNotes: 'Non-verbal most days; responds to touch, music, and familiar voices',
      religion: 'Anglican',
      culturalBackground: 'Anglo-Australian',
      veteranStatus: 'DVA Gold Card',
      dvaFileNumber: 'DVA-QX12345',
      profilePhoto: null
    },
    governmentIds: {
      medicareNumber: '3456 78901 2',
      medicareIrn: 1,
      medicareExpiry: '2026-11-30',
      ihi: '8003 6100 0047 6543',
      pensionCardNumber: 'PEN-456789',
      pensionType: 'DVA Pension',
      centrelinkCrn: '406 789 012B',
      ndisNumber: null,
      myAgedCareClientId: 'MAC-2021-091578',
      tfnEncrypted: '[ENCRYPTED]',
      tfnMasked: '***-***-892',
      privateHealthInsurer: null,
      privateHealthNumber: null
    },
    contactAddress: {
      homeAddress: { street: '78 Victoria Parade', suburb: 'Fitzroy', state: 'VIC', postcode: '3065', country: 'Australia' },
      mailingAddress: null,
      phoneMobile: null,
      phoneHome: '+61394561234',
      email: null
    },
    admission: {
      admissionDate: '2021-09-15',
      admissionType: 'Permanent',
      admissionSource: 'Home',
      referringOrganisation: 'St Vincent\'s Hospital Melbourne',
      facilityId: 'sunshine',
      wingUnit: 'ward_b',
      roomNumber: 'B203',
      bedNumber: '1',
      roomType: 'Dementia-specific',
      residentStatus: 'Active',
      expectedLengthOfStay: 'Permanent',
      dischargeDate: null,
      dischargeReason: null,
      dateOfDeath: null
    },
    funding: {
      anaccClass: 12,
      anaccClassificationDate: '2025-04-10',
      anaccReassessmentDue: '2025-10-10',
      anaccAssessorName: 'Dr. Peter Holden',
      baseCareTariff: 76.84,
      anaccSubsidyDaily: 312.80,
      entryAdjustmentPayment: null,
      supplements: [
        { type: 'DVA', dailyRate: 15.00, startDate: '2021-09-15', endDate: null },
        { type: 'Dementia/Cognition', dailyRate: 18.75, startDate: '2022-01-01', endDate: null }
      ],
      meansTestingStatus: 'Low means (supported)',
      accommodationPaymentType: 'DAP',
      radAmountAgreed: null,
      radBalance: null,
      dapDailyRate: 58.90,
      accommodationAgreementDate: '2021-09-15',
      meansAssessmentEffectiveDate: '2021-10-01',
      basicDailyFee: 61.96,
      incomeTestedCareFee: 0,
      extraServicesAgreement: { enabled: false, details: null }
    },
    clinical: {
      primaryDiagnosis: { code: 'G30.8', displayText: "Advanced Alzheimer's Disease" },
      secondaryDiagnoses: [
        { code: 'G20', displayText: "Parkinson's Disease", onsetDate: '2015-06-01' },
        { code: 'I50.9', displayText: 'Heart Failure, unspecified', onsetDate: '2019-03-15' }
      ],
      allergies: [
        { allergen: 'Codeine', type: 'drug', reaction: 'Respiratory depression', severity: 'severe', dateIdentified: '2000-03-01' },
        { allergen: 'Aspirin', type: 'drug', reaction: 'GI bleeding', severity: 'severe', dateIdentified: '2005-08-15' },
        { allergen: 'Latex', type: 'environmental', reaction: 'Contact dermatitis', severity: 'mild', dateIdentified: '2010-01-20' }
      ],
      clinicalAlerts: ['Falls risk', 'Allergy', 'DNACPR', 'Behaviour alert'],
      bloodType: 'A-',
      heightCm: 175,
      weightKg: 72,
      weightHistory: [
        { date: '2025-07-01', weightKg: 72, measuredBy: 'Nurse Garcia' },
        { date: '2025-06-01', weightKg: 73.1, measuredBy: 'Nurse Garcia' },
        { date: '2025-05-01', weightKg: 73.8, measuredBy: 'Nurse Garcia' },
        { date: '2025-01-15', weightKg: 76.5, measuredBy: 'Nurse Garcia' }
      ],
      bmi: 23.5,
      cognitiveStatus: 'Severe impairment',
      dementiaDiagnosis: { diagnosed: true, type: "Alzheimer's", dateDiagnosed: '2017-08-10' },
      mobilityStatus: 'Wheelchair',
      fallsRiskRating: { level: 'High', score: 8 },
      pressureInjuryStatus: { present: true, stage: 'Stage 2', locations: ['Sacrum'], woundPlanLink: 'WP-002' },
      continenceStatus: { bladder: 'Incontinent', bowel: 'Frequently incontinent', aids: ['Pad', 'Catheter (IDC)'] },
      painLevel: { currentScore: null, toolUsed: 'Abbey Pain Scale', lastAssessed: '2025-07-24T09:00:00Z' },
      infectionStatus: { currentInfections: [], isolationRequired: false, mrsa: false, vre: false, cdiff: false },
      immunisations: [
        { vaccine: 'COVID-19 (Pfizer)', dateGiven: '2025-03-10', batchNumber: 'FT2345', administeredBy: 'Nurse Garcia', nextDue: '2025-09-10' },
        { vaccine: 'Influenza', dateGiven: '2025-04-05', batchNumber: 'FL6789', administeredBy: 'Nurse Garcia', nextDue: '2026-04-05' }
      ],
      advanceCareDirective: { exists: true, documentId: 'DOC-ACD-002', reviewDate: '2025-09-15', summary: 'Comfort care only. No hospital transfers unless necessary for comfort. Wife Margaret to be primary decision maker.' },
      resuscitationStatus: 'Comfort measures only',
      goalsOfCare: 'Maximise comfort and dignity. Maintain connection with wife Margaret through daily visits. Music therapy for engagement. No aggressive treatments.',
      restrictivePractices: [{ type: 'Environmental', authorisation: 'Secured dementia unit', authorisedBy: 'Dr. Emily Wilson', startDate: '2022-01-15', reviewDate: '2025-10-15', behaviourSupportPlanId: 'BSP-002' }]
    },
    dietary: {
      dietType: 'Pureed',
      iddsiFoodLevel: 'Level 4 (Pureed)',
      iddsiFluidLevel: 'Level 3 (Moderately Thick)',
      foodAllergies: [],
      dietaryPreferences: ['Low Sodium'],
      portionSize: 'Small',
      fluidRestriction: { restricted: true, dailyLimitMl: 1500 },
      nutritionalRiskScore: { score: 8, tool: 'MNA', dateAssessed: '2025-07-01', riskLevel: 'High' },
      enteralFeeding: { feedType: null, rateMlHr: null, regime: null, startDate: null },
      mealtimeAssistance: 'Full assist'
    },
    personalLife: {
      lifeHistory: 'William (Bill) served in the Australian Army and later worked as a mechanical engineer. He was a keen golfer and a dedicated member of the Fitzroy RSL. He and Margaret have been married for over 60 years. He was known for his dry sense of humour.',
      occupationHistory: 'Australian Army (1955-1965), Mechanical Engineer at BHP (1965-2000)',
      hobbiesInterests: ['Golf', 'RSL', 'Woodworking', 'Watching cricket'],
      musicPreferences: 'Big band music, military marches, Slim Dusty',
      dailyRoutinePreferences: { wakeTime: '06:30', showerPreference: 'morning', mealPreferences: null, bedtime: '20:00' },
      socialPreferences: 'Previously very social; now responds to 1:1 interaction',
      petsAnimals: 'Had a golden retriever called Max',
      importantDates: [{ date: '1960-02-14', description: 'Wedding anniversary with Margaret' }],
      comfortItems: 'Army service medal, photo of Margaret, favourite blanket'
    },
    supportNetwork: {
      primaryContact: { name: 'Margaret Wilson', relationship: 'Wife', phone: '0413 456 789', phoneAlt: null, email: 'margaret.wilson@email.com', address: { street: '78 Victoria Parade', suburb: 'Fitzroy', state: 'VIC', postcode: '3065', country: 'Australia' } },
      emergencyContacts: [
        { name: 'Margaret Wilson', relationship: 'Wife', phone: '0413 456 789', email: 'margaret.wilson@email.com' },
        { name: 'Peter Wilson', relationship: 'Son', phone: '0424 567 890', email: 'peter.wilson@email.com' }
      ],
      authorisedRepresentatives: [{ name: 'Margaret Wilson', relationship: 'Wife', type: 'POA Medical', documentId: 'DOC-POA-003', expiryDate: null }],
      poaMedical: { exists: true, person: 'Margaret Wilson', documentId: 'DOC-POA-003' },
      poaFinancial: { exists: true, person: 'Peter Wilson', documentId: 'DOC-POA-004' },
      familyPortalUsers: [{ email: 'margaret.wilson@email.com', name: 'Margaret Wilson', relationship: 'Wife', permissionLevel: 'full', status: 'active' }],
      supportNetwork: [],
      gp: { name: 'Dr. Emily Wilson', practice: 'Fitzroy Medical Group', phone: '03 9555 2345', fax: '03 9555 2346', email: 'ewilson@fitzroymed.com.au', providerNumber: '3456789C', prescriberNumber: 'PBS-3456789' },
      specialists: [
        { name: 'Dr. Alan Park', specialty: 'Neurologist', practice: 'Melbourne Neurology', phone: '03 9555 8901', email: 'apark@melbneurol.com.au', providerNumber: '7890123F' },
        { name: 'Dr. Rachel Green', specialty: 'Cardiologist', practice: 'Heart Care Melbourne', phone: '03 9555 9012', email: 'rgreen@heartcare.com.au', providerNumber: '8901234G' }
      ],
      dentist: null,
      preferredPharmacy: 'MedFast Pharmacy Sunshine',
      alliedHealth: [{ name: 'Emma Clarke', discipline: 'Speech', practice: 'Dysphagia Services Melbourne', phone: '03 9555 7890', providerNumber: '9012345H' }]
    },
    leaveMovements: {
      currentLeaveStatus: 'In Facility',
      leaveHistory: [],
      hospitalTransfers: [{ dateOut: '2024-08-15', hospital: 'St Vincent\'s Hospital', reason: 'Aspiration pneumonia', transferSummaryId: 'DOC-TS-002', dateReturned: '2024-08-22', clinicalChangesOnReturn: 'Fluid consistency downgraded to Level 3' }],
      bedHold: { active: false, startDate: null, expectedReturn: null, reason: null },
      evacuationPriority: 'Priority 2'
    },
    documents: {
      residentAgreement: 'DOC-RA-002',
      consentForms: ['DOC-CON-004', 'DOC-CON-005'],
      poaDocuments: ['DOC-POA-003', 'DOC-POA-004'],
      advanceCareDirectiveDoc: 'DOC-ACD-002',
      myHealthRecord: { connected: true, consentGiven: true, lastSync: '2025-07-24T09:00:00Z' },
      externalReferrals: [],
      transferSummaries: ['DOC-TS-002'],
      photoGallery: []
    },
    crossSectorLinks: {
      ndisParticipantId: null,
      pharmacyPatientId: 'PH-002',
      crossSectorConsent: { ndisDataSharing: false, pharmacyDataSharing: true, consentDate: '2021-09-15', consentedBy: 'Margaret Wilson' }
    },
    qualityCompliance: {
      nqipData: { pressureInjuries: 1, falls: 0, fallsWithInjury: 0, physicalRestraint: false, unplannedWeightLoss: true, polypharmacy: false, antipsychotics: false },
      incidentReports: [],
      sirsNotifications: [],
      progressNotes: [],
      clinicalHandoverNotes: [],
      careMinutesLog: [],
      monthlyCareStatement: null,
      complaintsFeedback: [],
      starRatingContribution: null
    },
    audit: {
      createdAt: '2021-09-15T09:00:00Z',
      createdBy: 'admin',
      updatedAt: '2025-07-24T09:30:00Z',
      updatedBy: 'nurse_garcia',
      version: 82,
      isArchived: false,
      dataSensitivityLevel: 'Highly Sensitive',
      lastAccessedAt: '2025-07-24T09:30:00Z',
      lastAccessedBy: 'nurse_garcia'
    }
  },
  {
    id: 3,
    name: 'Dorothy Elizabeth Brown',
    photo: '👵',
    room: 'C105',
    facility: 'bayview',
    ward: 'ward_c',
    age: 91,
    dateOfBirth: '1933-08-14',
    admissionDate: '2020-12-03',
    medicare: 'DEBR8141933',
    careLevel: 'Medium',
    mobility: 'Walker for all distances',
    cognitiveStatus: 'Normal',
    primaryDiagnosis: ['Osteoporosis', 'Hypertension', 'Osteoarthritis'],
    secondaryConditions: ['Mild Depression', 'Constipation', 'Hearing Loss'],
    allergies: ['Sulfa Drugs', 'Iodine'],
    dietaryRestrictions: ['Low Sodium', 'High Calcium'],
    weight: 58,
    height: 155,
    bloodType: 'B+',
    emergencyContact: {
      name: 'Robert Brown',
      relationship: 'Son',
      phone: '0414 567 890',
      email: 'robert.brown@email.com',
      familyConnectEnabled: true,
      lastLogin: '2025-07-23 16:45',
      notificationPreferences: ['health_updates', 'activities', 'medication_changes']
    },
    primaryPhysician: 'Dr. Michael Chen',
    careTeam: ['Nurse Sarah Mitchell', 'Care Worker Tom Brown'],
    prescriptions: [
      { 
        id: 'RX005', 
        medication: 'Alendronate 70mg', 
        dosage: '70mg once weekly', 
        doctor: 'Dr. Michael Chen', 
        issued: '2025-06-15', 
        expires: '2025-09-15', 
        refillsLeft: 2, 
        totalRefills: 4, 
        status: 'Active', 
        instructions: 'Take on empty stomach with water', 
        sideEffects: ['Stomach upset', 'Esophageal irritation'], 
        lastTaken: '2025-07-21 08:00', 
        adherenceRate: 100, 
        automatedDispense: true 
      },
      { 
        id: 'RX006', 
        medication: 'Lisinopril 5mg', 
        dosage: '5mg once daily', 
        doctor: 'Dr. Michael Chen', 
        issued: '2025-06-20', 
        expires: '2025-07-20', 
        refillsLeft: 0, 
        totalRefills: 2, 
        status: 'Expired', 
        instructions: 'Take in morning', 
        sideEffects: ['Dry cough', 'Dizziness'], 
        lastTaken: '2025-07-20 07:00', 
        adherenceRate: 98, 
        automatedDispense: true 
      }
    ],
    medicalHistory: [
      { 
        date: '2025-07-24', 
        event: 'Blood pressure check', 
        result: '130/80 mmHg', 
        status: 'Normal', 
        doctor: 'Dr. Chen', 
        automated: true,
        type: 'AI-Monitored'
      },
      { 
        date: '2025-07-20', 
        event: 'Bone density scan', 
        result: 'T-score: -2.5', 
        status: 'Osteoporosis', 
        doctor: 'Dr. Chen', 
        automated: false
      }
    ],
    doctorNotes: [
      { 
        id: 'DN003', 
        date: '2025-07-24', 
        doctor: 'Dr. Michael Chen', 
        type: 'Progress Note', 
        noteFormat: 'SOAP', 
        content: 'Patient doing well. Blood pressure controlled, osteoporosis stable with current treatment.', 
        subjective: 'Patient reports feeling well, active in activities', 
        objective: 'Alert, cooperative, BP 130/80', 
        assessment: 'Hypertension controlled, osteoporosis stable', 
        plan: 'Continue current medications, monitor bone density annually', 
        priority: 'Normal', 
        sharedWithFamily: true,
        followUpRequired: '2025-08-24'
      }
    ],
    carePlan: {
      goals: ['Maintain bone health', 'Control blood pressure', 'Prevent falls', 'Maintain independence'],
      interventions: ['Calcium and vitamin D supplements', 'Blood pressure monitoring', 'Fall prevention exercises', 'Social activities'],
      assessments: {
        cognitive: { score: 28, date: '2025-07-15', tool: 'MMSE' },
        falls: { risk: 'Medium', date: '2025-07-10', score: 3 }
      },
      reviewDate: '2025-08-15'
    },
    lastAssessment: '2025-07-24',
    nextAssessment: '2025-08-24',
    fallRisk: 'Medium',
    cognitiveScore: 28,
    behavioralNotes: 'Very social, enjoys gardening and crafts',
    monthlyMedicationCost: 89.30,
    yearToDateCost: 625.10,
    familyUpdates: [
      { date: '2025-07-23', type: 'activity', message: 'Participated in gardening group', read: true },
      { date: '2025-07-22', type: 'health', message: 'Blood pressure normal', read: false }
    ],
    advanceCareDirective: true,
    resuscitationStatus: 'DNR',
    familyMembers: [
      {
        id: 'FM003',
        name: 'Robert Brown',
        relationship: 'Son',
        phone: '0414 567 890',
        email: 'robert.brown@email.com',
        isPrimaryContact: true,
        notificationPreferences: ['health_updates', 'activities', 'medication_changes'],
        lastVisit: '2025-07-23',
        nextVisit: '2025-07-30',
        accessLevel: 'Full'
      }
    ],
    demographics: {
      title: 'Mrs',
      legalFirstName: 'Dorothy',
      legalMiddleNames: 'Elizabeth',
      legalSurname: 'Brown',
      preferredName: 'Dot',
      previousSurname: 'Stewart',
      dateOfBirth: '1933-08-14',
      age: 91,
      gender: 'Female',
      pronouns: 'She/Her',
      sexAssignedAtBirth: 'Female',
      maritalStatus: 'Widowed',
      countryOfBirth: 'England',
      nationality: 'Australian',
      indigenousStatus: 'Neither',
      primaryLanguage: 'English',
      otherLanguages: [],
      interpreterRequired: false,
      communicationMethods: ['Hearing aid', 'Glasses'],
      communicationNotes: 'Hard of hearing in left ear; speak clearly and face her',
      religion: 'Church of England',
      culturalBackground: 'British-Australian',
      veteranStatus: 'Not a veteran',
      dvaFileNumber: null,
      profilePhoto: null
    },
    governmentIds: {
      medicareNumber: '4567 89012 3',
      medicareIrn: 1,
      medicareExpiry: '2027-08-31',
      ihi: '8003 6100 0058 7654',
      pensionCardNumber: 'PEN-567890',
      pensionType: 'Age Pension',
      centrelinkCrn: '507 890 123C',
      ndisNumber: null,
      myAgedCareClientId: 'MAC-2020-120356',
      tfnEncrypted: '[ENCRYPTED]',
      tfnMasked: '***-***-453',
      privateHealthInsurer: 'BUPA',
      privateHealthNumber: 'BPA-5432109'
    },
    contactAddress: {
      homeAddress: { street: '23 Beach Road', suburb: 'Sandringham', state: 'VIC', postcode: '3191', country: 'Australia' },
      mailingAddress: null,
      phoneMobile: null,
      phoneHome: '+61395553456',
      email: 'dot.brown@email.com'
    },
    admission: {
      admissionDate: '2020-12-03',
      admissionType: 'Permanent',
      admissionSource: 'Home',
      referringOrganisation: null,
      facilityId: 'bayview',
      wingUnit: 'ward_c',
      roomNumber: 'C105',
      bedNumber: '1',
      roomType: 'Single',
      residentStatus: 'Active',
      expectedLengthOfStay: 'Permanent',
      dischargeDate: null,
      dischargeReason: null,
      dateOfDeath: null
    },
    funding: {
      anaccClass: 4,
      anaccClassificationDate: '2025-02-20',
      anaccReassessmentDue: '2026-02-20',
      anaccAssessorName: 'Dr. Anne Richards',
      baseCareTariff: 76.84,
      anaccSubsidyDaily: 125.60,
      entryAdjustmentPayment: null,
      supplements: [],
      meansTestingStatus: 'Partially supported',
      accommodationPaymentType: 'RAD',
      radAmountAgreed: 450000,
      radBalance: 450000,
      dapDailyRate: null,
      accommodationAgreementDate: '2020-12-03',
      meansAssessmentEffectiveDate: '2021-01-01',
      basicDailyFee: 61.96,
      incomeTestedCareFee: 15.80,
      extraServicesAgreement: { enabled: true, details: 'Premium room with garden view, Foxtel, weekly hairdressing' }
    },
    clinical: {
      primaryDiagnosis: { code: 'M81.0', displayText: 'Age-related Osteoporosis without pathological fracture' },
      secondaryDiagnoses: [
        { code: 'I10', displayText: 'Essential Hypertension', onsetDate: '2005-03-01' },
        { code: 'M15.0', displayText: 'Primary Generalised Osteoarthritis', onsetDate: '2008-06-15' }
      ],
      allergies: [
        { allergen: 'Sulfa Drugs', type: 'drug', reaction: 'Skin rash', severity: 'moderate', dateIdentified: '1975-09-01' },
        { allergen: 'Iodine', type: 'drug', reaction: 'Hives', severity: 'mild', dateIdentified: '1990-05-10' }
      ],
      clinicalAlerts: ['Falls risk', 'Allergy'],
      bloodType: 'B+',
      heightCm: 155,
      weightKg: 58,
      weightHistory: [
        { date: '2025-07-01', weightKg: 58, measuredBy: 'Nurse Mitchell' },
        { date: '2025-06-01', weightKg: 58.2, measuredBy: 'Nurse Mitchell' },
        { date: '2025-05-01', weightKg: 58.5, measuredBy: 'Nurse Mitchell' }
      ],
      bmi: 24.1,
      cognitiveStatus: 'Normal',
      dementiaDiagnosis: { diagnosed: false, type: null, dateDiagnosed: null },
      mobilityStatus: 'Supervised',
      fallsRiskRating: { level: 'Medium', score: 3 },
      pressureInjuryStatus: { present: false, stage: null, locations: [], woundPlanLink: null },
      continenceStatus: { bladder: 'Continent', bowel: 'Continent', aids: [] },
      painLevel: { currentScore: 2, toolUsed: 'Numeric Rating Scale', lastAssessed: '2025-07-24T08:00:00Z' },
      infectionStatus: { currentInfections: [], isolationRequired: false, mrsa: false, vre: false, cdiff: false },
      immunisations: [
        { vaccine: 'COVID-19 (Pfizer)', dateGiven: '2025-03-20', batchNumber: 'FT3456', administeredBy: 'Nurse Mitchell', nextDue: '2025-09-20' },
        { vaccine: 'Influenza', dateGiven: '2025-04-10', batchNumber: 'FL7890', administeredBy: 'Nurse Mitchell', nextDue: '2026-04-10' },
        { vaccine: 'Shingles (Shingrix)', dateGiven: '2024-02-15', batchNumber: 'SH3456', administeredBy: 'Nurse Mitchell', nextDue: null }
      ],
      advanceCareDirective: { exists: true, documentId: 'DOC-ACD-003', reviewDate: '2025-12-03', summary: 'Would like reasonable treatment but no ICU admission. Son Robert to be consulted.' },
      resuscitationStatus: 'Not for resuscitation (NFR)',
      goalsOfCare: 'Maintain independence and mobility. Continue social activities. Manage pain effectively.',
      restrictivePractices: []
    },
    dietary: {
      dietType: 'Regular',
      iddsiFoodLevel: 'Level 7 (Regular)',
      iddsiFluidLevel: 'Level 0 (Thin)',
      foodAllergies: [],
      dietaryPreferences: ['Low Sodium', 'High Calcium'],
      portionSize: 'Small',
      fluidRestriction: { restricted: false, dailyLimitMl: null },
      nutritionalRiskScore: { score: 2, tool: 'MNA', dateAssessed: '2025-07-01', riskLevel: 'Low' },
      enteralFeeding: { feedType: null, rateMlHr: null, regime: null, startDate: null },
      mealtimeAssistance: 'Independent'
    },
    personalLife: {
      lifeHistory: 'Dorothy emigrated from England to Australia in 1955. She worked as a librarian for 35 years and is an avid reader. She married George Brown in 1958 and they had two children. She loves gardening and was president of her local garden club for 15 years.',
      occupationHistory: 'Librarian, Sandringham Library (1960-1995)',
      hobbiesInterests: ['Gardening', 'Crafts', 'Reading', 'Knitting', 'Bird watching'],
      musicPreferences: 'Classical music, especially Beethoven and Mozart. Enjoys BBC radio plays.',
      dailyRoutinePreferences: { wakeTime: '06:00', showerPreference: 'morning', mealPreferences: 'Tea with milk, no sugar. Enjoys scones.', bedtime: '21:30' },
      socialPreferences: 'Very social; enjoys group activities and leading craft sessions',
      petsAnimals: 'Had budgies; enjoys watching birds from her window',
      importantDates: [{ date: '1958-06-21', description: 'Wedding anniversary with George' }, { date: '1933-08-14', description: 'Birthday' }],
      comfortItems: 'Reading glasses, collection of books, photos of late husband George, garden view from room'
    },
    supportNetwork: {
      primaryContact: { name: 'Robert Brown', relationship: 'Son', phone: '0414 567 890', phoneAlt: null, email: 'robert.brown@email.com', address: null },
      emergencyContacts: [
        { name: 'Robert Brown', relationship: 'Son', phone: '0414 567 890', email: 'robert.brown@email.com' },
        { name: 'Susan Brown', relationship: 'Daughter', phone: '0425 678 901', email: 'susan.brown@email.com' }
      ],
      authorisedRepresentatives: [{ name: 'Robert Brown', relationship: 'Son', type: 'POA Medical', documentId: 'DOC-POA-005', expiryDate: null }],
      poaMedical: { exists: true, person: 'Robert Brown', documentId: 'DOC-POA-005' },
      poaFinancial: { exists: true, person: 'Robert Brown', documentId: 'DOC-POA-006' },
      familyPortalUsers: [
        { email: 'robert.brown@email.com', name: 'Robert Brown', relationship: 'Son', permissionLevel: 'full', status: 'active' },
        { email: 'susan.brown@email.com', name: 'Susan Brown', relationship: 'Daughter', permissionLevel: 'view', status: 'active' }
      ],
      supportNetwork: [{ name: 'Betty Masters', relationship: 'Friend', phone: '0436 789 012', email: null, role: 'Social visitor' }],
      gp: { name: 'Dr. Michael Chen', practice: 'Bayview Medical Centre', phone: '03 9555 4567', fax: '03 9555 4568', email: 'mchen@bayviewmed.com.au', providerNumber: '4567890D', prescriberNumber: 'PBS-4567890' },
      specialists: [{ name: 'Dr. Karen White', specialty: 'Rheumatologist', practice: 'Melbourne Rheumatology', phone: '03 9555 3456', email: 'kwhite@melbrheum.com.au', providerNumber: '5678901E' }],
      dentist: { name: 'Dr. Tony Nguyen', practice: 'Sandringham Dental', phone: '03 9555 6789', providerNumber: '6789012F' },
      preferredPharmacy: 'MedFast Pharmacy Bayview',
      alliedHealth: [{ name: 'Sarah Palmer', discipline: 'Physio', practice: 'Bayview Physiotherapy', phone: '03 9555 7890', providerNumber: '7890123G' }]
    },
    leaveMovements: {
      currentLeaveStatus: 'In Facility',
      leaveHistory: [{ type: 'Social Leave', startDate: '2025-05-10', endDate: '2025-05-11', destination: "Son Robert's home", notes: 'Mother\'s Day lunch', returnStatus: 'Returned' }],
      hospitalTransfers: [],
      bedHold: { active: false, startDate: null, expectedReturn: null, reason: null },
      evacuationPriority: 'Priority 3'
    },
    documents: {
      residentAgreement: 'DOC-RA-003',
      consentForms: ['DOC-CON-006', 'DOC-CON-007'],
      poaDocuments: ['DOC-POA-005', 'DOC-POA-006'],
      advanceCareDirectiveDoc: 'DOC-ACD-003',
      myHealthRecord: { connected: true, consentGiven: true, lastSync: '2025-07-23T14:00:00Z' },
      externalReferrals: [],
      transferSummaries: [],
      photoGallery: []
    },
    crossSectorLinks: { ndisParticipantId: null, pharmacyPatientId: 'PH-003', crossSectorConsent: { ndisDataSharing: false, pharmacyDataSharing: true, consentDate: '2020-12-03', consentedBy: 'Robert Brown' } },
    qualityCompliance: { nqipData: { pressureInjuries: 0, falls: 0, fallsWithInjury: 0, physicalRestraint: false, unplannedWeightLoss: false, polypharmacy: false, antipsychotics: false }, incidentReports: [], sirsNotifications: [], progressNotes: [], clinicalHandoverNotes: [], careMinutesLog: [], monthlyCareStatement: null, complaintsFeedback: [], starRatingContribution: null },
    audit: { createdAt: '2020-12-03T09:00:00Z', createdBy: 'admin', updatedAt: '2025-07-24T08:00:00Z', updatedBy: 'nurse_mitchell', version: 95, isArchived: false, dataSensitivityLevel: 'Standard', lastAccessedAt: '2025-07-24T08:00:00Z', lastAccessedBy: 'nurse_mitchell' }
  },
  {
    id: 4,
    name: 'Harold Thomas Davis',
    photo: '👴',
    room: 'D207',
    facility: 'bayview',
    ward: 'ward_d',
    age: 87,
    dateOfBirth: '1937-05-08',
    admissionDate: '2023-01-20',
    medicare: 'HTDA8051937',
    careLevel: 'Low',
    mobility: 'Independent with cane',
    cognitiveStatus: 'Normal',
    primaryDiagnosis: ['Type 2 Diabetes', 'Hypertension', 'Mild COPD'],
    secondaryConditions: ['Mild Depression', 'Sleep Apnea', 'Gout'],
    allergies: ['Penicillin', 'Aspirin'],
    dietaryRestrictions: ['Diabetic Diet', 'Low Purine', 'Low Sodium'],
    weight: 82,
    height: 178,
    bloodType: 'O-',
    emergencyContact: {
      name: 'Jennifer Davis',
      relationship: 'Daughter',
      phone: '0415 678 901',
      email: 'jennifer.davis@email.com',
      familyConnectEnabled: true,
      lastLogin: '2025-07-24 11:20',
      notificationPreferences: ['medication_changes', 'health_updates', 'activities']
    },
    primaryPhysician: 'Dr. Maria Garcia',
    careTeam: ['Nurse Maria Garcia', 'Care Worker Lisa Johnson'],
    prescriptions: [
      { 
        id: 'RX007', 
        medication: 'Metformin 1000mg', 
        dosage: '1000mg twice daily', 
        doctor: 'Dr. Maria Garcia', 
        issued: '2025-06-28', 
        expires: '2025-07-28', 
        refillsLeft: 2, 
        totalRefills: 5, 
        status: 'Active', 
        instructions: 'Take with meals', 
        sideEffects: ['Nausea', 'Stomach upset'], 
        lastTaken: '2025-07-24 08:00', 
        adherenceRate: 97, 
        automatedDispense: true 
      },
      { 
        id: 'RX008', 
        medication: 'Lisinopril 10mg', 
        dosage: '10mg once daily', 
        doctor: 'Dr. Maria Garcia', 
        issued: '2025-06-25', 
        expires: '2025-07-25', 
        refillsLeft: 0, 
        totalRefills: 3, 
        status: 'Expiring Soon', 
        instructions: 'Take in morning', 
        sideEffects: ['Dry cough', 'Dizziness'], 
        lastTaken: '2025-07-24 07:00', 
        adherenceRate: 98, 
        automatedDispense: true 
      }
    ],
    medicalHistory: [
      { 
        date: '2025-07-24', 
        event: 'Blood glucose check', 
        result: '7.2 mmol/L', 
        status: 'Elevated', 
        doctor: 'Dr. Garcia', 
        automated: true,
        type: 'AI-Monitored'
      },
      { 
        date: '2025-07-20', 
        event: 'Blood pressure check', 
        result: '140/85 mmHg', 
        status: 'Elevated', 
        doctor: 'Dr. Garcia', 
        automated: true,
        type: 'AI-Monitored'
      }
    ],
    doctorNotes: [
      { 
        id: 'DN004', 
        date: '2025-07-24', 
        doctor: 'Dr. Maria Garcia', 
        type: 'Progress Note', 
        noteFormat: 'SOAP', 
        content: 'Patient\'s diabetes and blood pressure slightly elevated. Needs medication adjustment.', 
        subjective: 'Patient reports feeling tired, increased thirst', 
        objective: 'Alert, cooperative, BP 140/85, glucose 7.2', 
        assessment: 'Diabetes and hypertension not well controlled', 
        plan: 'Adjust medication dosages, increase monitoring', 
        priority: 'High', 
        sharedWithFamily: true,
        followUpRequired: '2025-07-31'
      }
    ],
    carePlan: {
      goals: ['Control diabetes', 'Manage blood pressure', 'Maintain independence', 'Prevent complications'],
      interventions: ['Blood glucose monitoring', 'Blood pressure monitoring', 'Exercise program', 'Diet management'],
      assessments: {
        cognitive: { score: 29, date: '2025-07-15', tool: 'MMSE' },
        falls: { risk: 'Low', date: '2025-07-10', score: 2 }
      },
      reviewDate: '2025-08-01'
    },
    lastAssessment: '2025-07-24',
    nextAssessment: '2025-07-31',
    fallRisk: 'Low',
    cognitiveScore: 29,
    behavioralNotes: 'Very independent, enjoys reading and chess',
    monthlyMedicationCost: 123.40,
    yearToDateCost: 863.80,
    familyUpdates: [
      { date: '2025-07-23', type: 'health', message: 'Blood sugar elevated, medication adjustment needed', read: true },
      { date: '2025-07-22', type: 'activity', message: 'Won chess tournament', read: false }
    ],
    advanceCareDirective: true,
    resuscitationStatus: 'DNR',
    familyMembers: [
      {
        id: 'FM004',
        name: 'Jennifer Davis',
        relationship: 'Daughter',
        phone: '0415 678 901',
        email: 'jennifer.davis@email.com',
        isPrimaryContact: true,
        notificationPreferences: ['medication_changes', 'health_updates', 'activities'],
        lastVisit: '2025-07-24',
        nextVisit: '2025-07-28',
        accessLevel: 'Full'
      }
    ],
    demographics: {
      title: 'Mr',
      legalFirstName: 'Harold',
      legalMiddleNames: 'Thomas',
      legalSurname: 'Davis',
      preferredName: 'Harry',
      previousSurname: null,
      dateOfBirth: '1937-05-08',
      age: 87,
      gender: 'Male',
      pronouns: 'He/Him',
      sexAssignedAtBirth: 'Male',
      maritalStatus: 'Divorced',
      countryOfBirth: 'Australia',
      nationality: 'Australian',
      indigenousStatus: 'Aboriginal',
      primaryLanguage: 'English',
      otherLanguages: [],
      interpreterRequired: false,
      communicationMethods: ['Glasses'],
      communicationNotes: 'Clear communicator, prefers direct and honest conversations',
      religion: null,
      culturalBackground: 'Aboriginal Australian — Wurundjeri people',
      veteranStatus: 'Not a veteran',
      dvaFileNumber: null,
      profilePhoto: null
    },
    governmentIds: {
      medicareNumber: '5678 90123 4',
      medicareIrn: 1,
      medicareExpiry: '2027-05-31',
      ihi: '8003 6100 0069 8765',
      pensionCardNumber: 'PEN-678901',
      pensionType: 'Age Pension',
      centrelinkCrn: '608 901 234D',
      ndisNumber: null,
      myAgedCareClientId: 'MAC-2023-012089',
      tfnEncrypted: '[ENCRYPTED]',
      tfnMasked: '***-***-564',
      privateHealthInsurer: null,
      privateHealthNumber: null
    },
    contactAddress: {
      homeAddress: { street: '156 Bay Street', suburb: 'Port Melbourne', state: 'VIC', postcode: '3207', country: 'Australia' },
      mailingAddress: null,
      phoneMobile: '+61415678901',
      phoneHome: null,
      email: 'harry.davis@email.com'
    },
    admission: {
      admissionDate: '2023-01-20',
      admissionType: 'Permanent',
      admissionSource: 'Home',
      referringOrganisation: null,
      facilityId: 'bayview',
      wingUnit: 'ward_d',
      roomNumber: 'D207',
      bedNumber: '1',
      roomType: 'Single',
      residentStatus: 'Active',
      expectedLengthOfStay: 'Permanent',
      dischargeDate: null,
      dischargeReason: null,
      dateOfDeath: null
    },
    funding: {
      anaccClass: 3,
      anaccClassificationDate: '2025-03-01',
      anaccReassessmentDue: '2026-03-01',
      anaccAssessorName: 'Dr. Peter Holden',
      baseCareTariff: 76.84,
      anaccSubsidyDaily: 105.20,
      entryAdjustmentPayment: null,
      supplements: [{ type: 'ATSI', dailyRate: 10.25, startDate: '2023-01-20', endDate: null }],
      meansTestingStatus: 'Low means (supported)',
      accommodationPaymentType: 'DAP',
      radAmountAgreed: null,
      radBalance: null,
      dapDailyRate: 35.50,
      accommodationAgreementDate: '2023-01-20',
      meansAssessmentEffectiveDate: '2023-02-01',
      basicDailyFee: 61.96,
      incomeTestedCareFee: 0,
      extraServicesAgreement: { enabled: false, details: null }
    },
    clinical: {
      primaryDiagnosis: { code: 'E11.9', displayText: 'Type 2 Diabetes Mellitus' },
      secondaryDiagnoses: [
        { code: 'I10', displayText: 'Essential Hypertension', onsetDate: '2010-01-15' },
        { code: 'J44.9', displayText: 'COPD, unspecified', onsetDate: '2018-09-01' }
      ],
      allergies: [
        { allergen: 'Penicillin', type: 'drug', reaction: 'Rash', severity: 'moderate', dateIdentified: '1970-06-01' },
        { allergen: 'Aspirin', type: 'drug', reaction: 'GI upset', severity: 'mild', dateIdentified: '2005-03-15' }
      ],
      clinicalAlerts: ['Allergy'],
      bloodType: 'O-',
      heightCm: 178,
      weightKg: 82,
      weightHistory: [
        { date: '2025-07-01', weightKg: 82, measuredBy: 'Nurse Garcia' },
        { date: '2025-06-01', weightKg: 81.5, measuredBy: 'Nurse Garcia' },
        { date: '2025-05-01', weightKg: 81.0, measuredBy: 'Nurse Garcia' }
      ],
      bmi: 25.9,
      cognitiveStatus: 'Normal',
      dementiaDiagnosis: { diagnosed: false, type: null, dateDiagnosed: null },
      mobilityStatus: 'Independent',
      fallsRiskRating: { level: 'Low', score: 2 },
      pressureInjuryStatus: { present: false, stage: null, locations: [], woundPlanLink: null },
      continenceStatus: { bladder: 'Continent', bowel: 'Continent', aids: [] },
      painLevel: { currentScore: 1, toolUsed: 'Numeric Rating Scale', lastAssessed: '2025-07-24T07:30:00Z' },
      infectionStatus: { currentInfections: [], isolationRequired: false, mrsa: false, vre: false, cdiff: false },
      immunisations: [
        { vaccine: 'COVID-19 (Pfizer)', dateGiven: '2025-03-25', batchNumber: 'FT4567', administeredBy: 'Nurse Garcia', nextDue: '2025-09-25' },
        { vaccine: 'Influenza', dateGiven: '2025-04-15', batchNumber: 'FL8901', administeredBy: 'Nurse Garcia', nextDue: '2026-04-15' }
      ],
      advanceCareDirective: { exists: true, documentId: 'DOC-ACD-004', reviewDate: '2026-01-20', summary: 'Wants all reasonable treatment. No prolonged ICU stays.' },
      resuscitationStatus: 'For resuscitation',
      goalsOfCare: 'Maintain independence and diabetes control. Stay physically active. Continue reading and chess activities.',
      restrictivePractices: []
    },
    dietary: {
      dietType: 'Diabetic',
      iddsiFoodLevel: 'Level 7 (Regular)',
      iddsiFluidLevel: 'Level 0 (Thin)',
      foodAllergies: [],
      dietaryPreferences: ['Low Purine', 'Low Sodium'],
      portionSize: 'Regular',
      fluidRestriction: { restricted: false, dailyLimitMl: null },
      nutritionalRiskScore: { score: 1, tool: 'MNA', dateAssessed: '2025-07-01', riskLevel: 'Low' },
      enteralFeeding: { feedType: null, rateMlHr: null, regime: null, startDate: null },
      mealtimeAssistance: 'Independent'
    },
    personalLife: {
      lifeHistory: 'Harold (Harry) grew up in regional Victoria connected to his Aboriginal heritage. He worked as a carpenter and later as a building inspector. He is a proud Aboriginal man who enjoys sharing Dreamtime stories with staff and other residents.',
      occupationHistory: 'Carpenter (1955-1980), Building Inspector, City of Melbourne (1980-2002)',
      hobbiesInterests: ['Chess', 'Reading', 'Woodworking', 'Storytelling', 'Watching AFL football'],
      musicPreferences: 'Country music, Archie Roach, Paul Kelly',
      dailyRoutinePreferences: { wakeTime: '06:00', showerPreference: 'morning', mealPreferences: 'Strong black tea, enjoys a cooked breakfast', bedtime: '22:00' },
      socialPreferences: 'Independent but enjoys 1:1 conversations; competitive chess player',
      petsAnimals: 'Had working dogs on the family property',
      importantDates: [{ date: '1965-12-01', description: "Daughter Jennifer's birthday" }],
      comfortItems: 'Chess set, collection of books, Aboriginal art piece from his mother'
    },
    supportNetwork: {
      primaryContact: { name: 'Jennifer Davis', relationship: 'Daughter', phone: '0415 678 901', phoneAlt: null, email: 'jennifer.davis@email.com', address: null },
      emergencyContacts: [{ name: 'Jennifer Davis', relationship: 'Daughter', phone: '0415 678 901', email: 'jennifer.davis@email.com' }],
      authorisedRepresentatives: [{ name: 'Jennifer Davis', relationship: 'Daughter', type: 'Supported Decision Maker', documentId: 'DOC-SDM-001', expiryDate: null }],
      poaMedical: { exists: false, person: null, documentId: null },
      poaFinancial: { exists: false, person: null, documentId: null },
      familyPortalUsers: [{ email: 'jennifer.davis@email.com', name: 'Jennifer Davis', relationship: 'Daughter', permissionLevel: 'full', status: 'active' }],
      supportNetwork: [{ name: 'Uncle Jack Murray', relationship: 'Elder', phone: '0447 890 123', email: null, role: 'Cultural support / Elder' }],
      gp: { name: 'Dr. Maria Garcia', practice: 'Port Melbourne Health', phone: '03 9555 6789', fax: '03 9555 6790', email: 'mgarcia@portmelbhealth.com.au', providerNumber: '5678901E', prescriberNumber: 'PBS-5678901' },
      specialists: [{ name: 'Dr. Steven Patel', specialty: 'Endocrinologist', practice: 'Diabetes Care Melbourne', phone: '03 9555 0123', email: 'spatel@diabetescare.com.au', providerNumber: '6789012F' }],
      dentist: null,
      preferredPharmacy: 'MedFast Pharmacy Bayview',
      alliedHealth: []
    },
    leaveMovements: { currentLeaveStatus: 'In Facility', leaveHistory: [], hospitalTransfers: [], bedHold: { active: false, startDate: null, expectedReturn: null, reason: null }, evacuationPriority: 'Priority 4' },
    documents: { residentAgreement: 'DOC-RA-004', consentForms: ['DOC-CON-008', 'DOC-CON-009'], poaDocuments: [], advanceCareDirectiveDoc: 'DOC-ACD-004', myHealthRecord: { connected: true, consentGiven: true, lastSync: '2025-07-24T07:00:00Z' }, externalReferrals: [], transferSummaries: [], photoGallery: [] },
    crossSectorLinks: { ndisParticipantId: null, pharmacyPatientId: 'PH-004', crossSectorConsent: { ndisDataSharing: false, pharmacyDataSharing: true, consentDate: '2023-01-20', consentedBy: 'Jennifer Davis' } },
    qualityCompliance: { nqipData: { pressureInjuries: 0, falls: 0, fallsWithInjury: 0, physicalRestraint: false, unplannedWeightLoss: false, polypharmacy: false, antipsychotics: false }, incidentReports: [], sirsNotifications: [], progressNotes: [], clinicalHandoverNotes: [], careMinutesLog: [], monthlyCareStatement: null, complaintsFeedback: [], starRatingContribution: null },
    audit: { createdAt: '2023-01-20T09:00:00Z', createdBy: 'admin', updatedAt: '2025-07-24T07:30:00Z', updatedBy: 'nurse_garcia', version: 34, isArchived: false, dataSensitivityLevel: 'Sensitive', lastAccessedAt: '2025-07-24T07:30:00Z', lastAccessedBy: 'nurse_garcia' }
  },
  {
    id: 5,
    name: 'Mary Elizabeth Johnson',
    photo: '👵',
    room: 'A101',
    facility: 'sunshine',
    ward: 'ward_a',
    age: 78,
    dateOfBirth: '1947-03-15',
    admissionDate: '2023-08-12',
    medicare: 'MJOH3151947',
    careLevel: 'High',
    mobility: 'Wheelchair',
    cognitiveStatus: 'Mild Dementia',
    primaryDiagnosis: ['Type 2 Diabetes', 'Hypertension', 'Osteoarthritis'],
    secondaryConditions: ['Depression', 'Sleep Apnea', 'Constipation'],
    allergies: ['Penicillin', 'Sulfa Drugs'],
    dietaryRestrictions: ['Diabetic Diet', 'Low Sodium', 'High Fiber'],
    weight: 68,
    height: 165,
    bloodType: 'O+',
    emergencyContact: {
      name: 'Sarah Johnson',
      relationship: 'Daughter',
      phone: '0419 123 456',
      email: 'sarah.johnson@email.com',
      familyConnectEnabled: true,
      lastLogin: '2025-07-24 09:30',
      notificationPreferences: ['medication_changes', 'health_updates', 'activities']
    },
    primaryPhysician: 'Dr. Robert Smith',
    careTeam: ['Nurse Sarah Mitchell', 'Care Worker Tom Brown', 'Physiotherapist Mark Davis'],
    prescriptions: [
      { 
        id: 'RX009', 
        medication: 'Metformin 500mg', 
        dosage: '500mg twice daily', 
        doctor: 'Dr. Robert Smith', 
        issued: '2025-06-28', 
        expires: '2025-07-28', 
        refillsLeft: 2, 
        totalRefills: 5, 
        status: 'Active', 
        instructions: 'Take with meals', 
        sideEffects: ['Nausea', 'Stomach upset'], 
        lastTaken: '2025-07-24 08:00', 
        adherenceRate: 95, 
        automatedDispense: true 
      },
      { 
        id: 'RX010', 
        medication: 'Lisinopril 10mg', 
        dosage: '10mg once daily', 
        doctor: 'Dr. Robert Smith', 
        issued: '2025-06-25', 
        expires: '2025-07-25', 
        refillsLeft: 0, 
        totalRefills: 3, 
        status: 'Expiring Soon', 
        instructions: 'Take in morning', 
        sideEffects: ['Dizziness', 'Dry cough'], 
        lastTaken: '2025-07-24 07:00', 
        adherenceRate: 98, 
        automatedDispense: true 
      }
    ],
    medicalHistory: [
      { 
        date: '2025-07-24', 
        event: 'Blood glucose check', 
        result: '6.8 mmol/L', 
        status: 'Normal', 
        doctor: 'Dr. Smith', 
        automated: true,
        type: 'AI-Monitored'
      },
      { 
        date: '2025-07-20', 
        event: 'Blood glucose check', 
        result: '7.2 mmol/L', 
        status: 'Normal', 
        doctor: 'Dr. Smith', 
        automated: true,
        type: 'AI-Monitored'
      },
      { 
        date: '2025-07-18', 
        event: 'Blood pressure check', 
        result: '135/80 mmHg', 
        status: 'Elevated', 
        doctor: 'Dr. Smith', 
        automated: false
      },
      { 
        date: '2025-07-15', 
        event: 'Weight check', 
        result: '68kg', 
        status: 'Stable', 
        doctor: 'Dr. Smith', 
        automated: false
      }
    ],
    doctorNotes: [
      { 
        id: 'DN005', 
        date: '2025-07-24', 
        doctor: 'Dr. Robert Smith', 
        type: 'Progress Note', 
        noteFormat: 'SOAP', 
        content: 'Patient reports feeling well, no complaints of dizziness or fatigue. Appetite good.', 
        subjective: 'Patient reports feeling well, no complaints of dizziness or fatigue. Appetite good.', 
        objective: 'BP: 135/80, HR: 72 regular, Blood glucose: 6.8 mmol/L, Weight stable at 68kg', 
        assessment: 'Type 2 DM well controlled. HTN stable but slightly elevated. Cognitive function stable.', 
        plan: 'Continue current medications. Monitor BP daily. Review in 1 week.', 
        priority: 'Normal', 
        sharedWithFamily: true,
        followUpRequired: '2025-07-31'
      },
      { 
        id: 'DN006', 
        date: '2025-07-10', 
        doctor: 'Dr. Robert Smith', 
        type: 'Alert', 
        noteFormat: 'Alert', 
        content: 'Lisinopril prescription expiring soon - needs renewal', 
        subjective: '', 
        objective: '', 
        assessment: 'Medication renewal required', 
        plan: 'Contact pharmacy for prescription renewal', 
        priority: 'High', 
        sharedWithFamily: true
      }
    ],
    carePlan: {
      goals: ['Maintain blood glucose within target range', 'Prevent falls', 'Maintain social engagement'],
      interventions: ['Daily glucose monitoring', 'Medication compliance monitoring', 'Music therapy 3x/week'],
      assessments: {
        cognitive: { score: 18, date: '2025-07-20', tool: 'MMSE' },
        falls: { risk: 'High', date: '2025-07-15', score: 7 }
      },
      reviewDate: '2025-08-01'
    },
    lastAssessment: '2025-07-24',
    nextAssessment: '2025-08-01',
    fallRisk: 'High',
    cognitiveScore: 18,
    behavioralNotes: 'Responds well to music therapy, enjoys social activities',
    monthlyMedicationCost: 156.80,
    yearToDateCost: 1097.60,
    familyUpdates: [
      { date: '2025-07-23', type: 'medication', message: 'Metformin dose administered successfully', read: true },
      { date: '2025-07-22', type: 'activity', message: 'Participated in group music therapy session', read: true },
      { date: '2025-07-21', type: 'health', message: 'Blood sugar levels stable this week', read: false }
    ],
    advanceCareDirective: true,
    resuscitationStatus: 'NFR',
    familyMembers: [
      {
        id: 'FM005',
        name: 'Sarah Johnson',
        relationship: 'Daughter',
        phone: '0419 123 456',
        email: 'sarah.johnson@email.com',
        isPrimaryContact: true,
        notificationPreferences: ['medication_changes', 'health_updates', 'activities'],
        lastVisit: '2025-07-24',
        nextVisit: '2025-07-27',
        accessLevel: 'Full'
      },
      {
        id: 'FM006',
        name: 'Michael Johnson',
        relationship: 'Son',
        phone: '0420 234 567',
        email: 'michael.johnson@email.com',
        isPrimaryContact: false,
        notificationPreferences: ['emergencies', 'health_updates'],
        lastVisit: '2025-07-20',
        nextVisit: '2025-07-29',
        accessLevel: 'Limited'
      }
    ],
    demographics: {
      title: 'Mrs',
      legalFirstName: 'Mary',
      legalMiddleNames: 'Elizabeth',
      legalSurname: 'Johnson',
      preferredName: 'Mary',
      previousSurname: 'O\'Leary',
      dateOfBirth: '1947-03-15',
      age: 78,
      gender: 'Female',
      pronouns: 'She/Her',
      sexAssignedAtBirth: 'Female',
      maritalStatus: 'Widowed',
      countryOfBirth: 'Ireland',
      nationality: 'Australian',
      indigenousStatus: 'Neither',
      primaryLanguage: 'English',
      otherLanguages: ['Irish Gaelic'],
      interpreterRequired: false,
      communicationMethods: ['Glasses'],
      communicationNotes: 'Articulate and engaged; occasionally repeats questions',
      religion: 'Catholic',
      culturalBackground: 'Irish-Australian',
      veteranStatus: 'Not a veteran',
      dvaFileNumber: null,
      profilePhoto: null
    },
    governmentIds: {
      medicareNumber: '6789 01234 5',
      medicareIrn: 1,
      medicareExpiry: '2028-03-31',
      ihi: '8003 6100 0080 9876',
      pensionCardNumber: 'PEN-789012',
      pensionType: 'Age Pension',
      centrelinkCrn: '709 012 345E',
      ndisNumber: 'NDIS-430078523',
      myAgedCareClientId: 'MAC-2023-081245',
      tfnEncrypted: '[ENCRYPTED]',
      tfnMasked: '***-***-675',
      privateHealthInsurer: 'HCF',
      privateHealthNumber: 'HCF-1234567'
    },
    contactAddress: {
      homeAddress: { street: '8 Chapel Street', suburb: 'St Kilda', state: 'VIC', postcode: '3182', country: 'Australia' },
      mailingAddress: null,
      phoneMobile: '+61419123456',
      phoneHome: '+61395559876',
      email: 'mary.johnson@email.com'
    },
    admission: {
      admissionDate: '2023-08-12',
      admissionType: 'Permanent',
      admissionSource: 'Hospital',
      referringOrganisation: 'Royal Melbourne Hospital',
      facilityId: 'sunshine',
      wingUnit: 'ward_a',
      roomNumber: 'A101',
      bedNumber: '2',
      roomType: 'Shared',
      residentStatus: 'Active',
      expectedLengthOfStay: 'Permanent',
      dischargeDate: null,
      dischargeReason: null,
      dateOfDeath: null
    },
    funding: {
      anaccClass: 7,
      anaccClassificationDate: '2025-01-20',
      anaccReassessmentDue: '2026-01-20',
      anaccAssessorName: 'Dr. Anne Richards',
      baseCareTariff: 76.84,
      anaccSubsidyDaily: 178.30,
      entryAdjustmentPayment: null,
      supplements: [],
      meansTestingStatus: 'Partially supported',
      accommodationPaymentType: 'Combination',
      radAmountAgreed: 200000,
      radBalance: 175000,
      dapDailyRate: 32.10,
      accommodationAgreementDate: '2023-08-12',
      meansAssessmentEffectiveDate: '2023-09-01',
      basicDailyFee: 61.96,
      incomeTestedCareFee: 22.40,
      extraServicesAgreement: { enabled: false, details: null }
    },
    clinical: {
      primaryDiagnosis: { code: 'E11.9', displayText: 'Type 2 Diabetes Mellitus' },
      secondaryDiagnoses: [
        { code: 'I10', displayText: 'Essential Hypertension', onsetDate: '2010-05-01' },
        { code: 'M15.0', displayText: 'Primary Generalised Osteoarthritis', onsetDate: '2015-11-20' }
      ],
      allergies: [
        { allergen: 'Penicillin', type: 'drug', reaction: 'Anaphylaxis', severity: 'severe', dateIdentified: '1980-04-10' },
        { allergen: 'Sulfa Drugs', type: 'drug', reaction: 'Rash', severity: 'moderate', dateIdentified: '1995-07-15' }
      ],
      clinicalAlerts: ['Falls risk', 'Allergy', 'DNACPR'],
      bloodType: 'O+',
      heightCm: 165,
      weightKg: 68,
      weightHistory: [
        { date: '2025-07-01', weightKg: 68, measuredBy: 'Nurse Mitchell' },
        { date: '2025-06-01', weightKg: 68.3, measuredBy: 'Nurse Mitchell' },
        { date: '2025-05-01', weightKg: 68.8, measuredBy: 'Nurse Mitchell' }
      ],
      bmi: 25.0,
      cognitiveStatus: 'Mild impairment',
      dementiaDiagnosis: { diagnosed: false, type: null, dateDiagnosed: null },
      mobilityStatus: 'Wheelchair',
      fallsRiskRating: { level: 'High', score: 6 },
      pressureInjuryStatus: { present: false, stage: null, locations: [], woundPlanLink: null },
      continenceStatus: { bladder: 'Occasionally incontinent', bowel: 'Continent', aids: ['Pad'] },
      painLevel: { currentScore: 4, toolUsed: 'Numeric Rating Scale', lastAssessed: '2025-07-24T09:00:00Z' },
      infectionStatus: { currentInfections: [], isolationRequired: false, mrsa: false, vre: false, cdiff: false },
      immunisations: [
        { vaccine: 'COVID-19 (Pfizer)', dateGiven: '2025-03-18', batchNumber: 'FT5678', administeredBy: 'Nurse Mitchell', nextDue: '2025-09-18' },
        { vaccine: 'Influenza', dateGiven: '2025-04-08', batchNumber: 'FL9012', administeredBy: 'Nurse Mitchell', nextDue: '2026-04-08' }
      ],
      advanceCareDirective: { exists: true, documentId: 'DOC-ACD-005', reviewDate: '2025-12-12', summary: 'Comfort-focused care. No ICU. Daughter Sarah is substitute decision maker.' },
      resuscitationStatus: 'Not for resuscitation (NFR)',
      goalsOfCare: 'Manage pain and diabetes. Maintain social engagement with family. Support wheelchair mobility and physiotherapy.',
      restrictivePractices: []
    },
    dietary: {
      dietType: 'Diabetic',
      iddsiFoodLevel: 'Level 7 (Regular)',
      iddsiFluidLevel: 'Level 0 (Thin)',
      foodAllergies: [],
      dietaryPreferences: ['Low Sodium', 'High Fiber'],
      portionSize: 'Regular',
      fluidRestriction: { restricted: false, dailyLimitMl: null },
      nutritionalRiskScore: { score: 3, tool: 'MNA', dateAssessed: '2025-07-01', riskLevel: 'Low' },
      enteralFeeding: { feedType: null, rateMlHr: null, regime: null, startDate: null },
      mealtimeAssistance: 'Physical assist (setup)'
    },
    personalLife: {
      lifeHistory: 'Mary emigrated from County Cork, Ireland in 1970. She worked as a nurse at the Royal Melbourne Hospital for 30 years. She married Patrick Johnson in 1972 and they raised two children. She is deeply connected to her Irish heritage and Catholic faith.',
      occupationHistory: 'Registered Nurse, Royal Melbourne Hospital (1970-2000), Clinical Nurse Educator (2000-2010)',
      hobbiesInterests: ['Knitting', 'Irish dancing shows', 'Crossword puzzles', 'Baking', 'Watching The Chase'],
      musicPreferences: 'Irish folk music, The Dubliners, Daniel O\'Donnell',
      dailyRoutinePreferences: { wakeTime: '07:00', showerPreference: 'evening', mealPreferences: 'Tea with milk, enjoys Irish soda bread', bedtime: '21:30' },
      socialPreferences: 'Enjoys group activities, particularly music and craft sessions. Loves visitors.',
      petsAnimals: 'Had a cat called Whiskers',
      importantDates: [{ date: '1972-09-14', description: 'Wedding anniversary with Patrick' }, { date: '1975-05-20', description: "Daughter Sarah's birthday" }],
      comfortItems: 'Rosary beads, framed family photos, hand-knitted throw from Ireland'
    },
    supportNetwork: {
      primaryContact: { name: 'Sarah Johnson', relationship: 'Daughter', phone: '0419 123 456', phoneAlt: '03 9555 8765', email: 'sarah.johnson@email.com', address: { street: '22 Park Street', suburb: 'St Kilda', state: 'VIC', postcode: '3182', country: 'Australia' } },
      emergencyContacts: [
        { name: 'Sarah Johnson', relationship: 'Daughter', phone: '0419 123 456', email: 'sarah.johnson@email.com' },
        { name: 'Michael Johnson', relationship: 'Son', phone: '0420 234 567', email: 'michael.johnson@email.com' }
      ],
      authorisedRepresentatives: [{ name: 'Sarah Johnson', relationship: 'Daughter', type: 'POA Medical', documentId: 'DOC-POA-007', expiryDate: null }],
      poaMedical: { exists: true, person: 'Sarah Johnson', documentId: 'DOC-POA-007' },
      poaFinancial: { exists: true, person: 'Michael Johnson', documentId: 'DOC-POA-008' },
      familyPortalUsers: [
        { email: 'sarah.johnson@email.com', name: 'Sarah Johnson', relationship: 'Daughter', permissionLevel: 'full', status: 'active' },
        { email: 'michael.johnson@email.com', name: 'Michael Johnson', relationship: 'Son', permissionLevel: 'view', status: 'active' }
      ],
      supportNetwork: [{ name: 'Father Thomas Kelly', relationship: 'Parish priest', phone: '03 9555 1111', email: null, role: 'Spiritual support' }],
      gp: { name: 'Dr. Robert Smith', practice: 'Brighton Medical Centre', phone: '03 9555 7890', fax: '03 9555 7891', email: 'rsmith@brightonmed.com.au', providerNumber: '2345678A', prescriberNumber: 'PBS-2345678' },
      specialists: [{ name: 'Dr. Steven Patel', specialty: 'Endocrinologist', practice: 'Diabetes Care Melbourne', phone: '03 9555 0123', email: 'spatel@diabetescare.com.au', providerNumber: '6789012F' }],
      dentist: null,
      preferredPharmacy: 'MedFast Pharmacy Sunshine',
      alliedHealth: [{ name: 'Mark Davis', discipline: 'Physio', practice: 'Sunshine Physiotherapy', phone: '03 9555 5678', providerNumber: '5678901D' }]
    },
    leaveMovements: { currentLeaveStatus: 'In Facility', leaveHistory: [{ type: 'Social Leave', startDate: '2025-06-20', endDate: '2025-06-21', destination: "Daughter's home", notes: 'Family birthday', returnStatus: 'Returned' }], hospitalTransfers: [], bedHold: { active: false, startDate: null, expectedReturn: null, reason: null }, evacuationPriority: 'Priority 2' },
    documents: { residentAgreement: 'DOC-RA-005', consentForms: ['DOC-CON-010', 'DOC-CON-011'], poaDocuments: ['DOC-POA-007', 'DOC-POA-008'], advanceCareDirectiveDoc: 'DOC-ACD-005', myHealthRecord: { connected: true, consentGiven: true, lastSync: '2025-07-24T09:00:00Z' }, externalReferrals: ['DOC-REF-002'], transferSummaries: [], photoGallery: [] },
    crossSectorLinks: { ndisParticipantId: 'NDIS-430078523', pharmacyPatientId: 'PH-005', crossSectorConsent: { ndisDataSharing: true, pharmacyDataSharing: true, consentDate: '2023-08-12', consentedBy: 'Sarah Johnson' } },
    qualityCompliance: { nqipData: { pressureInjuries: 0, falls: 2, fallsWithInjury: 1, physicalRestraint: false, unplannedWeightLoss: false, polypharmacy: true, antipsychotics: false }, incidentReports: [], sirsNotifications: [], progressNotes: [], clinicalHandoverNotes: [], careMinutesLog: [], monthlyCareStatement: null, complaintsFeedback: [], starRatingContribution: null },
    audit: { createdAt: '2023-08-12T09:00:00Z', createdBy: 'admin', updatedAt: '2025-07-24T09:00:00Z', updatedBy: 'nurse_mitchell', version: 28, isArchived: false, dataSensitivityLevel: 'Sensitive', lastAccessedAt: '2025-07-24T09:00:00Z', lastAccessedBy: 'nurse_mitchell' }
  }
];

// Generate additional 115 residents to reach 120 total
const generateAdditionalResidents = () => {
  const additionalResidents = [];
  const firstNames = [
    'Alice', 'Betty', 'Carol', 'Doris', 'Edith', 'Florence', 'Grace', 'Helen', 'Iris', 'Joyce',
    'Kathleen', 'Lillian', 'Mildred', 'Nora', 'Olive', 'Pearl', 'Queenie', 'Rose', 'Sylvia', 'Thelma',
    'Ursula', 'Vera', 'Winifred', 'Yvonne', 'Zelda', 'Agnes', 'Beatrice', 'Constance', 'Dorothy', 'Ethel',
    'Frank', 'George', 'Harold', 'Ivan', 'James', 'Kenneth', 'Leonard', 'Maurice', 'Norman', 'Oscar',
    'Percy', 'Quentin', 'Reginald', 'Stanley', 'Trevor', 'Victor', 'Walter', 'Xavier', 'Yves', 'Zachary'
  ];
  
  const lastNames = [
    'Anderson', 'Brown', 'Clark', 'Davis', 'Evans', 'Fisher', 'Green', 'Harris', 'Irwin', 'Jones',
    'King', 'Lewis', 'Miller', 'Nelson', 'Owen', 'Parker', 'Quinn', 'Roberts', 'Smith', 'Taylor',
    'Underwood', 'Vaughan', 'Wilson', 'Young', 'Adams', 'Baker', 'Cooper', 'Edwards', 'Foster', 'Gray'
  ];

  const facilities = ['sunshine', 'bayview', 'greenhill', 'rosewood'];
  const wards = ['ward_a', 'ward_b', 'ward_c', 'ward_d'];
  const careLevels = ['Low', 'Medium', 'High', 'Critical'];
  const cognitiveStatuses = ['Normal', 'Mild Dementia', 'Moderate Dementia', 'Severe Dementia'];
  const mobilityLevels = ['Independent', 'Independent with cane', 'Walker for all distances', 'Wheelchair'];
  
  const primaryConditions = [
    ['Type 2 Diabetes', 'Hypertension'],
    ['Alzheimer\'s Disease', 'Osteoarthritis'],
    ['Heart Failure', 'COPD'],
    ['Stroke Recovery', 'Hypertension'],
    ['Parkinson\'s Disease', 'Dementia'],
    ['Osteoporosis', 'Arthritis'],
    ['Cancer', 'Pain Management'],
    ['Kidney Disease', 'Diabetes'],
    ['Dementia', 'Wandering'],
    ['Depression', 'Anxiety']
  ];

  const doctors = [
    'Dr. Robert Smith', 'Dr. Emily Wilson', 'Dr. Michael Chen', 'Dr. Maria Garcia',
    'Dr. David Kim', 'Dr. Lisa Anderson', 'Dr. James Brown', 'Dr. Sarah Clark'
  ];

  for (let i = 6; i <= 120; i++) {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    const age = Math.floor(Math.random() * 30) + 70; // 70-99 years old
    const facility = facilities[Math.floor(Math.random() * facilities.length)];
    const ward = wards[Math.floor(Math.random() * wards.length)];
    const careLevel = careLevels[Math.floor(Math.random() * careLevels.length)];
    const cognitiveStatus = cognitiveStatuses[Math.floor(Math.random() * cognitiveStatuses.length)];
    const mobility = mobilityLevels[Math.floor(Math.random() * mobilityLevels.length)];
    const conditions = primaryConditions[Math.floor(Math.random() * primaryConditions.length)];
    const doctor = doctors[Math.floor(Math.random() * doctors.length)];
    
    const resident = {
      id: i,
      name: `${firstName} ${lastName}`,
      photo: '👵',
      room: `${String.fromCharCode(65 + Math.floor(Math.random() * 4))}${Math.floor(Math.random() * 200) + 100}`,
      facility: facility,
      ward: ward,
      age: age,
      dateOfBirth: `${1940 + Math.floor(Math.random() * 30)}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
      admissionDate: `${2020 + Math.floor(Math.random() * 5)}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
      medicare: `${firstName.substring(0, 4).toUpperCase()}${lastName.substring(0, 3).toUpperCase()}${age}${Math.floor(Math.random() * 1000)}`,
      careLevel: careLevel,
      mobility: mobility,
      cognitiveStatus: cognitiveStatus,
      primaryDiagnosis: [
        ...conditions,
        ...['Hypertension', 'Osteoporosis', 'Depression', 'Arthritis', 'Heart Disease', 'COPD', 'Kidney Disease', 'Diabetes', 'Dementia', 'Stroke History'].slice(0, Math.floor(Math.random() * 4) + 2)
      ],
      secondaryConditions: ['Anxiety', 'Sleep Apnea', 'Urinary Incontinence', 'Pressure Ulcers', 'Vision Impairment', 'Hearing Loss', 'Osteoarthritis', 'Chronic Pain'].slice(0, Math.floor(Math.random() * 4) + 2),
      allergies: ['Penicillin', 'Aspirin', 'Latex'].slice(0, Math.floor(Math.random() * 2)),
      dietaryRestrictions: ['Low Sodium', 'Diabetic Diet', 'Pureed'].slice(0, Math.floor(Math.random() * 2) + 1),
      weight: Math.floor(Math.random() * 40) + 50,
      height: Math.floor(Math.random() * 30) + 150,
      bloodType: ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'][Math.floor(Math.random() * 8)],
      emergencyContact: {
        name: `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastName}`,
        relationship: ['Daughter', 'Son', 'Wife', 'Husband'][Math.floor(Math.random() * 4)],
        phone: `04${Math.floor(Math.random() * 90000000) + 10000000}`,
        email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@email.com`,
        familyConnectEnabled: true,
        lastLogin: `2025-07-${String(Math.floor(Math.random() * 24) + 1).padStart(2, '0')} ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        notificationPreferences: ['medication_changes', 'health_updates', 'activities']
      },
      primaryPhysician: doctor,
      careTeam: ['Nurse Sarah Mitchell', 'Care Worker Tom Brown'],
      prescriptions: (() => {
        const medicationList = [
          { name: 'Metformin 500mg', dosage: '500mg twice daily', reason: 'Type 2 Diabetes' },
          { name: 'Lisinopril 10mg', dosage: '10mg once daily', reason: 'Hypertension' },
          { name: 'Donepezil 10mg', dosage: '10mg once daily at bedtime', reason: 'Alzheimer\'s Disease' },
          { name: 'Atorvastatin 20mg', dosage: '20mg once daily', reason: 'High Cholesterol' },
          { name: 'Omeprazole 20mg', dosage: '20mg once daily before breakfast', reason: 'GERD' },
          { name: 'Amlodipine 5mg', dosage: '5mg once daily', reason: 'Hypertension' },
          { name: 'Furosemide 40mg', dosage: '40mg once daily', reason: 'Heart Failure' },
          { name: 'Warfarin 5mg', dosage: '5mg once daily', reason: 'Blood Clot Prevention' },
          { name: 'Paracetamol 500mg', dosage: '500mg every 6 hours as needed', reason: 'Pain Management' },
          { name: 'Tramadol 50mg', dosage: '50mg twice daily', reason: 'Chronic Pain' },
          { name: 'Sertraline 50mg', dosage: '50mg once daily', reason: 'Depression' },
          { name: 'Memantine 10mg', dosage: '10mg twice daily', reason: 'Dementia' },
          { name: 'Levodopa 100mg', dosage: '100mg three times daily', reason: 'Parkinson\'s Disease' },
          { name: 'Salbutamol Inhaler', dosage: '2 puffs as needed', reason: 'COPD' },
          { name: 'Insulin Glargine', dosage: '20 units at bedtime', reason: 'Diabetes' }
        ];
        
        // Select 4-7 medications for each Aged Care resident
        const selectedMeds = medicationList
          .sort(() => Math.random() - 0.5)
          .slice(0, Math.floor(Math.random() * 4) + 4);
        
        return selectedMeds.map((med, medIndex) => ({
          id: `RX${String(i).padStart(3, '0')}-${medIndex + 1}`,
          medication: med.name,
          dosage: med.dosage,
          reason: med.reason,
          doctor: doctor,
          issued: `2025-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
          expires: `2026-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
          refillsLeft: Math.floor(Math.random() * 5) + 1,
          totalRefills: Math.floor(Math.random() * 5) + 3,
          status: Math.random() > 0.8 ? 'Expiring Soon' : 'Active',
          instructions: med.dosage,
          sideEffects: ['Nausea', 'Dizziness', 'Headache', 'Fatigue', 'Dry mouth'].slice(0, Math.floor(Math.random() * 2) + 1),
          lastTaken: `2025-07-24 ${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
          adherenceRate: Math.floor(Math.random() * 20) + 80,
          automatedDispense: Math.random() > 0.3
        }));
      })(),
      medicalHistory: [
        {
          date: `2025-07-${String(Math.floor(Math.random() * 24) + 1).padStart(2, '0')}`,
          event: ['Blood pressure check', 'Blood glucose check', 'Weight check', 'Cognitive assessment'][Math.floor(Math.random() * 4)],
          result: ['Normal', 'Elevated', 'Stable'][Math.floor(Math.random() * 3)],
          status: ['Normal', 'Elevated', 'Stable'][Math.floor(Math.random() * 3)],
          doctor: doctor,
          automated: Math.random() > 0.5,
          type: Math.random() > 0.7 ? 'AI-Monitored' : 'Standard'
        }
      ],
      doctorNotes: [
        {
          id: `DN${String(i).padStart(3, '0')}`,
          date: `2025-07-${String(Math.floor(Math.random() * 24) + 1).padStart(2, '0')}`,
          doctor: doctor,
          type: 'Progress Note',
          noteFormat: 'SOAP',
          content: 'Patient stable with current treatment plan.',
          subjective: 'Patient reports feeling well',
          objective: 'Vital signs stable',
          assessment: 'Condition well controlled',
          plan: 'Continue current medications',
          priority: 'Normal',
          sharedWithFamily: true,
          followUpRequired: `2025-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`
        }
      ],
      carePlan: {
        goals: [
          'Maintain optimal health and wellbeing',
          'Prevent complications from chronic conditions',
          'Maintain independence in daily activities',
          'Manage pain and discomfort effectively',
          'Improve mobility and physical function',
          'Enhance cognitive function and memory',
          'Maintain social connections and engagement',
          'Prevent falls and injuries',
          'Manage medication adherence',
          'Support family involvement in care'
        ].slice(0, Math.floor(Math.random() * 5) + 4),
        interventions: [
          'Regular health monitoring and assessments',
          'Comprehensive medication management',
          'Individualized exercise and mobility program',
          'Pain management strategies',
          'Cognitive stimulation activities',
          'Social engagement programs',
          'Fall prevention measures',
          'Nutritional support and dietary management',
          'Family education and support',
          'Regular medical reviews'
        ].slice(0, Math.floor(Math.random() * 5) + 4),
        assessments: {
          cognitive: { score: Math.floor(Math.random() * 30) + 10, date: `2025-07-${String(Math.floor(Math.random() * 24) + 1).padStart(2, '0')}`, tool: 'MMSE' },
          falls: { risk: ['Low', 'Medium', 'High'][Math.floor(Math.random() * 3)], date: `2025-07-${String(Math.floor(Math.random() * 24) + 1).padStart(2, '0')}`, score: Math.floor(Math.random() * 10) + 1 }
        },
        reviewDate: `2025-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`
      },
      lastAssessment: `2025-07-${String(Math.floor(Math.random() * 24) + 1).padStart(2, '0')}`,
      nextAssessment: `2025-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
      fallRisk: ['Low', 'Medium', 'High'][Math.floor(Math.random() * 3)],
      cognitiveScore: Math.floor(Math.random() * 30) + 10,
      behavioralNotes: 'Generally cooperative and engaged in activities',
      monthlyMedicationCost: Math.floor(Math.random() * 200) + 50,
      yearToDateCost: Math.floor(Math.random() * 1400) + 500,
      familyUpdates: [
        {
          date: `2025-07-${String(Math.floor(Math.random() * 24) + 1).padStart(2, '0')}`,
          type: ['activity', 'health', 'medication'][Math.floor(Math.random() * 3)],
          message: 'Patient participated in activities today',
          read: Math.random() > 0.5
        }
      ],
      advanceCareDirective: Math.random() > 0.2,
      resuscitationStatus: ['DNR', 'NFR', 'Full'][Math.floor(Math.random() * 3)],
      familyMembers: [
        {
          id: `FM${String(i).padStart(3, '0')}`,
          name: `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastName}`,
          relationship: ['Daughter', 'Son', 'Wife', 'Husband'][Math.floor(Math.random() * 4)],
          phone: `04${Math.floor(Math.random() * 90000000) + 10000000}`,
          email: `${firstNames[Math.floor(Math.random() * firstNames.length)].toLowerCase()}.${lastName.toLowerCase()}@email.com`,
          isPrimaryContact: Math.random() > 0.5,
          notificationPreferences: ['medication_changes', 'health_updates', 'activities'],
          lastVisit: `2025-07-${String(Math.floor(Math.random() * 24) + 1).padStart(2, '0')}`,
          nextVisit: `2025-07-${String(Math.floor(Math.random() * 24) + 1).padStart(2, '0')}`,
          accessLevel: ['Full', 'Limited'][Math.floor(Math.random() * 2)]
        }
      ]
    };
    
    additionalResidents.push(resident);
  }
  
  return additionalResidents;
};

// Add the additional residents to the main residents array
residents.push(...generateAdditionalResidents());

// Generate NDIS participants with disabilities and medications
const generateNDISParticipants = () => {
  const ndisParticipants = [];
  const ndisFacilities = ['ndis_sunshine_support', 'ndis_bayview_community', 'ndis_house_1', 'ndis_house_2', 'ndis_house_3'];
  
  const firstNames = [
    'Alex', 'Jordan', 'Taylor', 'Casey', 'Morgan', 'Riley', 'Avery', 'Quinn', 'Sage', 'River',
    'Cameron', 'Dakota', 'Emery', 'Finley', 'Harper', 'Hayden', 'Jamie', 'Kai', 'Logan', 'Noah',
    'Parker', 'Reese', 'Rowan', 'Skylar', 'Tyler', 'Blake', 'Drew', 'Eden', 'Gray', 'Indigo',
    'Phoenix', 'River', 'Sage', 'Ocean', 'Storm', 'Luna', 'Nova', 'Zephyr', 'Orion', 'Atlas'
  ];
  
  // Unique NDIS participant last names (no overlap with aged care residents)
  // Aged care uses: Anderson, Brown, Clark, Davis, Evans, Fisher, Green, Harris, Irwin, Jones, King, Lewis, Miller, Nelson, Owen, Parker, Quinn, Roberts, Smith, Taylor, Underwood, Vaughan, Wilson, Young, Adams, Baker, Cooper, Edwards, Foster, Gray
  const lastNames = [
    'Mitchell', 'Patel', 'Chen', 'Kumar', 'Singh', 'O\'Brien', 'Murphy', 'Walsh', 'O\'Connor', 'Ryan',
    'Nguyen', 'Lee', 'Kim', 'Zhang', 'Wang', 'Martinez', 'Garcia', 'Rodriguez', 'Lopez', 'Gonzalez',
    'Fernandez', 'Perez', 'Sanchez', 'Ramirez', 'Torres', 'Flores', 'Rivera', 'Gomez', 'Diaz', 'Cruz',
    'Hughes', 'Price', 'Wood', 'Bennett', 'Griffin', 'Russell', 'Alexander', 'Watson', 'Brooks', 'Sanders',
    'Perry', 'Powell', 'Long', 'Patterson', 'Washington', 'Butler', 'Simmons', 'Gonzales', 'Bryant', 'Hayes',
    'Myers', 'Ford', 'Hamilton', 'Barnes', 'Coleman', 'Richardson', 'Howard', 'Cox', 'Ward', 'Peterson',
    'James', 'Kelly', 'Reed', 'Bailey', 'Stewart', 'Morris', 'Rogers', 'Cook', 'Morgan', 'Bell',
    'Turner', 'Phillips', 'Campbell', 'Collins', 'Sanchez', 'Torres', 'Ramirez', 'Gonzalez', 'Martinez', 'Garcia'
  ];

  // Comprehensive NDIS disabilities and their associated medications
  // Expanded for investor presentations - 25+ diverse conditions
  const disabilities = [
    {
      name: 'Intellectual Disability',
      severity: ['Mild', 'Moderate', 'Severe', 'Profound'],
      medications: [
        { name: 'Risperidone 1mg', reason: 'Behavioral management', dosage: '1mg twice daily' },
        { name: 'Sertraline 50mg', reason: 'Anxiety management', dosage: '50mg once daily' },
        { name: 'Valproic Acid 500mg', reason: 'Seizure control', dosage: '500mg twice daily' },
        { name: 'Olanzapine 5mg', reason: 'Aggression management', dosage: '5mg once daily' },
        { name: 'Clonidine 0.1mg', reason: 'Hyperactivity', dosage: '0.1mg twice daily' }
      ]
    },
    {
      name: 'Autism Spectrum Disorder',
      severity: ['Level 1', 'Level 2', 'Level 3'],
      medications: [
        { name: 'Aripiprazole 5mg', reason: 'Irritability and aggression', dosage: '5mg once daily' },
        { name: 'Sertraline 50mg', reason: 'Anxiety and repetitive behaviors', dosage: '50mg once daily' },
        { name: 'Melatonin 3mg', reason: 'Sleep regulation', dosage: '3mg at bedtime' },
        { name: 'Guanfacine 1mg', reason: 'ADHD symptoms', dosage: '1mg twice daily' },
        { name: 'Risperidone 0.5mg', reason: 'Severe behavioral challenges', dosage: '0.5mg twice daily' }
      ]
    },
    {
      name: 'Cerebral Palsy',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Baclofen 10mg', reason: 'Muscle spasticity', dosage: '10mg three times daily' },
        { name: 'Diazepam 5mg', reason: 'Muscle relaxation', dosage: '5mg twice daily' },
        { name: 'Paracetamol 500mg', reason: 'Pain management', dosage: '500mg every 6 hours as needed' },
        { name: 'Tizanidine 4mg', reason: 'Spasticity management', dosage: '4mg three times daily' },
        { name: 'Dantrolene 25mg', reason: 'Chronic spasticity', dosage: '25mg four times daily' }
      ]
    },
    {
      name: 'Down Syndrome',
      severity: ['Mild', 'Moderate'],
      medications: [
        { name: 'Levothyroxine 75mcg', reason: 'Hypothyroidism', dosage: '75mcg once daily' },
        { name: 'Donepezil 5mg', reason: 'Cognitive support', dosage: '5mg once daily' },
        { name: 'Folic Acid 5mg', reason: 'Nutritional support', dosage: '5mg once daily' },
        { name: 'Vitamin D 1000IU', reason: 'Bone health', dosage: '1000IU once daily' },
        { name: 'Omega-3 1000mg', reason: 'Cognitive function', dosage: '1000mg once daily' }
      ]
    },
    {
      name: 'Multiple Sclerosis',
      severity: ['Relapsing-Remitting', 'Primary Progressive', 'Secondary Progressive'],
      medications: [
        { name: 'Interferon Beta-1a', reason: 'Disease modification', dosage: '30mcg weekly injection' },
        { name: 'Baclofen 10mg', reason: 'Spasticity management', dosage: '10mg three times daily' },
        { name: 'Amitriptyline 25mg', reason: 'Neuropathic pain', dosage: '25mg at bedtime' },
        { name: 'Fingolimod 0.5mg', reason: 'Disease progression', dosage: '0.5mg once daily' },
        { name: 'Modafinil 100mg', reason: 'Fatigue management', dosage: '100mg twice daily' }
      ]
    },
    {
      name: 'Spinal Cord Injury',
      severity: ['C1-C4', 'C5-C8', 'T1-T12', 'L1-L5'],
      medications: [
        { name: 'Baclofen 10mg', reason: 'Spasticity', dosage: '10mg three times daily' },
        { name: 'Oxybutynin 5mg', reason: 'Bladder control', dosage: '5mg twice daily' },
        { name: 'Gabapentin 300mg', reason: 'Neuropathic pain', dosage: '300mg three times daily' },
        { name: 'Duloxetine 30mg', reason: 'Depression and pain', dosage: '30mg twice daily' },
        { name: 'Tolterodine 2mg', reason: 'Overactive bladder', dosage: '2mg twice daily' }
      ]
    },
    {
      name: 'Acquired Brain Injury',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Levetiracetam 500mg', reason: 'Seizure prevention', dosage: '500mg twice daily' },
        { name: 'Sertraline 50mg', reason: 'Depression and mood', dosage: '50mg once daily' },
        { name: 'Donepezil 10mg', reason: 'Cognitive function', dosage: '10mg once daily' },
        { name: 'Memantine 10mg', reason: 'Cognitive enhancement', dosage: '10mg twice daily' },
        { name: 'Methylphenidate 10mg', reason: 'Attention and focus', dosage: '10mg twice daily' }
      ]
    },
    {
      name: 'Bipolar Disorder',
      severity: ['Type I', 'Type II', 'Cyclothymic'],
      medications: [
        { name: 'Lithium 300mg', reason: 'Mood stabilization', dosage: '300mg twice daily' },
        { name: 'Quetiapine 100mg', reason: 'Mood stabilization', dosage: '100mg at bedtime' },
        { name: 'Lamotrigine 50mg', reason: 'Mood stabilization', dosage: '50mg twice daily' },
        { name: 'Valproic Acid 500mg', reason: 'Mood episodes', dosage: '500mg twice daily' },
        { name: 'Olanzapine 10mg', reason: 'Acute mania', dosage: '10mg once daily' }
      ]
    },
    {
      name: 'Schizophrenia',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Olanzapine 10mg', reason: 'Psychosis management', dosage: '10mg once daily' },
        { name: 'Risperidone 2mg', reason: 'Positive symptoms', dosage: '2mg twice daily' },
        { name: 'Aripiprazole 10mg', reason: 'Psychosis and mood', dosage: '10mg once daily' },
        { name: 'Clozapine 100mg', reason: 'Treatment-resistant', dosage: '100mg twice daily' },
        { name: 'Paliperidone 3mg', reason: 'Long-acting treatment', dosage: '3mg once daily' }
      ]
    },
    {
      name: 'Muscular Dystrophy',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Prednisolone 20mg', reason: 'Muscle strength', dosage: '20mg once daily' },
        { name: 'Baclofen 10mg', reason: 'Spasticity', dosage: '10mg three times daily' },
        { name: 'Calcium 1000mg', reason: 'Bone health', dosage: '1000mg once daily' },
        { name: 'Eteplirsen', reason: 'DMD treatment', dosage: '30mg/kg weekly IV' },
        { name: 'Deflazacort 18mg', reason: 'DMD muscle preservation', dosage: '18mg once daily' }
      ]
    },
    {
      name: 'Epilepsy',
      severity: ['Controlled', 'Partially Controlled', 'Uncontrolled'],
      medications: [
        { name: 'Levetiracetam 500mg', reason: 'Seizure control', dosage: '500mg twice daily' },
        { name: 'Valproic Acid 500mg', reason: 'Generalized seizures', dosage: '500mg twice daily' },
        { name: 'Carbamazepine 200mg', reason: 'Focal seizures', dosage: '200mg twice daily' },
        { name: 'Lamotrigine 50mg', reason: 'Seizure prevention', dosage: '50mg twice daily' },
        { name: 'Topiramate 50mg', reason: 'Refractory epilepsy', dosage: '50mg twice daily' }
      ]
    },
    {
      name: 'Attention Deficit Hyperactivity Disorder (ADHD)',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Methylphenidate 10mg', reason: 'Attention and focus', dosage: '10mg twice daily' },
        { name: 'Dexamphetamine 5mg', reason: 'ADHD symptoms', dosage: '5mg twice daily' },
        { name: 'Atomoxetine 40mg', reason: 'Non-stimulant option', dosage: '40mg once daily' },
        { name: 'Guanfacine 1mg', reason: 'ADHD and aggression', dosage: '1mg twice daily' },
        { name: 'Lisdexamfetamine 30mg', reason: 'Long-acting ADHD', dosage: '30mg once daily' }
      ]
    },
    {
      name: 'Huntington\'s Disease',
      severity: ['Early Stage', 'Mid Stage', 'Late Stage'],
      medications: [
        { name: 'Tetrabenazine 25mg', reason: 'Chorea management', dosage: '25mg three times daily' },
        { name: 'Haloperidol 2mg', reason: 'Psychotic symptoms', dosage: '2mg twice daily' },
        { name: 'Sertraline 50mg', reason: 'Depression', dosage: '50mg once daily' },
        { name: 'Risperidone 1mg', reason: 'Behavioral symptoms', dosage: '1mg twice daily' },
        { name: 'Amantadine 100mg', reason: 'Motor symptoms', dosage: '100mg twice daily' }
      ]
    },
    {
      name: 'Parkinson\'s Disease',
      severity: ['Early', 'Moderate', 'Advanced'],
      medications: [
        { name: 'Levodopa/Carbidopa 100/25mg', reason: 'Motor symptoms', dosage: '100/25mg three times daily' },
        { name: 'Pramipexole 0.5mg', reason: 'Dopamine agonist', dosage: '0.5mg three times daily' },
        { name: 'Rasagiline 1mg', reason: 'MAO-B inhibitor', dosage: '1mg once daily' },
        { name: 'Entacapone 200mg', reason: 'COMT inhibitor', dosage: '200mg with each levodopa' },
        { name: 'Amantadine 100mg', reason: 'Dyskinesia management', dosage: '100mg twice daily' }
      ]
    },
    {
      name: 'Tourette Syndrome',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Aripiprazole 5mg', reason: 'Tic reduction', dosage: '5mg once daily' },
        { name: 'Risperidone 1mg', reason: 'Tic management', dosage: '1mg twice daily' },
        { name: 'Clonidine 0.1mg', reason: 'Tic suppression', dosage: '0.1mg twice daily' },
        { name: 'Haloperidol 2mg', reason: 'Severe tics', dosage: '2mg twice daily' },
        { name: 'Guanfacine 1mg', reason: 'ADHD and tics', dosage: '1mg twice daily' }
      ]
    },
    {
      name: 'Fragile X Syndrome',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Sertraline 50mg', reason: 'Anxiety and mood', dosage: '50mg once daily' },
        { name: 'Aripiprazole 5mg', reason: 'Aggression and irritability', dosage: '5mg once daily' },
        { name: 'Guanfacine 1mg', reason: 'ADHD symptoms', dosage: '1mg twice daily' },
        { name: 'Melatonin 3mg', reason: 'Sleep disorders', dosage: '3mg at bedtime' },
        { name: 'Risperidone 1mg', reason: 'Behavioral challenges', dosage: '1mg twice daily' }
      ]
    },
    {
      name: 'Prader-Willi Syndrome',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Growth Hormone', reason: 'Growth and metabolism', dosage: '0.24mg/kg weekly injection' },
        { name: 'Sertraline 50mg', reason: 'Anxiety and OCD', dosage: '50mg once daily' },
        { name: 'Risperidone 1mg', reason: 'Behavioral management', dosage: '1mg twice daily' },
        { name: 'Topiramate 25mg', reason: 'Appetite suppression', dosage: '25mg twice daily' },
        { name: 'Metformin 500mg', reason: 'Insulin resistance', dosage: '500mg twice daily' }
      ]
    },
    {
      name: 'Williams Syndrome',
      severity: ['Mild', 'Moderate'],
      medications: [
        { name: 'Atenolol 25mg', reason: 'Cardiovascular management', dosage: '25mg once daily' },
        { name: 'Calcium 1000mg', reason: 'Bone health', dosage: '1000mg once daily' },
        { name: 'Vitamin D 1000IU', reason: 'Calcium absorption', dosage: '1000IU once daily' },
        { name: 'Sertraline 25mg', reason: 'Anxiety management', dosage: '25mg once daily' },
        { name: 'Melatonin 3mg', reason: 'Sleep regulation', dosage: '3mg at bedtime' }
      ]
    },
    {
      name: 'Rett Syndrome',
      severity: ['Stage I', 'Stage II', 'Stage III', 'Stage IV'],
      medications: [
        { name: 'Levetiracetam 250mg', reason: 'Seizure control', dosage: '250mg twice daily' },
        { name: 'Baclofen 5mg', reason: 'Spasticity', dosage: '5mg three times daily' },
        { name: 'Sertraline 25mg', reason: 'Anxiety and mood', dosage: '25mg once daily' },
        { name: 'Melatonin 3mg', reason: 'Sleep disorders', dosage: '3mg at bedtime' },
        { name: 'Carbamazepine 100mg', reason: 'Seizure management', dosage: '100mg twice daily' }
      ]
    },
    {
      name: 'Angelman Syndrome',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Levetiracetam 250mg', reason: 'Seizure control', dosage: '250mg twice daily' },
        { name: 'Clonazepam 0.5mg', reason: 'Seizure and anxiety', dosage: '0.5mg twice daily' },
        { name: 'Melatonin 3mg', reason: 'Sleep disorders', dosage: '3mg at bedtime' },
        { name: 'Valproic Acid 250mg', reason: 'Seizure prevention', dosage: '250mg twice daily' },
        { name: 'Topiramate 25mg', reason: 'Refractory seizures', dosage: '25mg twice daily' }
      ]
    },
    {
      name: '22q11.2 Deletion Syndrome',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Calcium 1000mg', reason: 'Hypocalcemia', dosage: '1000mg twice daily' },
        { name: 'Vitamin D 2000IU', reason: 'Calcium metabolism', dosage: '2000IU once daily' },
        { name: 'Sertraline 50mg', reason: 'Anxiety and mood', dosage: '50mg once daily' },
        { name: 'Risperidone 1mg', reason: 'Psychotic symptoms', dosage: '1mg twice daily' },
        { name: 'Aripiprazole 5mg', reason: 'Behavioral management', dosage: '5mg once daily' }
      ]
    },
    {
      name: 'Spina Bifida',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Folic Acid 5mg', reason: 'Prevention support', dosage: '5mg once daily' },
        { name: 'Oxybutynin 5mg', reason: 'Bladder dysfunction', dosage: '5mg twice daily' },
        { name: 'Baclofen 10mg', reason: 'Spasticity', dosage: '10mg three times daily' },
        { name: 'Gabapentin 300mg', reason: 'Neuropathic pain', dosage: '300mg three times daily' },
        { name: 'Tolterodine 2mg', reason: 'Overactive bladder', dosage: '2mg twice daily' }
      ]
    },
    {
      name: 'Visual Impairment',
      severity: ['Partially Sighted', 'Legally Blind', 'Totally Blind'],
      medications: [
        { name: 'Latanoprost 0.005%', reason: 'Glaucoma management', dosage: '1 drop daily' },
        { name: 'Timolol 0.5%', reason: 'Intraocular pressure', dosage: '1 drop twice daily' },
        { name: 'Brinzolamide 1%', reason: 'Glaucoma treatment', dosage: '1 drop twice daily' },
        { name: 'Dorzolamide 2%', reason: 'Ocular hypertension', dosage: '1 drop three times daily' },
        { name: 'Bimatoprost 0.03%', reason: 'Glaucoma control', dosage: '1 drop daily' }
      ]
    },
    {
      name: 'Hearing Impairment',
      severity: ['Mild', 'Moderate', 'Severe', 'Profound'],
      medications: [
        { name: 'Prednisolone 20mg', reason: 'Sudden hearing loss', dosage: '20mg once daily (short-term)' },
        { name: 'Antibiotics (as needed)', reason: 'Infection-related', dosage: 'As prescribed' },
        { name: 'Antihistamines (as needed)', reason: 'Allergy-related', dosage: 'As prescribed' },
        { name: 'Decongestants (as needed)', reason: 'Eustachian tube', dosage: 'As prescribed' }
      ]
    },
    {
      name: 'Post-Traumatic Stress Disorder (PTSD)',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Sertraline 50mg', reason: 'Anxiety and depression', dosage: '50mg once daily' },
        { name: 'Paroxetine 20mg', reason: 'PTSD symptoms', dosage: '20mg once daily' },
        { name: 'Prazosin 1mg', reason: 'Nightmares', dosage: '1mg at bedtime' },
        { name: 'Mirtazapine 15mg', reason: 'Sleep and mood', dosage: '15mg at bedtime' },
        { name: 'Venlafaxine 75mg', reason: 'Anxiety and depression', dosage: '75mg twice daily' }
      ]
    },
    {
      name: 'Obsessive-Compulsive Disorder (OCD)',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Sertraline 100mg', reason: 'OCD symptoms', dosage: '100mg once daily' },
        { name: 'Fluvoxamine 100mg', reason: 'OCD treatment', dosage: '100mg twice daily' },
        { name: 'Clomipramine 25mg', reason: 'Refractory OCD', dosage: '25mg three times daily' },
        { name: 'Risperidone 1mg', reason: 'Augmentation therapy', dosage: '1mg twice daily' },
        { name: 'Aripiprazole 5mg', reason: 'Treatment-resistant', dosage: '5mg once daily' }
      ]
    },
    {
      name: 'Borderline Personality Disorder',
      severity: ['Mild', 'Moderate', 'Severe'],
      medications: [
        { name: 'Sertraline 50mg', reason: 'Depression and mood', dosage: '50mg once daily' },
        { name: 'Quetiapine 50mg', reason: 'Mood instability', dosage: '50mg twice daily' },
        { name: 'Olanzapine 5mg', reason: 'Emotional dysregulation', dosage: '5mg once daily' },
        { name: 'Lamotrigine 25mg', reason: 'Mood stabilization', dosage: '25mg twice daily' },
        { name: 'Valproic Acid 250mg', reason: 'Impulsivity', dosage: '250mg twice daily' }
      ]
    }
  ];

  let participantId = 200; // Start from 200 to avoid conflicts with existing residents

  ndisFacilities.forEach((facility, facilityIndex) => {
    // Generate 8-12 participants per NDIS facility
    const participantsPerFacility = Math.floor(Math.random() * 5) + 8;
    
    for (let i = 0; i < participantsPerFacility; i++) {
      const currentParticipantId = participantId++; // Capture ID before increment
      const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      const age = Math.floor(Math.random() * 50) + 18; // 18-68 years old (typical NDIS age range)
      const disability = disabilities[Math.floor(Math.random() * disabilities.length)];
      const severity = disability.severity[Math.floor(Math.random() * disability.severity.length)];
      
      // Select 2-4 medications related to the disability (ensure all participants have medications)
      const numMedications = Math.floor(Math.random() * 3) + 2; // 2-4 medications
      const selectedMedications = disability.medications
        .sort(() => Math.random() - 0.5)
        .slice(0, numMedications);
      
      // Ensure at least 2 medications are always selected
      if (selectedMedications.length < 2) {
        selectedMedications.push(...disability.medications.slice(0, 2 - selectedMedications.length));
      }
      
      const dateOfBirth = new Date(new Date().getFullYear() - age, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1);
      const admissionDate = new Date(2020 + Math.floor(Math.random() * 5), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1);
      
      const participant = {
        id: currentParticipantId,
        name: `${firstName} ${lastName}`,
        photo: '🧑',
        room: `Room ${String(i + 1).padStart(2, '0')}`,
        facility: facility,
        ward: facility === 'ndis_house_1' ? 'independence_house' :
              facility === 'ndis_house_2' ? 'community_hub_house' :
              facility === 'ndis_house_3' ? 'wellness_center_house' :
              facility === 'ndis_sunshine_support' ? ['support_wing_a', 'support_wing_b', 'support_wing_c'][Math.floor(Math.random() * 3)] :
              ['community_wing_a', 'community_wing_b'][Math.floor(Math.random() * 2)],
        age: age,
        dateOfBirth: dateOfBirth.toISOString().split('T')[0],
        admissionDate: admissionDate.toISOString().split('T')[0],
        medicare: `${firstName.substring(0, 4).toUpperCase()}${lastName.substring(0, 3).toUpperCase()}${age}${Math.floor(Math.random() * 1000)}`,
        // NDIS-specific fields - NO aged care fields
        supportLevel: ['Low', 'Medium', 'High'][Math.floor(Math.random() * 3)], // NDIS equivalent of careLevel
        mobility: ['Independent', 'Independent with aids', 'Wheelchair', 'Requires assistance'][Math.floor(Math.random() * 4)],
        cognitiveStatus: ['Normal', 'Mild impairment', 'Moderate impairment', 'Severe impairment'][Math.floor(Math.random() * 4)],
        
        // NDIS-specific fields
        disability: disability.name,
        disabilitySeverity: severity,
        disabilities: [disability.name],
        ndisParticipantId: `NDIS-${String(currentParticipantId).padStart(6, '0')}`,
        primaryDiagnosis: [disability.name],
        secondaryConditions: ['Anxiety', 'Depression', 'Sleep disorders', 'Behavioral challenges'].slice(0, Math.floor(Math.random() * 3) + 1),
        
        allergies: ['Penicillin', 'Latex', 'Aspirin'].slice(0, Math.floor(Math.random() * 2)),
        dietaryRestrictions: ['Gluten-free', 'Dairy-free', 'Texture modified'].slice(0, Math.floor(Math.random() * 2)),
        weight: Math.floor(Math.random() * 50) + 50,
        height: Math.floor(Math.random() * 40) + 150,
        bloodType: ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'][Math.floor(Math.random() * 8)],
        
        emergencyContact: {
          name: `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastName}`,
          relationship: ['Parent', 'Guardian', 'Sibling', 'Support Coordinator'][Math.floor(Math.random() * 4)],
          phone: `04${Math.floor(Math.random() * 90000000) + 10000000}`,
          email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@email.com`,
          familyConnectEnabled: true,
          lastLogin: new Date(2025, 6, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0],
          notificationPreferences: ['medication_changes', 'health_updates', 'activities', 'incidents']
        },
        
        primaryPhysician: ['Dr. Sarah Mitchell', 'Dr. James Wilson', 'Dr. Emma Chen', 'Dr. Michael Brown'][Math.floor(Math.random() * 4)],
        careTeam: ['Support Worker', 'Occupational Therapist', 'Speech Therapist'].slice(0, Math.floor(Math.random() * 3) + 1),
        
        // NDIS participants MUST have medications - all participants have at least 2-4 medications
        prescriptions: selectedMedications.map((med, medIndex) => {
          const issuedDate = new Date(2025, Math.floor(Math.random() * 6), Math.floor(Math.random() * 28) + 1);
          const expiresDate = new Date(issuedDate);
          expiresDate.setMonth(expiresDate.getMonth() + Math.floor(Math.random() * 6) + 3); // 3-9 months validity
          
          return {
            id: `NDIS-RX-${String(currentParticipantId).padStart(6, '0')}-${String(medIndex + 1).padStart(2, '0')}`,
            medication: med.name,
            dosage: med.dosage,
            reason: med.reason,
            doctor: ['Dr. Sarah Mitchell', 'Dr. James Wilson', 'Dr. Emma Chen', 'Dr. Michael Brown'][Math.floor(Math.random() * 4)],
            issued: issuedDate.toISOString().split('T')[0],
            expires: expiresDate.toISOString().split('T')[0],
            refillsLeft: Math.floor(Math.random() * 4) + 1,
            totalRefills: Math.floor(Math.random() * 3) + 3,
            status: 'Active',
            instructions: med.dosage,
            sideEffects: ['Drowsiness', 'Nausea', 'Dizziness', 'Headache', 'Fatigue'].slice(0, Math.floor(Math.random() * 2) + 1),
            lastTaken: new Date(2025, 6, 24, Math.floor(Math.random() * 24), Math.floor(Math.random() * 60)).toISOString(),
            adherenceRate: Math.floor(Math.random() * 15) + 85, // 85-100% adherence
            automatedDispense: Math.random() > 0.3,
            ndisFunded: true, // NDIS-specific field
            supportCategory: ['Core Supports', 'Capacity Building', 'Capital Supports'][Math.floor(Math.random() * 3)]
          };
        }),
        
        // NDIS-specific medical history
        medicalHistory: [
          {
            date: new Date(2025, 6, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0],
            event: 'NDIS Disability Assessment Review',
            result: 'Stable',
            status: 'Ongoing',
            doctor: ['Dr. Sarah Mitchell', 'Dr. James Wilson'][Math.floor(Math.random() * 2)],
            automated: false,
            type: 'NDIS Assessment',
            assessmentType: 'Functional Capacity Assessment'
          },
          {
            date: new Date(2025, 5, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0],
            event: 'Medication Review',
            result: 'Medications effective',
            status: 'Stable',
            doctor: ['Dr. Sarah Mitchell', 'Dr. James Wilson'][Math.floor(Math.random() * 2)],
            automated: false,
            type: 'Medication Review',
            assessmentType: 'Pharmacological Assessment'
          }
        ],
        
        // NDIS-specific clinical notes
        doctorNotes: [
          {
            id: `NDIS-NOTE-${String(currentParticipantId).padStart(6, '0')}`,
            date: new Date(2025, 6, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0],
            doctor: ['Dr. Sarah Mitchell', 'Dr. James Wilson'][Math.floor(Math.random() * 2)],
            type: 'NDIS Progress Note',
            noteFormat: 'SOAP',
            content: `Participant managing well with ${disability.name} (${severity} severity). Current medications (${selectedMedications.length} active) are effective. Support plan goals progressing well.`,
            subjective: `Participant reports feeling stable and engaged with support services`,
            objective: `Vital signs within normal range. Medications: ${selectedMedications.map(m => m.name).join(', ')}`,
            assessment: `${disability.name} - ${severity} severity, well managed with current support plan`,
            plan: `Continue current NDIS support plan and medications. Review in 6 months.`,
            priority: 'Normal',
            sharedWithFamily: true,
            followUpRequired: new Date(2026, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
            ndisRelated: true
          }
        ],
        
        // NDIS Support Plan (not Care Plan - aged care terminology)
        supportPlan: {
          goals: [
            `Improve ${disability.name} management`,
            'Increase community participation',
            'Enhance independent living skills',
            'Maintain health and wellbeing'
          ],
          interventions: [
            'Medication management',
            'Support worker assistance',
            'Occupational therapy',
            'Speech therapy',
            'Behavioral support'
          ],
          assessments: {
            functional: { 
              score: Math.floor(Math.random() * 50) + 30, 
              date: new Date(2025, 6, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0], 
              tool: 'NDIS Functional Assessment' 
            },
            behavioral: { 
              score: Math.floor(Math.random() * 40) + 20, 
              date: new Date(2025, 6, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0], 
              tool: 'Behavior Support Assessment' 
            }
          },
          reviewDate: new Date(2026, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
          planType: 'NDIS Support Plan',
          planNumber: `NDIS-PLAN-${String(currentParticipantId).padStart(6, '0')}`
        },
        
        lastAssessment: new Date(2025, 6, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0],
        nextAssessment: new Date(2026, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
        // Remove aged care specific fields - NDIS participants don't have these
        // fallRisk: ['Low', 'Medium'][Math.floor(Math.random() * 2)], // REMOVED - Aged Care only
        // cognitiveScore: Math.floor(Math.random() * 30) + 15, // REMOVED - Aged Care only
        behavioralNotes: 'Participant is cooperative and engaged in support activities',
        monthlyMedicationCost: Math.floor(Math.random() * 300) + 100,
        yearToDateCost: Math.floor(Math.random() * 2000) + 1000,
        
        familyUpdates: [
          {
            date: new Date(2025, 6, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0],
            type: ['activity', 'health', 'medication'][Math.floor(Math.random() * 3)],
            message: 'Participant engaged well in activities today',
            read: Math.random() > 0.5
          }
        ],
        
        // NDIS participants do NOT have advanceCareDirective or resuscitationStatus - these are aged care only
        
        familyMembers: [
          {
            id: `FM${String(currentParticipantId).padStart(3, '0')}`,
            name: `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastName}`,
            relationship: ['Parent', 'Guardian', 'Sibling', 'Support Person'][Math.floor(Math.random() * 4)],
            phone: `04${Math.floor(Math.random() * 90000000) + 10000000}`,
            email: `${firstNames[Math.floor(Math.random() * firstNames.length)].toLowerCase()}.${lastName.toLowerCase()}@email.com`,
            isPrimaryContact: true,
            notificationPreferences: ['medication_changes', 'health_updates', 'activities', 'incidents'],
            lastVisit: new Date(2025, 6, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0],
            nextVisit: new Date(2025, 7, Math.floor(Math.random() * 24) + 1).toISOString().split('T')[0],
            accessLevel: 'Full'
          }
        ]
      };
      
      ndisParticipants.push(participant);
    }
  });
  
  return ndisParticipants;
};

// Add NDIS participants to the residents array
residents.push(...generateNDISParticipants());

// ============================================================================
// CARE MINUTES & AN-ACC DATA GENERATION
// Generate comprehensive care minutes for ALL aged care residents
// ============================================================================

const generateComprehensiveCareMinutes = () => {
  const careMinutes = [];
  const today = new Date();
  const dates = [];
  
  // Generate dates for last 14 days (2 weeks of comprehensive data)
  for (let i = 0; i < 14; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dates.push(date.toISOString().split('T')[0]);
  }
  
  // Helper function to determine AN-ACC class based on resident characteristics
  const determineANACCClass = (resident) => {
    // If already has AN-ACC class, use it
    if (resident.anaccClass) {
      return resident.anaccClass;
    }
    
    const careLevel = resident.careLevel || 'Medium';
    const cognitiveStatus = resident.cognitiveStatus || 'Normal';
    const mobility = resident.mobility || 'Independent';
    const hasComplexConditions = resident.primaryDiagnosis?.some(d => 
      ['Heart Failure', 'COPD', 'Parkinson\'s Disease', 'Stroke Recovery', 'Cancer', 'Advanced Alzheimer\'s Disease'].includes(d)
    ) || false;
    
    let baseClass = 1;
    
    // Care level impact
    if (careLevel === 'Critical') baseClass += 4;
    else if (careLevel === 'High') baseClass += 2;
    else if (careLevel === 'Medium') baseClass += 1;
    
    // Cognitive status impact
    if (cognitiveStatus === 'Severe Dementia') baseClass += 3;
    else if (cognitiveStatus === 'Moderate Dementia') baseClass += 2;
    else if (cognitiveStatus === 'Mild Dementia') baseClass += 1;
    
    // Mobility impact
    if (mobility === 'Wheelchair' || mobility?.includes('Wheelchair')) baseClass += 2;
    else if (mobility?.includes('Walker') || mobility?.includes('walker')) baseClass += 1;
    
    // Complex conditions impact
    if (hasComplexConditions) baseClass += 1;
    
    // Cap between 1-13
    return Math.min(Math.max(baseClass, 1), 13);
  };
  
  // Helper function to calculate target minutes based on AN-ACC class
  const calculateTargetMinutes = (anaccClass) => {
    if (anaccClass >= 9 && anaccClass <= 13) return 235;
    if (anaccClass >= 6 && anaccClass <= 8) return 225;
    return 215;
  };
  
  // Get all aged care residents (exclude NDIS)
  const agedCareResidents = residents.filter(r => 
    r && 
    r.id && 
    r.facility && 
    r.facility !== 'ndis_sunshine_support' && 
    !r.facility?.startsWith('ndis_')
  );
  
  console.log(`[Care Minutes] Generating data for ${agedCareResidents.length} residents across ${dates.length} days...`);
  
  // Generate care minutes for each resident for each date
  agedCareResidents.forEach((resident) => {
    const anaccClass = determineANACCClass(resident);
    const targetMinutes = calculateTargetMinutes(anaccClass);
    const targetRNMinutes = 44;
    
    // Add AN-ACC class to resident
    resident.anaccClass = anaccClass;
    
    dates.forEach((date) => {
      // Create deterministic variation based on resident ID and date for consistency
      const seed = (resident.id || 0) * 1000 + new Date(date).getTime();
      const random = (seed) => {
        const x = Math.sin(seed) * 10000;
        return x - Math.floor(x);
      };
      const variation = Math.floor((random(seed) - 0.5) * 30); // -15 to +15 minutes
      
      // Base minutes based on AN-ACC class
      let baseRN = 40;
      let baseEN = 25;
      let basePCA = 100;
      let baseAllied = 15;
      
      if (anaccClass >= 9) {
        baseRN = 55;
        baseEN = 40;
        basePCA = 140;
        baseAllied = 30;
      } else if (anaccClass >= 6) {
        baseRN = 45;
        baseEN = 30;
        basePCA = 120;
        baseAllied = 20;
      }
      
      // Apply variation
      const rnMinutes = Math.max(30, baseRN + Math.floor(variation * 0.3));
      const enMinutes = Math.max(15, baseEN + Math.floor(variation * 0.2));
      const pcaMinutes = Math.max(80, basePCA + Math.floor(variation * 0.5));
      const alliedMinutes = Math.max(10, baseAllied + Math.floor(variation * 0.1));
      
      const totalMinutes = rnMinutes + enMinutes + pcaMinutes + alliedMinutes;
      const rnMinutesActual = rnMinutes;
      
      // ENSURE TODAY IS ALWAYS COMPLIANT for investor demo
      const isToday = date === today;
      let compliant, rnCompliant, complianceGap, rnComplianceGap;
      
      if (isToday) {
        // Force compliance for today's data
        compliant = true;
        rnCompliant = true;
        complianceGap = 0;
        rnComplianceGap = 0;
      } else {
        // Historical data can have realistic variation (~80% compliance)
        const complianceChance = random(seed * 2);
        compliant = complianceChance > 0.2 ? totalMinutes >= targetMinutes : false;
        rnCompliant = complianceChance > 0.15 ? rnMinutesActual >= targetRNMinutes : false;
        complianceGap = compliant ? 0 : Math.max(0, targetMinutes - totalMinutes);
        rnComplianceGap = rnCompliant ? 0 : Math.max(0, targetRNMinutes - rnMinutesActual);
      }
      
      // Generate one record per day (morning shift)
      const shift = 'morning';
      
      careMinutes.push({
        id: `cm-${resident.id}-${date}-${shift}`,
        residentId: resident.id,
        facilityId: resident.facility,
        wardId: resident.ward,
        date: date,
        shift: shift,
        minutes: {
          rn: rnMinutes,
          en: enMinutes,
          pca: pcaMinutes,
          allied: alliedMinutes,
          other: 0
        },
        totalMinutes: totalMinutes,
        rnMinutes: rnMinutesActual,
        targetMinutes: targetMinutes,
        targetRNMinutes: targetRNMinutes,
        anaccClass: anaccClass,
        anaccAdjustedTarget: targetMinutes,
        compliant: compliant,
        rnCompliant: rnCompliant,
        complianceGap: complianceGap,
        rnComplianceGap: rnComplianceGap,
        source: 'roster',
        calculatedFromRoster: true,
        recordedAt: `${date}T08:00:00Z`
      });
    });
  });
  
  console.log(`[Care Minutes] Generated ${careMinutes.length} care minutes records`);
  return careMinutes;
};

// Generate comprehensive care minutes data for ALL residents
const comprehensiveCareMinutes = generateComprehensiveCareMinutes();

// ============================================================================
// ENHANCE RESIDENTS WITH COMPLIANCE DATA
// Add compliance fields (AN-ACC, care minutes, reassessment) to resident profiles
// ============================================================================

const enhanceResidentsWithCompliance = () => {
  const today = new Date().toISOString().split('T')[0];
  const thisMonth = new Date().getMonth() + 1;
  const thisYear = new Date().getFullYear();
  
  // Get today's care minutes for each resident
  const todayCareMinutes = comprehensiveCareMinutes.filter(cm => cm.date === today);
  
  // Calculate monthly summaries
  const monthlyCareMinutes = comprehensiveCareMinutes.filter(cm => {
    const cmDate = new Date(cm.date);
    return cmDate.getMonth() + 1 === thisMonth && cmDate.getFullYear() === thisYear;
  });
  
  residents.forEach(resident => {
    // Skip NDIS participants
    if (resident.facility?.startsWith('ndis_') || resident.facility === 'ndis_sunshine_support') {
      return;
    }
    
    // Get today's care minutes for this resident
    const residentTodayCM = todayCareMinutes.find(cm => cm.residentId === resident.id);
    
    // Get monthly care minutes for this resident
    const residentMonthlyCM = monthlyCareMinutes.filter(cm => cm.residentId === resident.id);
    
    // Calculate monthly summary
    let monthlySummary = null;
    if (residentMonthlyCM.length > 0) {
      const totalMinutes = residentMonthlyCM.reduce((sum, cm) => sum + (cm.totalMinutes || 0), 0);
      const totalRNMinutes = residentMonthlyCM.reduce((sum, cm) => sum + (cm.rnMinutes || 0), 0);
      const compliantDays = residentMonthlyCM.filter(cm => cm.compliant).length;
      const rnCompliantDays = residentMonthlyCM.filter(cm => cm.rnCompliant).length;
      const fullyCompliantDays = residentMonthlyCM.filter(cm => cm.compliant && cm.rnCompliant).length;
      
      monthlySummary = {
        month: thisMonth,
        year: thisYear,
        averageMinutes: totalMinutes / residentMonthlyCM.length,
        averageRNMinutes: totalRNMinutes / residentMonthlyCM.length,
        compliantDays,
        rnCompliantDays,
        fullyCompliantDays,
        complianceRate: (fullyCompliantDays / residentMonthlyCM.length) * 100,
        totalDays: residentMonthlyCM.length
      };
    }
    
    // Enhance resident with compliance data
    resident.anacc = {
      class: resident.anaccClass || null,
      classDescription: resident.anaccClass ? `Class ${resident.anaccClass}` : null,
      assessmentDate: null,
      lastAssessmentDate: null,
      nextAssessmentDate: null,
      assessmentType: 'shadow',
      rugAdlTotal: null,
      rockwoodFrailtyScale: null,
      bradenScale: null,
      demmiScore: null,
      technicalNursing: {
        oxygenTherapy: false,
        enteralFeeding: false,
        complexWoundManagement: false
      },
      projectedDailySubsidy: null,
      nwcWeighting: null,
      officialClassification: null,
      officialClassificationDate: null
    };
    
    resident.careMinutes = {
      current: residentTodayCM ? {
        date: residentTodayCM.date,
        totalMinutes: residentTodayCM.totalMinutes || 0,
        rnMinutes: residentTodayCM.rnMinutes || 0,
        targetMinutes: residentTodayCM.targetMinutes || 215,
        targetRNMinutes: residentTodayCM.targetRNMinutes || 44,
        anaccAdjustedTarget: residentTodayCM.anaccAdjustedTarget || residentTodayCM.targetMinutes || 215,
        compliant: residentTodayCM.compliant ?? false,
        rnCompliant: residentTodayCM.rnCompliant ?? false,
        complianceGap: residentTodayCM.complianceGap || 0,
        rnComplianceGap: residentTodayCM.rnComplianceGap || 0
      } : {
        date: today,
        totalMinutes: 0,
        rnMinutes: 0,
        targetMinutes: 215,
        targetRNMinutes: 44,
        anaccAdjustedTarget: 215,
        compliant: false,
        rnCompliant: false,
        complianceGap: 0,
        rnComplianceGap: 0
      },
      monthly: monthlySummary || {
        month: null,
        year: null,
        averageMinutes: 0,
        averageRNMinutes: 0,
        compliantDays: 0,
        rnCompliantDays: 0,
        fullyCompliantDays: 0,
        complianceRate: 0,
        totalDays: 0
      },
      quarterly: {
        quarter: null,
        year: null,
        averageMinutes: 0,
        averageRNMinutes: 0,
        compliantDays: 0,
        rnCompliantDays: 0,
        fullyCompliantDays: 0,
        complianceRate: 0,
        totalDays: 0
      },
      lastUpdated: new Date().toISOString()
    };
    
    // Add reassessment schedule (simplified - would be calculated by ANACCReassessmentService)
    const anaccClass = resident.anaccClass || 1;
    const reassessmentMonths = anaccClass >= 9 ? 6 : 12;
    const nextReassessment = new Date();
    nextReassessment.setMonth(nextReassessment.getMonth() + reassessmentMonths);
    
    resident.reassessmentSchedule = {
      anaccReassessmentDue: nextReassessment.toISOString().split('T')[0],
      anaccReassessmentEligible: false,
      anaccReassessmentOverdue: false,
      lastReassessmentDate: null,
      reassessmentType: anaccClass >= 9 ? '6_month' : '12_month',
      reminders: []
    };
    
    // Star ratings (facility-level, but placeholder per resident)
    resident.starRatings = {
      overallRating: null,
      experienceScore: null,
      complianceScore: null,
      staffingScore: null,
      qualityScore: null,
      lastAssessmentDate: null,
      nextAssessmentDate: null
    };
  });
  
  console.log(`[Resident Enhancement] Enhanced ${residents.length} residents with compliance data`);
};

// Enhance all residents with compliance data
enhanceResidentsWithCompliance();

export const medicationAlerts = [
  { 
    id: '1', 
    type: 'critical', 
    priority: 'High', 
    facility: 'sunshine',
    resident: 'Margaret Thompson', 
    ward: 'Ward A', 
    room: 'A101',
    medication: 'Metformin 500mg', 
    issue: 'Prescription expires in 1 day', 
    action: 'Contact Dr. Smith immediately', 
    timestamp: '2025-01-24 09:30', 
    status: 'Active', 
    escalated: false, 
    automatedWorkflow: true, 
    workflowStatus: 'Doctor notified via ePrescribe system' 
  },
  { 
    id: '2', 
    type: 'safety', 
    priority: 'High', 
    facility: 'sunshine',
    resident: 'Dorothy Williams', 
    ward: 'Ward D', 
    room: 'D205',
    medication: 'Morphine 5mg', 
    issue: 'Missed dose - 6 hours overdue', 
    action: 'Administer immediately, check pain levels', 
    timestamp: '2025-01-24 10:15', 
    status: 'Active', 
    escalated: true, 
    automatedWorkflow: false, 
    workflowStatus: 'Manual intervention required' 
  },
  {
    id: '3',
    type: 'interaction',
    priority: 'High',
    facility: 'sunshine',
    resident: 'John Smith',
    ward: 'Ward B',
    room: 'B203',
    medication: 'Warfarin 5mg',
    issue: 'Drug interaction detected with newly prescribed Aspirin',
    action: 'Review medication chart, consult pharmacist',
    timestamp: '2025-01-24 11:00',
    status: 'Active',
    escalated: false,
    automatedWorkflow: true,
    workflowStatus: 'Pharmacist review scheduled'
  },
  {
    id: '4',
    type: 'supply',
    priority: 'Medium',
    facility: 'sunshine',
    resident: 'Mary Johnson',
    ward: 'Ward C',
    room: 'C105',
    medication: 'Insulin Glargine',
    issue: 'Low stock - only 3 days supply remaining',
    action: 'Order from pharmacy, expedite delivery',
    timestamp: '2025-01-24 08:45',
    status: 'Active',
    escalated: false,
    automatedWorkflow: true,
    workflowStatus: 'Purchase order generated automatically'
  },
  {
    id: '5',
    type: 'compliance',
    priority: 'Medium',
    facility: 'sunshine',
    resident: 'Robert Brown',
    ward: 'Ward A',
    room: 'A205',
    medication: 'Atorvastatin 20mg',
    issue: 'Medication not administered as scheduled - 3 missed doses this week',
    action: 'Review adherence, discuss with resident and family',
    timestamp: '2025-01-24 14:20',
    status: 'Active',
    escalated: false,
    automatedWorkflow: false,
    workflowStatus: 'Care plan review required'
  },
  {
    id: '6',
    type: 'incident',
    priority: 'High',
    facility: 'sunshine',
    resident: 'Alice Davis',
    ward: 'Ward D',
    room: 'D301',
    medication: 'Digoxin 0.25mg',
    issue: 'Adverse reaction reported - nausea and dizziness',
    action: 'Discontinue medication, monitor vitals, notify doctor',
    timestamp: '2025-01-24 13:15',
    status: 'Active',
    escalated: true,
    automatedWorkflow: true,
    workflowStatus: 'Doctor notified, incident report filed'
  },
  {
    id: '7',
    type: 'safety',
    priority: 'Medium',
    facility: 'sunshine',
    resident: 'William Wilson',
    ward: 'Ward B',
    room: 'B108',
    medication: 'Furosemide 40mg',
    issue: 'Blood pressure drop detected after administration',
    action: 'Monitor blood pressure, adjust dosage if needed',
    timestamp: '2025-01-24 12:30',
    status: 'Active',
    escalated: false,
    automatedWorkflow: true,
    workflowStatus: 'Vital signs monitoring activated'
  },
  {
    id: '8',
    type: 'critical',
    priority: 'High',
    facility: 'sunshine',
    resident: 'Elizabeth Taylor',
    ward: 'Ward C',
    room: 'C208',
    medication: 'Metoprolol 50mg',
    issue: 'Allergy alert - patient has known allergy to beta-blockers',
    action: 'STOP medication immediately, contact doctor for alternative',
    timestamp: '2025-01-24 15:45',
    status: 'Active',
    escalated: true,
    automatedWorkflow: true,
    workflowStatus: 'Emergency protocol activated, doctor paged'
  },
  {
    id: '9',
    type: 'supply',
    priority: 'Low',
    facility: 'sunshine',
    resident: 'Thomas Anderson',
    ward: 'Ward A',
    room: 'A312',
    medication: 'Omeprazole 20mg',
    issue: 'Stock level below reorder point',
    action: 'Add to next pharmacy order',
    timestamp: '2025-01-24 07:00',
    status: 'Resolved',
    escalated: false,
    automatedWorkflow: true,
    workflowStatus: 'Added to weekly order list'
  },
  {
    id: '10',
    type: 'compliance',
    priority: 'Low',
    facility: 'sunshine',
    resident: 'Patricia Martinez',
    ward: 'Ward D',
    room: 'D115',
    medication: 'Levothyroxine 75mcg',
    issue: 'Medication administration documentation incomplete',
    action: 'Complete documentation, verify administration',
    timestamp: '2025-01-23 16:20',
    status: 'Resolved',
    escalated: false,
    automatedWorkflow: false,
    workflowStatus: 'Documentation completed'
  }
];

// Pharmacy Orders (Bulk Ordering & Collaboration)
export const pharmacyOrders = [
  {
    id: 'ORD-1001',
    facility: 'sunshine',
    ward: 'Ward A',
    residentId: 1,
    residentName: 'Margaret Rose Thompson',
    pharmacy: 'MediCare Pharmacy - North',
    items: [
      { drug: 'Metformin 500mg', qty: 60, repeatsRemaining: 2, pbsEligible: true, authority: false },
      { drug: 'Donepezil 10mg', qty: 30, repeatsRemaining: 1, pbsEligible: true, authority: false }
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 18).toISOString(),
    status: 'Pending Approval', // Pending Approval → Sent → Accepted/Rejected → Packed → Dispatched → Delivered → Completed
    priority: 'High',
    slaHours: 24,
    notes: 'Prescription expiring in 2 days'
  },
  {
    id: 'ORD-1002',
    facility: 'bayview',
    ward: 'Ward C',
    residentId: 12,
    residentName: 'Thomas Nguyen',
    pharmacy: 'Community Care Pharmacy',
    items: [
      { drug: 'Atorvastatin 20mg', qty: 30, repeatsRemaining: 3, pbsEligible: true, authority: false }
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    status: 'Sent to Pharmacy',
    priority: 'Medium',
    slaHours: 48,
    notes: ''
  },
  {
    id: 'ORD-1003',
    facility: 'greenhill',
    ward: 'Ward B',
    residentId: 45,
    residentName: 'Margaret O\'Connor',
    pharmacy: 'MediCare Pharmacy - East',
    items: [
      { drug: 'Sertraline 50mg', qty: 30, repeatsRemaining: 0, pbsEligible: true, authority: false },
      { drug: 'Paracetamol 500mg', qty: 100, repeatsRemaining: 5, pbsEligible: false, authority: false }
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 30).toISOString(),
    status: 'Accepted',
    priority: 'Low',
    slaHours: 72,
    notes: 'Pack into sachets (DAA)'
  }
];

// Controlled Drug Register (S8)
export const s8RegisterEntries = [
  {
    id: 'S8-001',
    facility: 'sunshine',
    drug: 'Oxycodone 5mg',
    action: 'Administration', // Administration | Receipt | Destruction | Adjustment
    quantity: 2,
    balanceAfter: 18,
    residentId: 7,
    residentName: 'Mary Johnson',
    nurse: 'Nurse Sarah Mitchell',
    witness: 'Nurse Daniel Carter',
    timestamp: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
    notes: 'Administered as prescribed. Pain score reduced from 7 to 4.'
  },
  {
    id: 'S8-002',
    facility: 'bayview',
    drug: 'Morphine 10mg',
    action: 'Receipt',
    quantity: 50,
    balanceAfter: 150,
    residentId: null,
    residentName: null,
    nurse: 'Nurse Olivia Brown',
    witness: 'Pharmacist John Smith',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    notes: 'Delivery from pharmacy. Batch #MOR-09-25, Exp 09/2026'
  }
];

export const enquiries = [
  {
    id: 'ENQ001',
    date: '2025-07-24 09:30',
    name: 'Margaret Wilson',
    contact: {
      phone: '0412 345 678',
      email: 'margaret.wilson@email.com'
    },
    enquiryType: 'Permanent Placement',
    source: 'Website',
    urgency: 'High - needs placement within 2 weeks',
    careNeeds: ['Dementia care', 'Mobility assistance', 'Medication management'],
    status: 'Tour Scheduled',
    assignedTo: 'Sarah Mitchell',
    nextAction: 'Tour on 2025-07-26 at 10:00am',
    facility: 'sunshine'
  }
];

export const activitiesData = [
  {
    id: 'ACT001',
    name: 'Morning Tai Chi',
    type: 'Physical',
    date: '2025-07-25',
    time: '09:00',
    duration: 45,
    location: 'Garden Courtyard',
    facilitator: 'Master Li',
    capacity: 20,
    enrolled: 18,
    waitlist: 3,
    facility: 'sunshine'
  },
  {
    id: 'ACT002',
    name: 'Memory Lane Music',
    type: 'Cognitive',
    date: '2025-07-25',
    time: '14:00',
    duration: 60,
    location: 'Memory Care Lounge',
    facilitator: 'Music Therapist Sarah',
    capacity: 15,
    enrolled: 15,
    waitlist: 5,
    facility: 'sunshine'
  }
];

export const staffData = [
  {
    id: 'STAFF001',
    name: 'Sarah Mitchell',
    role: 'Facility Manager',
    facility: 'sunshine',
    photo: '👩‍⚕️',
    qualifications: ['RN', 'MBA Healthcare'],
    schedule: {
      today: '8:00-17:00',
      tomorrow: '8:00-17:00'
    },
    performance: {
      rating: 4.8,
      lastReview: '2025-06-15'
    }
  }
];

export const qualityMetrics = {
  falls: { current: 2.1, target: 3.0, trend: -15, benchmark: 3.5, improvement: '15% reduction from last month' },
  pressureInjuries: { current: 0.8, target: 1.0, trend: -20, benchmark: 1.2, improvement: '20% reduction from last month' },
  medicationErrors: { current: 0.5, target: 1.0, trend: -25, benchmark: 1.5, improvement: '25% reduction from last month' },
  infections: { current: 3.2, target: 4.0, trend: -8, benchmark: 4.5, improvement: '8% reduction from last month' }
};

export const automatedWorkflows = [
  { 
    id: 'WF001', 
    name: 'Prescription Renewal Alert', 
    description: 'Auto-notify doctors 7 days before expiry', 
    timeSaved: '2 hrs/week', 
    errorReduction: '95%', 
    status: 'Active' 
  },
  { 
    id: 'WF002', 
    name: 'Family Update Notifications', 
    description: 'Auto-send health updates to families', 
    timeSaved: '3 hrs/week', 
    errorReduction: '98%', 
    status: 'Active' 
  }
];

export const pharmacistOrders = [
  { 
    id: 'PO001', 
    facility: 'Sunshine Gardens', 
    orderDate: '2025-07-24', 
    status: 'pending', 
    priority: 'high', 
    totalValue: 1234.50, 
    residentCount: 5 
  },
  { 
    id: 'PO002', 
    facility: 'Bayview Gardens', 
    orderDate: '2025-07-23', 
    status: 'accepted', 
    priority: 'medium', 
    totalValue: 856.75, 
    residentCount: 3 
  }
];

export const aiSuggestions = [
  {
    id: 'AI001',
    type: 'optimization',
    title: 'Roster optimization opportunity',
    description: 'AI detected overstaffing on Tuesday afternoons',
    savings: '$2,400/month',
    confidence: 92
  },
  {
    id: 'AI002',
    type: 'risk',
    title: 'Fall risk pattern detected',
    description: 'Increased fall risk in Ward A between 6-7 PM',
    confidence: 87
  }
];

export const facilityStats = {
  all: {
    occupancy: 92.8,
    incidents: 3,
    medicationAdherence: 95.5,
    activitiesParticipation: 83.5
  },
  sunshine: {
    occupancy: 94.5,
    incidents: 2,
    medicationAdherence: 96.2,
    activitiesParticipation: 85
  },
  bayview: {
    occupancy: 91.3,
    incidents: 1,
    medicationAdherence: 94.8,
    activitiesParticipation: 82
  },
  greenhill: {
    occupancy: 88.0,
    incidents: 0,
    medicationAdherence: 93.5,
    activitiesParticipation: 80
  },
  rosewood: {
    occupancy: 92.0,
    incidents: 1,
    medicationAdherence: 95.0,
    activitiesParticipation: 84
  },
  ndis_sunshine_support: {
    occupancy: 89.0,
    incidents: 0,
    medicationAdherence: 96.8,
    activitiesParticipation: 88
  },
  ndis_bayview_community: {
    occupancy: 92.0,
    incidents: 0,
    medicationAdherence: 97.0,
    activitiesParticipation: 90
  },
  ndis_house_1: {
    occupancy: 100.0,
    incidents: 0,
    medicationAdherence: 99.1,
    activitiesParticipation: 95
  },
  ndis_house_2: {
    occupancy: 93.0,
    incidents: 0,
    medicationAdherence: 97.3,
    activitiesParticipation: 92
  },
  ndis_house_3: {
    occupancy: 94.0,
    incidents: 0,
    medicationAdherence: 96.7,
    activitiesParticipation: 89
  }
};

export const noteTemplates = {
  progress: { title: 'Progress Note', fields: ['subjective', 'objective', 'assessment', 'plan'] },
  consultation: { title: 'Consultation Note', fields: ['reason', 'history', 'examination', 'diagnosis', 'plan'] },
  medication: { title: 'Medication Review', fields: ['current_medications', 'effectiveness', 'side_effects', 'changes', 'monitoring'] },
  incident: { title: 'Incident Report', fields: ['incident_type', 'description', 'immediate_action', 'injuries', 'follow_up'] },
  discharge: { title: 'Discharge Summary', fields: ['admission_reason', 'hospital_course', 'discharge_diagnosis', 'discharge_medications', 'follow_up_instructions'] },
  palliative: { title: 'Palliative Care Review', fields: ['comfort_assessment', 'pain_management', 'family_communication', 'goals_of_care', 'plan'] }
};

// Quality Management Data
export const qualityReports = [
  {
    id: 'QR001',
    title: 'Monthly Quality Assessment - January 2024',
    facility: 'Sunshine Gardens',
    date: '2024-01-31',
    status: 'Passed',
    score: 94.5,
    assessor: 'Sarah Mitchell',
    category: 'Comprehensive',
    metrics: {
      occupancy: 94.5,
      incidents: 2,
      medicationAdherence: 96.2,
      activitiesParticipation: 85,
      staffSatisfaction: 87,
      familySatisfaction: 92,
      infectionRate: 0.8,
      fallRate: 2.1
    },
    findings: [
      'Medication administration protocols followed correctly',
      'Infection control measures properly implemented',
      'Resident care plans up to date',
      'Staff training requirements met'
    ],
    recommendations: [
      'Increase staff training on fall prevention',
      'Implement new medication verification system',
      'Enhance family communication protocols'
    ],
    nextReview: '2024-02-28'
  },
  {
    id: 'QR002',
    title: 'Incident Report Analysis',
    facility: 'Bayview Gardens',
    date: '2025-07-22',
    status: 'Under Review',
    score: 95.2,
    assessor: 'Michael Chen',
    category: 'Incident',
    findings: [
      'Two minor falls reported this month',
      'No serious injuries sustained',
      'Preventive measures implemented',
      'Staff response time within standards'
    ],
    recommendations: [
      'Install additional handrails in common areas',
      'Review resident mobility assessments'
    ],
    nextReview: '2025-08-22'
  },
  {
    id: 'QR003',
    title: 'Medication Safety Audit',
    facility: 'Green Hill Residence',
    date: '2025-07-20',
    status: 'Passed',
    score: 99.1,
    assessor: 'Dr. Emily Wilson',
    category: 'Medication',
    findings: [
      '100% medication administration accuracy',
      'All prescriptions properly documented',
      'Drug interaction checks performed',
      'Storage conditions meet standards'
    ],
    recommendations: [
      'Continue current medication protocols',
      'Maintain high standards of documentation'
    ],
    nextReview: '2025-08-20'
  },
  {
    id: 'QR004',
    title: 'Infection Control Assessment',
    facility: 'Rosewood Care Community',
    date: '2025-07-18',
    status: 'Passed',
    score: 97.8,
    assessor: 'Lisa Rodriguez',
    category: 'Infection Control',
    findings: [
      'Hand hygiene compliance at 98%',
      'Personal protective equipment properly used',
      'Cleaning protocols followed correctly',
      'No outbreaks reported'
    ],
    recommendations: [
      'Continue staff education on hand hygiene',
      'Monitor cleaning schedule adherence'
    ],
    nextReview: '2025-08-18'
  },
  {
    id: 'QR005',
    title: 'Resident Satisfaction Survey',
    facility: 'Sunshine Gardens',
    date: '2025-07-15',
    status: 'Completed',
    score: 96.3,
    assessor: 'Quality Team',
    category: 'Satisfaction',
    findings: [
      '94% of residents satisfied with care',
      'Family communication rated highly',
      'Activities participation increased',
      'Food quality well received'
    ],
    recommendations: [
      'Increase variety of activities offered',
      'Enhance communication with families'
    ],
    nextReview: '2025-08-15'
  }
];

export const qualityIncidents = [
  {
    id: 'QI001',
    type: 'Fall',
    severity: 'Medium',
    facility: 'Sunshine Gardens',
    date: '2024-01-20',
    resident: 'Margaret Johnson',
    description: 'Resident fell while transferring from wheelchair to toilet. No injury sustained.',
    immediateAction: 'Assessed for injuries, none found',
    followUp: 'Increased supervision during transfers, installed grab bars',
    status: 'Resolved',
    staffInvolved: 'Nurse Sarah, Care Worker Mike',
    preventionMeasures: [
      'Additional staff training on safe transfers',
      'Installation of additional safety equipment',
      'Review of care plan'
    ]
  },
  {
    id: 'QI002',
    type: 'Medication Error',
    severity: 'Near Miss',
    facility: 'Bayview Gardens',
    date: '2025-07-21',
    resident: 'Robert Smith',
    description: 'Wrong medication prepared but caught before administration',
    immediateAction: 'Correct medication administered',
    followUp: 'Additional training for medication preparation',
    status: 'Resolved'
  },
  {
    id: 'QI003',
    type: 'Infection Control',
    severity: 'Low',
    facility: 'Green Hill Residence',
    date: '2025-07-19',
    resident: 'Multiple',
    description: 'Minor outbreak of common cold',
    immediateAction: 'Isolation protocols implemented',
    followUp: 'Enhanced cleaning procedures',
    status: 'Resolved'
  }
];

// Roster Management Data
export const staffRoster = [
  {
    id: 'SR001',
    staffId: 'ST001',
    name: 'Sarah Johnson',
    role: 'Registered Nurse',
    facility: 'Sunshine Gardens',
    ward: 'Memory Care',
    shift: 'Morning',
    startTime: '07:00',
    endTime: '15:00',
    date: '2025-07-25',
    status: 'Confirmed',
    notes: 'Medication round specialist'
  },
  {
    id: 'SR002',
    staffId: 'ST002',
    name: 'Michael Chen',
    role: 'Care Worker',
    facility: 'Sunshine Gardens',
    ward: 'General Care',
    shift: 'Afternoon',
    startTime: '15:00',
    endTime: '23:00',
    date: '2025-07-25',
    status: 'Confirmed',
    notes: 'Activities coordinator'
  },
  {
    id: 'SR003',
    staffId: 'ST003',
    name: 'Emily Wilson',
    role: 'Registered Nurse',
    facility: 'Bayview Gardens',
    ward: 'Assisted Living',
    shift: 'Night',
    startTime: '23:00',
    endTime: '07:00',
    date: '2025-07-25',
    status: 'Confirmed',
    notes: 'Night shift supervisor'
  },
  {
    id: 'SR004',
    staffId: 'ST004',
    name: 'David Brown',
    role: 'Care Worker',
    facility: 'Green Hill Residence',
    ward: 'General Care',
    shift: 'Morning',
    startTime: '07:00',
    endTime: '15:00',
    date: '2025-07-25',
    status: 'Confirmed',
    notes: 'Physical therapy assistant'
  },
  {
    id: 'SR005',
    staffId: 'ST005',
    name: 'Lisa Rodriguez',
    role: 'Registered Nurse',
    facility: 'Rosewood Care Community',
    ward: 'Palliative Care',
    shift: 'Afternoon',
    startTime: '15:00',
    endTime: '23:00',
    date: '2025-07-25',
    status: 'Confirmed',
    notes: 'Palliative care specialist'
  },
  {
    id: 'SR006',
    staffId: 'ST006',
    name: 'James Wilson',
    role: 'Care Worker',
    facility: 'Sunshine Gardens',
    ward: 'Memory Care',
    shift: 'Night',
    startTime: '23:00',
    endTime: '07:00',
    date: '2025-07-25',
    status: 'Confirmed',
    notes: 'Behavioral support specialist'
  },
  {
    id: 'SR007',
    staffId: 'ST007',
    name: 'Maria Garcia',
    role: 'Registered Nurse',
    facility: 'Bayview Gardens',
    ward: 'General Care',
    shift: 'Morning',
    startTime: '07:00',
    endTime: '15:00',
    date: '2025-07-25',
    status: 'Confirmed',
    notes: 'Wound care specialist'
  },
  {
    id: 'SR008',
    staffId: 'ST008',
    name: 'Robert Smith',
    role: 'Care Worker',
    facility: 'Green Hill Residence',
    ward: 'Assisted Living',
    shift: 'Afternoon',
    startTime: '15:00',
    endTime: '23:00',
    date: '2025-07-25',
    status: 'Confirmed',
    notes: 'Recreation therapy assistant'
  }
];

/**
 * QS 8 — workforce credential visibility (demo).
 * Production: HRIS / AHPRA verification APIs; not authoritative for regulatory audits.
 */
export const staffCredentialRecords = [
  {
    staffId: 'ST001',
    name: 'Sarah Johnson',
    role: 'Registered Nurse',
    ahpraRegistrationId: 'NMW0001234567',
    ahpraExpiry: '2027-05-31',
    wwccExpiry: '2026-12-01',
    policeCheckExpiry: '2026-09-15',
    mandatoryTrainingExpiry: '2026-08-30',
    influenzaVaccineDue: '2026-05-01',
    status: 'compliant',
  },
  {
    staffId: 'ST002',
    name: 'Michael Chen',
    role: 'Care Worker',
    ahpraRegistrationId: '—',
    ahpraExpiry: null,
    wwccExpiry: '2026-04-20',
    policeCheckExpiry: '2026-11-01',
    mandatoryTrainingExpiry: '2026-03-01',
    influenzaVaccineDue: '2026-05-01',
    status: 'warning',
  },
  {
    staffId: 'ST003',
    name: 'Emily Wilson',
    role: 'Registered Nurse',
    ahpraRegistrationId: 'NMW0002233445',
    ahpraExpiry: '2027-01-15',
    wwccExpiry: '2027-02-28',
    policeCheckExpiry: '2026-06-10',
    mandatoryTrainingExpiry: '2026-10-15',
    influenzaVaccineDue: '2026-05-01',
    status: 'compliant',
  },
  {
    staffId: 'ST004',
    name: 'David Brown',
    role: 'Care Worker',
    ahpraRegistrationId: '—',
    ahpraExpiry: null,
    wwccExpiry: '2025-12-01',
    policeCheckExpiry: '2026-02-01',
    mandatoryTrainingExpiry: '2026-07-01',
    influenzaVaccineDue: '2026-05-01',
    status: 'expired',
  },
  {
    staffId: 'ST005',
    name: 'Lisa Rodriguez',
    role: 'Registered Nurse',
    ahpraRegistrationId: 'NMW0003344556',
    ahpraExpiry: '2026-09-30',
    wwccExpiry: '2026-08-15',
    policeCheckExpiry: '2026-12-20',
    mandatoryTrainingExpiry: '2026-09-01',
    influenzaVaccineDue: '2026-05-01',
    status: 'warning',
  },
  {
    staffId: 'ST006',
    name: 'James Taylor',
    role: 'Enrolled Nurse',
    ahpraRegistrationId: 'NMW0004455667',
    ahpraExpiry: '2027-03-01',
    wwccExpiry: '2027-01-10',
    policeCheckExpiry: '2026-10-05',
    mandatoryTrainingExpiry: '2026-11-20',
    influenzaVaccineDue: '2026-05-01',
    status: 'compliant',
  },
  {
    staffId: 'ST007',
    name: 'Maria Garcia',
    role: 'Registered Nurse',
    ahpraRegistrationId: 'NMW0005566778',
    ahpraExpiry: '2027-06-30',
    wwccExpiry: '2026-11-25',
    policeCheckExpiry: '2026-07-18',
    mandatoryTrainingExpiry: '2026-12-05',
    influenzaVaccineDue: '2026-05-01',
    status: 'compliant',
  },
  {
    staffId: 'ST008',
    name: 'Robert Smith',
    role: 'Care Worker',
    ahpraRegistrationId: '—',
    ahpraExpiry: null,
    wwccExpiry: '2026-05-30',
    policeCheckExpiry: '2026-04-12',
    mandatoryTrainingExpiry: '2026-06-15',
    influenzaVaccineDue: '2026-05-01',
    status: 'compliant',
  },
];

export const shiftTypes = [
  { id: 'morning', name: 'Morning', startTime: '07:00', endTime: '15:00', color: 'bg-blue-100' },
  { id: 'afternoon', name: 'Afternoon', startTime: '15:00', endTime: '23:00', color: 'bg-green-100' },
  { id: 'night', name: 'Night', startTime: '23:00', endTime: '07:00', color: 'bg-purple-100' }
];

export const rosterStats = {
  totalStaff: 45,
  onDuty: 32,
  offDuty: 13,
  overtime: 8,
  vacancies: 2,
  coverage: 94.2
};

// Staffing Requirements by Ward and Shift
export const staffingRequirements = [
  {
    facility: 'Sunshine Gardens',
    ward: 'Memory Care',
    shift: 'Morning',
    required: {
      nurses: 3,
      careWorkers: 4,
      total: 7
    },
    assigned: {
      nurses: 3,
      careWorkers: 3,
      total: 6
    },
    shortage: {
      nurses: 0,
      careWorkers: 1,
      total: 1
    },
    status: 'shortage'
  },
  {
    facility: 'Sunshine Gardens',
    ward: 'Memory Care',
    shift: 'Afternoon',
    required: {
      nurses: 2,
      careWorkers: 3,
      total: 5
    },
    assigned: {
      nurses: 2,
      careWorkers: 3,
      total: 5
    },
    shortage: {
      nurses: 0,
      careWorkers: 0,
      total: 0
    },
    status: 'adequate'
  },
  {
    facility: 'Sunshine Gardens',
    ward: 'Memory Care',
    shift: 'Night',
    required: {
      nurses: 1,
      careWorkers: 2,
      total: 3
    },
    assigned: {
      nurses: 1,
      careWorkers: 1,
      total: 2
    },
    shortage: {
      nurses: 0,
      careWorkers: 1,
      total: 1
    },
    status: 'shortage'
  },
  {
    facility: 'Sunshine Gardens',
    ward: 'General Care',
    shift: 'Morning',
    required: {
      nurses: 4,
      careWorkers: 5,
      total: 9
    },
    assigned: {
      nurses: 4,
      careWorkers: 5,
      total: 9
    },
    shortage: {
      nurses: 0,
      careWorkers: 0,
      total: 0
    },
    status: 'adequate'
  },
  {
    facility: 'Sunshine Gardens',
    ward: 'General Care',
    shift: 'Afternoon',
    required: {
      nurses: 3,
      careWorkers: 4,
      total: 7
    },
    assigned: {
      nurses: 2,
      careWorkers: 4,
      total: 6
    },
    shortage: {
      nurses: 1,
      careWorkers: 0,
      total: 1
    },
    status: 'shortage'
  },
  {
    facility: 'Sunshine Gardens',
    ward: 'General Care',
    shift: 'Night',
    required: {
      nurses: 2,
      careWorkers: 3,
      total: 5
    },
    assigned: {
      nurses: 2,
      careWorkers: 3,
      total: 5
    },
    shortage: {
      nurses: 0,
      careWorkers: 0,
      total: 0
    },
    status: 'adequate'
  },
  {
    facility: 'Bayview Gardens',
    ward: 'High Care',
    shift: 'Morning',
    required: {
      nurses: 5,
      careWorkers: 6,
      total: 11
    },
    assigned: {
      nurses: 4,
      careWorkers: 5,
      total: 9
    },
    shortage: {
      nurses: 1,
      careWorkers: 1,
      total: 2
    },
    status: 'critical'
  },
  {
    facility: 'Bayview Gardens',
    ward: 'High Care',
    shift: 'Afternoon',
    required: {
      nurses: 4,
      careWorkers: 5,
      total: 9
    },
    assigned: {
      nurses: 4,
      careWorkers: 5,
      total: 9
    },
    shortage: {
      nurses: 0,
      careWorkers: 0,
      total: 0
    },
    status: 'adequate'
  },
  {
    facility: 'Bayview Gardens',
    ward: 'High Care',
    shift: 'Night',
    required: {
      nurses: 3,
      careWorkers: 4,
      total: 7
    },
    assigned: {
      nurses: 2,
      careWorkers: 3,
      total: 5
    },
    shortage: {
      nurses: 1,
      careWorkers: 1,
      total: 2
    },
    status: 'critical'
  },
  {
    facility: 'Rosewood Care Community',
    ward: 'Palliative Care',
    shift: 'Morning',
    required: {
      nurses: 2,
      careWorkers: 3,
      total: 5
    },
    assigned: {
      nurses: 2,
      careWorkers: 3,
      total: 5
    },
    shortage: {
      nurses: 0,
      careWorkers: 0,
      total: 0
    },
    status: 'adequate'
  },
  {
    facility: 'Rosewood Care Community',
    ward: 'Palliative Care',
    shift: 'Afternoon',
    required: {
      nurses: 2,
      careWorkers: 3,
      total: 5
    },
    assigned: {
      nurses: 2,
      careWorkers: 2,
      total: 4
    },
    shortage: {
      nurses: 0,
      careWorkers: 1,
      total: 1
    },
    status: 'shortage'
  },
  {
    facility: 'Rosewood Care Community',
    ward: 'Palliative Care',
    shift: 'Night',
    required: {
      nurses: 1,
      careWorkers: 2,
      total: 3
    },
    assigned: {
      nurses: 1,
      careWorkers: 2,
      total: 3
    },
    shortage: {
      nurses: 0,
      careWorkers: 0,
      total: 0
    },
    status: 'adequate'
  }
];

// Billing Management Data
export const billingInvoices = [
  {
    id: 'INV001',
    residentId: 'R001',
    residentName: 'Margaret Johnson',
    facility: 'Sunshine Gardens',
    invoiceDate: '2024-01-25',
    dueDate: '2024-02-25',
    amount: 4560.00,
    status: 'Paid',
    paymentDate: '2024-01-26',
    paymentMethod: 'Direct Debit',
    items: [
      { description: 'Accommodation - Single Room', amount: 3200.00 },
      { description: 'Personal Care Services', amount: 800.00 },
      { description: 'Medication Management', amount: 300.00 },
      { description: 'Activities Program', amount: 260.00 }
    ],
    notes: 'Family satisfied with care quality',
    contactPerson: 'Sarah Johnson',
    contactPhone: '0412 345 678'
  },
  {
    id: 'INV002',
    residentId: 'R002',
    residentName: 'Robert Smith',
    facility: 'Bayview Gardens',
    invoiceDate: '2025-07-23',
    dueDate: '2025-08-23',
    amount: 3890.00,
    status: 'Paid',
    paymentDate: '2025-07-24',
    paymentMethod: 'Credit Card',
    items: [
      { description: 'Accommodation - July 2025', amount: 2800.00 },
      { description: 'Care Services', amount: 950.00 },
      { description: 'Medication Management', amount: 140.00 }
    ]
  },
  {
    id: 'INV003',
    residentId: 'R003',
    residentName: 'Elizabeth Brown',
    facility: 'Green Hill Residence',
    invoiceDate: '2025-07-22',
    dueDate: '2025-08-22',
    amount: 5230.00,
    status: 'Overdue',
    paymentDate: null,
    paymentMethod: null,
    items: [
      { description: 'Accommodation - July 2025', amount: 3500.00 },
      { description: 'Care Services', amount: 1500.00 },
      { description: 'Medication Management', amount: 230.00 }
    ]
  },
  {
    id: 'INV004',
    residentId: 'R004',
    residentName: 'William Davis',
    facility: 'Rosewood Care Community',
    invoiceDate: '2025-07-21',
    dueDate: '2025-08-21',
    amount: 4120.00,
    status: 'Pending',
    paymentDate: null,
    paymentMethod: null,
    items: [
      { description: 'Accommodation - July 2025', amount: 3000.00 },
      { description: 'Care Services', amount: 1000.00 },
      { description: 'Medication Management', amount: 120.00 }
    ]
  },
  {
    id: 'INV005',
    residentId: 'R005',
    residentName: 'Dorothy Wilson',
    facility: 'Sunshine Gardens',
    invoiceDate: '2025-07-20',
    dueDate: '2025-08-20',
    amount: 4780.00,
    status: 'Paid',
    paymentDate: '2025-07-22',
    paymentMethod: 'Bank Transfer',
    items: [
      { description: 'Accommodation - July 2025', amount: 3200.00 },
      { description: 'Care Services', amount: 1300.00 },
      { description: 'Medication Management', amount: 280.00 }
    ]
  },
  {
    id: 'INV006',
    residentId: 'R006',
    residentName: 'James Anderson',
    facility: 'Bayview Gardens',
    invoiceDate: '2025-07-19',
    dueDate: '2025-08-19',
    amount: 3560.00,
    status: 'Paid',
    paymentDate: '2025-07-20',
    paymentMethod: 'Direct Debit',
    items: [
      { description: 'Accommodation - July 2025', amount: 2500.00 },
      { description: 'Care Services', amount: 900.00 },
      { description: 'Medication Management', amount: 160.00 }
    ]
  },
  {
    id: 'INV007',
    residentId: 'R007',
    residentName: 'Patricia Taylor',
    facility: 'Green Hill Residence',
    invoiceDate: '2025-07-18',
    dueDate: '2025-08-18',
    amount: 4890.00,
    status: 'Overdue',
    paymentDate: null,
    paymentMethod: null,
    items: [
      { description: 'Accommodation - July 2025', amount: 3200.00 },
      { description: 'Care Services', amount: 1400.00 },
      { description: 'Medication Management', amount: 290.00 }
    ]
  },
  {
    id: 'INV008',
    residentId: 'R008',
    residentName: 'Richard Martinez',
    facility: 'Rosewood Care Community',
    invoiceDate: '2025-07-17',
    dueDate: '2025-08-17',
    amount: 4230.00,
    status: 'Pending',
    paymentDate: null,
    paymentMethod: null,
    items: [
      { description: 'Accommodation - July 2025', amount: 3000.00 },
      { description: 'Care Services', amount: 1100.00 },
      { description: 'Medication Management', amount: 130.00 }
    ]
  }
];

export const billingStats = {
  monthlyRevenue: 45230.00,
  paidInvoices: 156,
  overdueInvoices: 12,
  pendingInvoices: 23,
  averageInvoiceAmount: 4230.00,
  paymentRate: 87.5,
  outstandingAmount: 15680,
  collectionRate: 92.3,
  totalInvoices: 191,
  averagePaymentTime: 3.2,
  latePaymentRate: 6.3,
  refunds: 2
};

export const paymentMethods = [
  { id: 'direct_debit', name: 'Direct Debit', percentage: 45 },
  { id: 'credit_card', name: 'Credit Card', percentage: 30 },
  { id: 'bank_transfer', name: 'Bank Transfer', percentage: 15 },
  { id: 'cash', name: 'Cash', percentage: 10 }
];

export const billingCategories = [
  { name: 'Accommodation', amount: 285000.00, percentage: 67 },
  { name: 'Care Services', amount: 98000.00, percentage: 23 },
  { name: 'Medication Management', amount: 25000.00, percentage: 6 },
  { name: 'Additional Services', amount: 15000.00, percentage: 4 }
];

// Care Minutes Data - Exported from generation function above (after all residents are added)
// The comprehensiveCareMinutes variable is defined at line 2900 after all residents are added
export const careMinutesData = comprehensiveCareMinutes;

// Star Ratings Mock Data
export const starRatingsData = [
  {
    id: 'sr-001',
    facilityId: 'sunshine',
    assessmentDate: '2025-01-15',
    effectiveDate: '2025-01-15',
    residentsExperience: {
      score: 4.2,
      weight: 0.33,
      sources: {
        consumerSurvey: 4.3,
        familyFeedback: 4.1,
        satisfactionMetrics: 4.2
      },
      lastUpdated: '2025-01-15T10:00:00Z'
    },
    compliance: {
      score: 4.5,
      weight: 0.30,
      sources: {
        qualityStandards: 4.6,
        auditResults: 4.4,
        sirsCompliance: 5.0
      },
      lastUpdated: '2025-01-15T10:00:00Z',
      capsOverall: false
    },
    staffing: {
      score: 4.0,
      weight: 0.22,
      sources: {
        careMinutesCompliance: 5.0,
        rnMinutesCompliance: 5.0,
        staffingLevels: 3.5,
        turnoverRate: 4.0
      },
      lastUpdated: '2025-01-15T10:00:00Z',
      meetsBothTargets: true
    },
    qualityMeasures: {
      score: 4.3,
      weight: 0.15,
      sources: {
        nqipIndicators: 4.2,
        clinicalMetrics: 4.0,
        safetyIndicators: 4.5
      },
      lastUpdated: '2025-01-15T10:00:00Z'
    },
    overallRating: 4.3,
    benchmarks: {
      national: 3.8,
      state: 4.0,
      peerGroup: 4.1
    },
    calculatedAt: '2025-01-15T10:00:00Z',
    calculatedBy: 'system'
  },
  {
    id: 'sr-002',
    facilityId: 'sunshine',
    assessmentDate: '2024-10-15',
    effectiveDate: '2024-10-15',
    residentsExperience: {
      score: 4.0,
      weight: 0.33,
      sources: {
        consumerSurvey: 4.1,
        familyFeedback: 3.9,
        satisfactionMetrics: 4.0
      },
      lastUpdated: '2024-10-15T10:00:00Z'
    },
    compliance: {
      score: 4.3,
      weight: 0.30,
      sources: {
        qualityStandards: 4.4,
        auditResults: 4.2,
        sirsCompliance: 4.5
      },
      lastUpdated: '2024-10-15T10:00:00Z',
      capsOverall: false
    },
    staffing: {
      score: 3.8,
      weight: 0.22,
      sources: {
        careMinutesCompliance: 4.5,
        rnMinutesCompliance: 4.0,
        staffingLevels: 3.5,
        turnoverRate: 3.5
      },
      lastUpdated: '2024-10-15T10:00:00Z',
      meetsBothTargets: true
    },
    qualityMeasures: {
      score: 4.1,
      weight: 0.15,
      sources: {
        nqipIndicators: 4.0,
        clinicalMetrics: 4.2,
        safetyIndicators: 4.1
      },
      lastUpdated: '2024-10-15T10:00:00Z'
    },
    overallRating: 4.1,
    benchmarks: {
      national: 3.8,
      state: 4.0,
      peerGroup: 4.0
    },
    calculatedAt: '2024-10-15T10:00:00Z',
    calculatedBy: 'system'
  },
  {
    id: 'sr-003',
    facilityId: 'sunshine',
    assessmentDate: '2024-07-15',
    effectiveDate: '2024-07-15',
    residentsExperience: {
      score: 3.9,
      weight: 0.33,
      sources: {
        consumerSurvey: 4.0,
        familyFeedback: 3.8,
        satisfactionMetrics: 3.9
      },
      lastUpdated: '2024-07-15T10:00:00Z'
    },
    compliance: {
      score: 4.2,
      weight: 0.30,
      sources: {
        qualityStandards: 4.3,
        auditResults: 4.1,
        sirsCompliance: 4.4
      },
      lastUpdated: '2024-07-15T10:00:00Z',
      capsOverall: false
    },
    staffing: {
      score: 3.6,
      weight: 0.22,
      sources: {
        careMinutesCompliance: 4.0,
        rnMinutesCompliance: 3.8,
        staffingLevels: 3.5,
        turnoverRate: 3.5
      },
      lastUpdated: '2024-07-15T10:00:00Z',
      meetsBothTargets: true
    },
    qualityMeasures: {
      score: 4.0,
      weight: 0.15,
      sources: {
        nqipIndicators: 3.9,
        clinicalMetrics: 4.1,
        safetyIndicators: 4.0
      },
      lastUpdated: '2024-07-15T10:00:00Z'
    },
    overallRating: 4.0,
    benchmarks: {
      national: 3.8,
      state: 3.9,
      peerGroup: 3.9
    },
    calculatedAt: '2024-07-15T10:00:00Z',
    calculatedBy: 'system'
  },
  {
    id: 'sr-004',
    facilityId: 'bayview',
    assessmentDate: '2025-01-10',
    effectiveDate: '2025-01-10',
    residentsExperience: {
      score: 4.0,
      weight: 0.33,
      sources: {
        consumerSurvey: 4.1,
        familyFeedback: 3.9,
        satisfactionMetrics: 4.0
      },
      lastUpdated: '2025-01-10T10:00:00Z'
    },
    compliance: {
      score: 4.3,
      weight: 0.30,
      sources: {
        qualityStandards: 4.4,
        auditResults: 4.2,
        sirsCompliance: 4.5
      },
      lastUpdated: '2025-01-10T10:00:00Z',
      capsOverall: false
    },
    staffing: {
      score: 3.8,
      weight: 0.22,
      sources: {
        careMinutesCompliance: 4.5,
        rnMinutesCompliance: 4.0,
        staffingLevels: 3.5,
        turnoverRate: 3.5
      },
      lastUpdated: '2025-01-10T10:00:00Z',
      meetsBothTargets: true
    },
    qualityMeasures: {
      score: 4.1,
      weight: 0.15,
      sources: {
        nqipIndicators: 4.0,
        clinicalMetrics: 4.2,
        safetyIndicators: 4.1
      },
      lastUpdated: '2025-01-10T10:00:00Z'
    },
    overallRating: 4.1,
    benchmarks: {
      national: 3.8,
      state: 4.0,
      peerGroup: 4.0
    },
    calculatedAt: '2025-01-10T10:00:00Z',
    calculatedBy: 'system'
  }
];

// Comprehensive NQIP Mock Data
export const nqipData = [
  {
    id: 'nqip-001',
    facilityId: 'sunshine',
    period: 'Q4 2024',
    reportDate: '2024-12-31',
    overallScore: 87.3,
    status: 'compliant',
    indicators: {
      falls: {
        rate: 2.1,
        target: 3.0,
        benchmark: 3.5,
        trend: -15,
        status: 'excellent',
        incidents: 12,
        residentDays: 5700,
        lastPeriod: 2.5
      },
      pressureInjuries: {
        rate: 0.8,
        target: 1.0,
        benchmark: 1.2,
        trend: -20,
        status: 'excellent',
        incidents: 5,
        residentDays: 5700,
        lastPeriod: 1.0
      },
      medicationErrors: {
        rate: 0.5,
        target: 1.0,
        benchmark: 1.5,
        trend: -25,
        status: 'excellent',
        incidents: 3,
        residentDays: 5700,
        lastPeriod: 0.7
      },
      infections: {
        rate: 3.2,
        target: 4.0,
        benchmark: 4.5,
        trend: -8,
        status: 'good',
        incidents: 18,
        residentDays: 5700,
        lastPeriod: 3.5
      },
      restraintUse: {
        rate: 1.2,
        target: 2.0,
        benchmark: 2.5,
        trend: -30,
        status: 'excellent',
        incidents: 7,
        residentDays: 5700,
        lastPeriod: 1.7
      },
      weightLoss: {
        rate: 4.1,
        target: 5.0,
        benchmark: 6.0,
        trend: -12,
        status: 'good',
        incidents: 23,
        residentDays: 5700,
        lastPeriod: 4.7
      }
    },
    benchmarks: {
      national: 78.5,
      state: 82.1,
      peerGroup: 85.7
    },
    submittedAt: '2024-12-31T23:59:59Z',
    submittedBy: 'system'
  }
];

// Comprehensive SIRS Mock Data
export const sirsData = [
  {
    id: 'sirs-001',
    facilityId: 'sunshine',
    incidentId: 'SIRS001',
    reportType: 'Priority 1',
    incidentType: 'Physical Abuse',
    severity: 'High',
    reportDate: '2025-01-15',
    incidentDate: '2025-01-15',
    reportTime: '14:30',
    residentId: 1,
    residentName: 'Margaret Rose Thompson',
    reportedBy: 'Sarah Mitchell',
    reportedByRole: 'Facility Manager',
    description: 'Allegation of physical abuse during personal care assistance. Resident reported inappropriate handling during transfer.',
    immediateActions: [
      'Resident assessed by RN',
      'Staff member removed from duty',
      'Family notified',
      'Internal investigation commenced'
    ],
    status: 'Under Investigation',
    investigator: 'Sarah Mitchell',
    investigationStartDate: '2025-01-15',
    dueDate: '2025-01-16',
    daysRemaining: 1,
    governmentNotified: true,
    governmentNotificationDate: '2025-01-15T15:00:00Z',
    familyNotified: true,
    familyNotificationDate: '2025-01-15T14:45:00Z',
    findings: null,
    recommendations: null,
    resolvedDate: null
  },
  {
    id: 'sirs-002',
    facilityId: 'sunshine',
    incidentId: 'SIRS002',
    reportType: 'Priority 2',
    incidentType: 'Neglect',
    severity: 'Medium',
    reportDate: '2025-01-10',
    incidentDate: '2025-01-10',
    reportTime: '09:15',
    residentId: 2,
    residentName: 'William James Wilson',
    reportedBy: 'Maria Garcia',
    reportedByRole: 'Registered Nurse',
    description: 'Failure to provide adequate nutrition and hydration. Resident missed two meals due to staffing shortage.',
    immediateActions: [
      'Nutritional assessment completed',
      'Additional staff allocated',
      'Care plan reviewed',
      'Family notified'
    ],
    status: 'Resolved',
    investigator: 'David Brown',
    investigationStartDate: '2025-01-10',
    dueDate: '2025-02-09',
    daysRemaining: 0,
    governmentNotified: true,
    governmentNotificationDate: '2025-01-10T10:00:00Z',
    familyNotified: true,
    familyNotificationDate: '2025-01-10T09:30:00Z',
    findings: 'Staffing shortage identified. Additional resources allocated. No intentional neglect.',
    recommendations: [
      'Implement backup staffing protocol',
      'Review meal delivery procedures',
      'Increase monitoring for high-needs residents'
    ],
    resolvedDate: '2025-01-12'
  },
  {
    id: 'sirs-003',
    facilityId: 'bayview',
    incidentId: 'SIRS003',
    reportType: 'Priority 1',
    incidentType: 'Unreasonable Use of Force',
    severity: 'High',
    reportDate: '2025-01-08',
    incidentDate: '2025-01-08',
    reportTime: '16:45',
    residentId: 3,
    residentName: 'Elizabeth Brown',
    reportedBy: 'Michael Chen',
    reportedByRole: 'Facility Manager',
    description: 'Allegation of unreasonable use of force during behavioral intervention.',
    immediateActions: [
      'Resident assessed',
      'Staff member suspended',
      'Police notified',
      'Aged Care Quality and Safety Commission notified'
    ],
    status: 'Under Investigation',
    investigator: 'Michael Chen',
    investigationStartDate: '2025-01-08',
    dueDate: '2025-01-09',
    daysRemaining: 0,
    governmentNotified: true,
    governmentNotificationDate: '2025-01-08T17:00:00Z',
    familyNotified: true,
    familyNotificationDate: '2025-01-08T17:15:00Z',
    findings: null,
    recommendations: null,
    resolvedDate: null
  }
];

// Comprehensive Audits Mock Data
export const auditData = [
  {
    id: 'audit-001',
    facilityId: 'sunshine',
    auditType: 'Medication Management',
    auditDate: '2025-01-25',
    scheduledDate: '2025-01-25',
    auditor: 'Dr. Jennifer Lee',
    auditorRole: 'Clinical Auditor',
    auditorOrganization: 'Aged Care Quality and Safety Commission',
    status: 'Completed',
    score: 94.2,
    maxScore: 100,
    category: 'medication',
    scope: 'Comprehensive medication management audit including prescribing, administration, storage, and documentation',
    findings: [
      {
        id: 'f-001',
        severity: 'Minor',
        description: 'Medication storage temperature log incomplete for one week',
        recommendation: 'Implement automated temperature monitoring',
        status: 'Addressed',
        addressedDate: '2025-01-26'
      },
      {
        id: 'f-002',
        severity: 'Minor',
        description: 'Two medication charts missing second signature',
        recommendation: 'Review medication administration protocols',
        status: 'Addressed',
        addressedDate: '2025-01-26'
      }
    ],
    recommendations: [
      'Implement automated medication verification system',
      'Enhance staff training on medication documentation',
      'Review medication storage protocols'
    ],
    strengths: [
      '100% medication administration accuracy',
      'Excellent drug interaction checking',
      'Comprehensive medication reviews'
    ],
    nextAuditDate: '2025-04-25',
    nextAuditType: 'Medication Management',
    reportUrl: '/reports/audit-001.pdf',
    submittedAt: '2025-01-26T10:00:00Z'
  },
  {
    id: 'audit-002',
    facilityId: 'sunshine',
    auditType: 'Infection Control',
    auditDate: '2025-01-20',
    scheduledDate: '2025-01-20',
    auditor: 'Nurse Sarah Johnson',
    auditorRole: 'Infection Control Specialist',
    auditorOrganization: 'Internal Quality Team',
    status: 'Completed',
    score: 91.8,
    maxScore: 100,
    category: 'infection',
    scope: 'Infection prevention and control measures, hand hygiene, PPE usage, cleaning protocols',
    findings: [
      {
        id: 'f-003',
        severity: 'Minor',
        description: 'Hand hygiene compliance at 95%, target is 98%',
        recommendation: 'Additional hand hygiene training sessions',
        status: 'In Progress',
        addressedDate: null
      }
    ],
    recommendations: [
      'Increase hand hygiene monitoring',
      'Review PPE storage locations',
      'Enhance cleaning schedule documentation'
    ],
    strengths: [
      'Excellent outbreak management protocols',
      'Comprehensive staff training',
      'Well-maintained cleaning equipment'
    ],
    nextAuditDate: '2025-04-20',
    nextAuditType: 'Infection Control',
    reportUrl: '/reports/audit-002.pdf',
    submittedAt: '2025-01-21T14:00:00Z'
  },
  {
    id: 'audit-003',
    facilityId: 'sunshine',
    auditType: 'Resident Care',
    auditDate: '2025-01-15',
    scheduledDate: '2025-01-15',
    auditor: 'Dr. Robert Smith',
    auditorRole: 'Clinical Director',
    auditorOrganization: 'Internal Quality Team',
    status: 'Completed',
    score: 96.5,
    maxScore: 100,
    category: 'resident',
    scope: 'Resident care plans, assessments, care delivery, documentation',
    findings: [],
    recommendations: [
      'Continue excellent care plan documentation',
      'Maintain high standards of resident engagement'
    ],
    strengths: [
      'Comprehensive care plans',
      'Excellent resident engagement',
      'Strong family communication',
      'Well-documented assessments'
    ],
    nextAuditDate: '2025-04-15',
    nextAuditType: 'Resident Care',
    reportUrl: '/reports/audit-003.pdf',
    submittedAt: '2025-01-16T09:00:00Z'
  },
  {
    id: 'audit-004',
    facilityId: 'sunshine',
    auditType: 'Safety & Security',
    auditDate: '2025-02-01',
    scheduledDate: '2025-02-01',
    auditor: 'David Wilson',
    auditorRole: 'Safety Officer',
    auditorOrganization: 'Internal Quality Team',
    status: 'Scheduled',
    score: null,
    maxScore: 100,
    category: 'safety',
    scope: 'Safety protocols, emergency procedures, security measures, risk management',
    findings: null,
    recommendations: null,
    strengths: null,
    nextAuditDate: null,
    nextAuditType: null,
    reportUrl: null,
    submittedAt: null
  }
];

// ============================================================================
// NDIS INCIDENT MANAGEMENT MOCK DATA
// Comprehensive incident data for NDIS participants with compliance deadlines
// ============================================================================

const generateNDISIncidents = () => {
  const incidents = [];
  const ndisResidents = residents.filter(r => 
    r.facility && (r.facility.startsWith('ndis_') || r.facility === 'ndis_sunshine_support')
  );
  
  if (ndisResidents.length === 0) return [];
  
  const incidentTypes = ['DEATH', 'SERIOUS_INJURY', 'ABUSE', 'UNLAWFUL_CONTACT', 'UNAUTHORIZED_RESTRICTIVE', 'OTHER'];
  const statuses = ['pending', 'under_investigation', 'resolved', 'closed'];
  
  // Generate incidents for last 90 days
  const today = new Date();
  for (let i = 0; i < 90; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Generate 1-3 incidents per day randomly (but not every day)
    if (Math.random() > 0.3) continue; // Skip 70% of days
    
    const incidentsPerDay = Math.floor(Math.random() * 2) + 1;
    
    for (let j = 0; j < incidentsPerDay && ndisResidents.length > 0; j++) {
      const resident = ndisResidents[Math.floor(Math.random() * ndisResidents.length)];
      const type = incidentTypes[Math.floor(Math.random() * incidentTypes.length)];
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      
      // Calculate deadline based on type
      let deadlineHours = 24; // Default
      if (type === 'UNAUTHORIZED_RESTRICTIVE') {
        deadlineHours = 120; // 5 business days
      } else if (type === 'OTHER') {
        deadlineHours = 1440; // 60 business days
      }
      
      const occurredAt = new Date(date);
      occurredAt.setHours(Math.floor(Math.random() * 24), Math.floor(Math.random() * 60), 0, 0);
      
      const reportingDeadline = new Date(occurredAt);
      reportingDeadline.setHours(reportingDeadline.getHours() + deadlineHours);
      
      const incident = {
        id: `NDIS-INC-${date.toISOString().split('T')[0]}-${j + 1}`,
        participantId: resident.id,
        participantName: resident.name,
        type: type,
        occurredAt: occurredAt.toISOString(),
        location: resident.facility || 'NDIS Facility',
        description: `Incident description for ${resident.name} - ${type.replace('_', ' ').toLowerCase()}`,
        severity: type === 'DEATH' || type === 'SERIOUS_INJURY' || type === 'ABUSE' || type === 'UNLAWFUL_CONTACT' ? 'CRITICAL' : 'HIGH',
        status: status,
        reportingDeadline: reportingDeadline.toISOString(),
        reportedBy: `Staff Member ${Math.floor(Math.random() * 50) + 1}`,
        reportedAt: new Date(occurredAt.getTime() + Math.random() * 3600000).toISOString(), // Within 1 hour
        immediateActions: 'Immediate actions taken to ensure safety',
        emergencyServicesNotified: type === 'DEATH' || type === 'SERIOUS_INJURY' ? true : Math.random() > 0.7,
        witnesses: [`Witness ${Math.floor(Math.random() * 10) + 1}`],
        followUpRequired: true,
        followUpActions: 'Follow-up actions documented',
        attachments: [],
        createdAt: occurredAt.toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      incidents.push(incident);
    }
  }
  
  return incidents.sort((a, b) => new Date(b.occurredAt) - new Date(a.occurredAt));
};

export const ndisIncidents = generateNDISIncidents();

// ============================================================================
// NDIS QUALITY METRICS & OUTCOMES DATA
// Comprehensive quality tracking for investor presentations
// ============================================================================

export const ndisQualityMetrics = {
  overallScore: 94.5,
  participantSatisfaction: 92.3,
  goalAchievementRate: 87.6,
  serviceDeliveryCompliance: 96.2,
  incidentRate: 2.3, // per 1000 service hours
  restrictivePracticesRate: 0.8, // per 1000 service hours
  staffTurnoverRate: 12.5, // percentage
  trainingCompletionRate: 98.7,
  credentialCompliance: 99.2,
  bspCompliance: 95.8,
  outcomesFramework: {
    socialParticipation: { baseline: 3.2, current: 4.1, target: 4.5, improvement: 28.1 },
    independence: { baseline: 2.8, current: 3.6, target: 4.0, improvement: 28.6 },
    health: { baseline: 3.5, current: 4.0, target: 4.3, improvement: 14.3 },
    learning: { baseline: 3.0, current: 3.7, target: 4.2, improvement: 23.3 },
    work: { baseline: 2.5, current: 3.2, target: 3.8, improvement: 28.0 },
    homeLiving: { baseline: 3.3, current: 4.0, target: 4.4, improvement: 21.2 }
  },
  lastUpdated: new Date().toISOString()
};

// ============================================================================
// NDIS COMPLIANCE REPORTS DATA
// Comprehensive compliance tracking for audits
// ============================================================================

const generateNDISComplianceReports = () => {
  const reports = [];
  const reportTypes = [
    { type: 'Quarterly Compliance Review', frequency: 'quarterly', priority: 'HIGH' },
    { type: 'Annual Quality Audit', frequency: 'annually', priority: 'CRITICAL' },
    { type: 'Incident Management Review', frequency: 'monthly', priority: 'HIGH' },
    { type: 'Restrictive Practices Audit', frequency: 'monthly', priority: 'HIGH' },
    { type: 'Staff Credential Audit', frequency: 'quarterly', priority: 'MEDIUM' },
    { type: 'BSP Compliance Review', frequency: 'quarterly', priority: 'HIGH' },
    { type: 'Training Compliance Check', frequency: 'monthly', priority: 'MEDIUM' },
    { type: 'Document Management Audit', frequency: 'quarterly', priority: 'MEDIUM' }
  ];

  const today = new Date();
  for (let i = 0; i < 12; i++) {
    const reportDate = new Date(today);
    reportDate.setMonth(reportDate.getMonth() - i);
    
    reportTypes.forEach((reportType, idx) => {
      const report = {
        id: `NDIS-COMP-${reportDate.toISOString().split('T')[0]}-${idx + 1}`,
        type: reportType.type,
        reportDate: reportDate.toISOString().split('T')[0],
        status: i === 0 ? 'pending' : i < 3 ? 'under_review' : 'completed',
        priority: reportType.priority,
        complianceScore: Math.floor(Math.random() * 15) + 85, // 85-100
        findings: [
          'All staff credentials current and verified',
          'Incident reporting deadlines met',
          'BSP reviews completed on schedule',
          'Training requirements fulfilled'
        ],
        recommendations: [
          'Continue current compliance practices',
          'Enhance documentation processes',
          'Review restrictive practices reduction strategies'
        ],
        nextReviewDate: new Date(reportDate.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        submittedBy: `Compliance Officer ${idx + 1}`,
        reviewedBy: i < 3 ? null : `Quality Manager ${idx + 1}`,
        createdAt: reportDate.toISOString(),
        updatedAt: new Date().toISOString()
      };
      reports.push(report);
    });
  }
  
  return reports.sort((a, b) => new Date(b.reportDate) - new Date(a.reportDate));
};

export const ndisComplianceReports = generateNDISComplianceReports();

// ============================================================================
// NDIS BEHAVIOR SUPPORT PLANS (BSP) DATA
// Comprehensive BSP tracking with version control
// ============================================================================

const generateNDISBSPs = () => {
  const bsps = [];
  // Use residents array that's already defined in this file
  const ndisParticipants = residents.filter(r => 
    r && r.facility && (r.facility.startsWith('ndis_') || r.facility === 'ndis_sunshine_support')
  );

  ndisParticipants.forEach((participant, idx) => {
    const createdDate = new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1);
    const reviewDate = new Date(createdDate);
    reviewDate.setFullYear(reviewDate.getFullYear() + 1);
    
    const bsp = {
      id: `BSP-${participant.id}-v1`,
      participantId: participant.id,
      participantName: participant.name,
      version: 1,
      status: reviewDate < new Date() ? 'expired' : reviewDate < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) ? 'review_due' : 'active',
      createdDate: createdDate.toISOString().split('T')[0],
      reviewDate: reviewDate.toISOString().split('T')[0],
      expiryDate: reviewDate.toISOString().split('T')[0],
      developedBy: 'Behavior Support Practitioner',
      approvedBy: 'Senior Practitioner',
      restrictivePractices: [
        { type: 'Chemical', authorized: true, authorizationNumber: `AUTH-${idx}-001`, expiryDate: reviewDate.toISOString().split('T')[0] },
        { type: 'Physical', authorized: Math.random() > 0.5, authorizationNumber: Math.random() > 0.5 ? `AUTH-${idx}-002` : null, expiryDate: null }
      ],
      goals: [
        'Reduce aggressive behaviors',
        'Increase independent living skills',
        'Improve social interactions'
      ],
      strategies: [
        'Positive behavior support',
        'Environmental modifications',
        'Skill building',
        'Crisis intervention plan'
      ],
      reviewHistory: [],
      attachments: [],
      createdAt: createdDate.toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    bsps.push(bsp);
  });
  
  return bsps;
};

export const ndisBSPs = generateNDISBSPs();

// ============================================================================
// NDIS TRAINING RECORDS DATA
// Comprehensive staff training tracking
// ============================================================================

const generateNDISTrainingRecords = () => {
  const records = [];
  const trainingModules = [
    { name: 'NDIS Worker Screening', required: true, frequency: 'annually', duration: 2 },
    { name: 'Restrictive Practices Training', required: true, frequency: 'annually', duration: 4 },
    { name: 'Incident Management', required: true, frequency: 'annually', duration: 3 },
    { name: 'Behavior Support Plans', required: true, frequency: 'annually', duration: 3 },
    { name: 'Medication Administration', required: true, frequency: 'annually', duration: 4 },
    { name: 'First Aid', required: true, frequency: 'triennially', duration: 8 },
    { name: 'Manual Handling', required: true, frequency: 'annually', duration: 4 },
    { name: 'Infection Control', required: true, frequency: 'annually', duration: 2 },
    { name: 'Cultural Competency', required: true, frequency: 'annually', duration: 3 },
    { name: 'Privacy and Confidentiality', required: true, frequency: 'annually', duration: 2 }
  ];

  // Use staffData that's already defined in this file
  const ndisStaff = staffData.filter(s => 
    s && s.facility && (s.facility.startsWith('ndis_') || s.facility === 'ndis_sunshine_support')
  );

  ndisStaff.forEach((staff, staffIdx) => {
    trainingModules.forEach((module, modIdx) => {
      const completedDate = new Date();
      completedDate.setMonth(completedDate.getMonth() - Math.floor(Math.random() * 12));
      
      let expiryDate = new Date(completedDate);
      if (module.frequency === 'annually') {
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      } else if (module.frequency === 'triennially') {
        expiryDate.setFullYear(expiryDate.getFullYear() + 3);
      }
      
      const record = {
        id: `TRAIN-${staff.id}-${modIdx + 1}`,
        staffId: staff.id,
        staffName: staff.name,
        moduleName: module.name,
        completedDate: completedDate.toISOString().split('T')[0],
        expiryDate: expiryDate.toISOString().split('T')[0],
        status: expiryDate < new Date() ? 'expired' : expiryDate < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) ? 'expiring_soon' : 'current',
        score: Math.floor(Math.random() * 20) + 80, // 80-100
        certificateNumber: `CERT-${staff.id}-${module.name.substring(0, 3).toUpperCase()}-${completedDate.getFullYear()}`,
        trainer: `Trainer ${modIdx + 1}`,
        required: module.required,
        duration: module.duration,
        createdAt: completedDate.toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      records.push(record);
    });
  });
  
  return records;
};

export const ndisTrainingRecords = generateNDISTrainingRecords();

// ============================================================================
// NDIS AUDIT DATA
// Comprehensive audit tracking for compliance
// ============================================================================

const generateNDISAudits = () => {
  const audits = [];
  const auditTypes = [
    'NDIS Commission Audit',
    'Internal Quality Audit',
    'Restrictive Practices Audit',
    'Incident Management Audit',
    'Document Management Audit',
    'Staff Credential Audit',
    'BSP Compliance Audit',
    'Training Compliance Audit'
  ];

  const today = new Date();
  for (let i = 0; i < 8; i++) {
    const auditDate = new Date(today);
    auditDate.setMonth(auditDate.getMonth() - i * 1.5);
    
    const audit = {
      id: `AUDIT-NDIS-${auditDate.toISOString().split('T')[0]}`,
      type: auditTypes[i % auditTypes.length],
      auditDate: auditDate.toISOString().split('T')[0],
      scheduledDate: auditDate.toISOString().split('T')[0],
      status: i === 0 ? 'scheduled' : i === 1 ? 'in_progress' : 'completed',
      auditor: i === 0 ? null : `Auditor ${i}`,
      auditorOrganization: i === 0 ? null : i % 2 === 0 ? 'NDIS Commission' : 'Internal Quality Team',
      score: i < 2 ? null : Math.floor(Math.random() * 10) + 90, // 90-100
      maxScore: 100,
      findings: i < 2 ? [] : [
        'Excellent compliance with incident reporting requirements',
        'All BSPs current and properly authorized',
        'Staff credentials verified and current',
        'Training records complete and up-to-date'
      ],
      recommendations: i < 2 ? [] : [
        'Continue current compliance practices',
        'Enhance documentation processes',
        'Review restrictive practices reduction strategies'
      ],
      nextAuditDate: new Date(auditDate.getTime() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      reportUrl: i < 2 ? null : `/reports/audit-${auditDate.toISOString().split('T')[0]}.pdf`,
      submittedAt: i < 2 ? null : new Date(auditDate.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: auditDate.toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    audits.push(audit);
  }
  
  return audits.sort((a, b) => new Date(b.auditDate) - new Date(a.auditDate));
};

export const ndisAudits = generateNDISAudits();
